import { headerAndFooter, constants } from '@config';
import { logError } from 'src/utils/logger';
import sanitizeContent from 'src/utils/sanitize-content';

/**
 * @typedef {Array<{selectedElement: HTMLElement, type: string, permission: string | null}>} Clipboard
 */

/*
 * svgcanvas.js
 *
 * Licensed under the MIT License
 *
 * Copyright(c) 2010 Alexis Deveria
 * Copyright(c) 2010 Pavol Rusnak
 * Copyright(c) 2010 Jeff Schiller
 *
 */

// Dependencies:
// 1) jQuery
// 2) browser.js
// 3) svgtransformlist.js
// 4) math.js
// 5) units.js
// 6) svgutils.js
// 7) sanitize.js
// 8) history.js
// 9) select.js
// 10) draw.js
// 11) path.js
// 12) coords.js
// 13) recalculate.js

(function () {
  if (!window.console) {
    window.console = {};
    window.console.log = function (str) { };
    window.console.dir = function (str) { };
  }

  if (window.opera) {
    window.console.log = function (str) { opera.postError(str); };
    window.console.dir = function (str) { };
  }
}());

// Class: SvgCanvas
// The main SvgCanvas class that manages all SVG-related functions
//
// Parameters:
// container - The container HTML element that should hold the SVG root element
// config - An object that contains configuration data
export default class SvgCanvas {
  constructor (container, config) {
    // Alias Namespace constants
    const NS = svgedit.NS;

    // Default configuration options
    const curConfig = {
      show_outside_canvas: true,
      selectNew: true,
      dimensions: [640, 480]
    };

    // Update config with new one if given
    if (config) {
      $.extend(curConfig, config);
    }

    this.curConfig = curConfig;

    svgedit.debug = curConfig.debug;

    // Array with width/height of canvas
    const dimensions = curConfig.dimensions;

    const editor = $(curConfig.editor).get(0);
    const canvas = this;

    // "document" element associated with the container (same as window.document using default svg-editor.js)
    // NOTE: This is not actually a SVG document, but a HTML document.
    const svgdoc = container.ownerDocument;

    // This is a container for the document being edited, not the document itself.
    const wrapper = $(curConfig.wrapper).get(0);
    const workarea = $(curConfig.workarea).get(0);
    const svgcanvas = container;

    const svgroot = svgdoc.importNode(svgedit.utilities.text2xml(
      '<svg id="svgroot" xmlns="' + NS.SVG + '" xlinkns="' + NS.XLINK + '" ' +
      'width="' + dimensions[0] + '" height="' + dimensions[1] + '" x="' + 0 + '" y="' + 0 + '" overflow="visible">' +
      '<defs>' +
      '<clipPath id="clip_canvas"><rect class="canvasCliper" x="0" y="0" width="0" height="0"></rect></clipPath>' +
      '<pattern id="transparencypattern" patternUnits="userSpaceOnUse" x="10" y="10" width="20" height="20"><rect fill="#DDD" stroke="none" x="0" y="0" width="10" height="10"></rect><rect fill="#DDD" stroke="none" x="10" y="10" width="10" height="10"></rect></pattern>' +
      '<symbol id="arrow"><g xmlns="http://www.w3.org/2000/svg" fill="#888" width="98" height="95.33">' +
      '<circle fill="none" cx="48.679" cy="46.526" r="37.821"/>' +
      // '<path d="M48.679,5.705c-22.509,0-40.821,18.312-40.821,40.821S26.17,87.347,48.679,87.347c22.508,0,40.821-18.312,40.821-40.821   S71.188,5.705,48.679,5.705z M48.679,84.347c-20.854,0-37.821-16.967-37.821-37.821S27.825,8.705,48.679,8.705   S86.5,25.671,86.5,46.526S69.533,84.347,48.679,84.347z"/>' +
      // '<polygon points="65.043,45.939 47.88,24.962 44.859,27.433 60.333,46.347 44.859,65.262 47.88,67.732 65.042,46.755 65.377,46.348   65.377,46.348 65.377,46.348  "/>' +
      // '<polygon points="55.654,46.348 55.654,46.348 55.654,46.348 55.32,45.939 38.158,24.962 35.137,27.433 50.611,46.347   35.137,65.262 38.158,67.732 55.32,46.755  "/>' +
      '</g></symbol>' +
      '</defs>' +
      '</svg>').documentElement, true);
    container.appendChild(svgroot);
    let svgcontent = svgdoc.createElementNS(NS.SVG, 'svg');

    // Panel for selector (overlay canvas)
    const canvaspanel = $(curConfig.canvasPanel).get(0);
    const svgpanel = svgdoc.importNode(svgedit.utilities.text2xml(
      '<svg id="svgpanel" xmlns="' + NS.SVG + '" xlinkns="' + NS.XLINK + '" ' +
      'width="100%" height="100%" x="0" y="0" overflow="visible">' +
      '</svg>').documentElement, true);

    canvaspanel.appendChild(svgpanel);

    // This function resets the svgcontent element while keeping it in the DOM.
    const clearSvgContentElement = canvas.clearSvgContentElement = function () {
      while (svgcontent.firstChild) { svgcontent.removeChild(svgcontent.firstChild); }

      // TODO: Clear out all other attributes first?
      $(svgcontent).attr({
        id: 'svgcontent',
        width: dimensions[0],
        height: dimensions[1],
        x: dimensions[0],
        y: dimensions[1],
        overflow: curConfig.show_outside_canvas
          ? 'visible'
          : 'hidden',
        xmlns: NS.SVG,
        'xmlns:se': NS.SE,
        'xmlns:xlink': NS.XLINK
      }).appendTo(svgroot);

      // TODO: make this string optional and set by the client
      //  const comment = svgdoc.createComment(" Created with SVG-edit - http://svg-edit.googlecode.com/ ");
      //  svgcontent.appendChild(comment);
    };
    clearSvgContentElement();

    // Prefix string for element IDs
    let idprefix = 'svg_';

    // Function: setIdPrefix
    // Changes the ID prefix to the given value
    //
    // Parameters:
    // p - String with the new prefix
    canvas.setIdPrefix = function (p) {
      idprefix = p;
    };

    // Current svgedit.draw.Drawing object
    // @type {svgedit.draw.Drawing}
    canvas.current_drawing_ = new svgedit.draw.Drawing(svgcontent, idprefix);

    // Function: getCurrentDrawing
    // Returns the current Drawing.
    // @return {svgedit.draw.Drawing}
    const getCurrentDrawing = canvas.getCurrentDrawing = function () {
      return canvas.current_drawing_;
    };

    // Float displaying the current zoom level (1 = 100%, .5 = 50%, etc)
    let currentZoom = 1;

    // pointer to current group (for in-group editing)
    let currentGroup = null;

    // Object containing data for the currently selected styles
    const allProperties = {
      shape: {
        fill: curConfig.initFill.color,
        fill_paint: null,
        fill_opacity: curConfig.initFill.opacity,
        stroke: curConfig.initStroke.color,
        stroke_paint: null,
        stroke_opacity: curConfig.initStroke.opacity,
        stroke_width: curConfig.initStroke.width,
        stroke_dasharray: 'none',
        stroke_linejoin: 'miter',
        stroke_linecap: 'butt',
        opacity: curConfig.initOpacity
      }
    };

    allProperties.text = $.extend(true, {}, allProperties.shape);
    $.extend(allProperties.text, {
      fill: '#000000',
      stroke_width: 0,
      font_size: 24,
      font_family: 'serif'
    });

    // Current shape style properties
    let curShape = allProperties.shape;

    // Array with all the currently selected elements
    // default size of 1 until it needs to grow bigger
    let selectedElements = new Array(1);
    let highlightedElement = null;

    function recalculatePositionBasedOnLayer (attr) {
      const { main, header, footer } = headerAndFooter;
      const currentLayer = getCurrentDrawing().getCurrentLayerName();

      if (!currentLayer || ![main.layer, header.layer, footer.layer].includes(currentLayer)) return;
      if (currentLayer === main.layer) canvas.setCurrentLayer(header.layer);
      if (!attr.y) return;

      const { id } = getCurrentDrawing().getCurrentLayerName() === header.layer ? header : footer;
      const bbox = svgcontent.getElementById(id).getBBox();

      if (attr.width && attr.height && (attr.width > bbox.width || attr.height > bbox.height)) {
        const ratio = attr.width / attr.height;
        attr.height = bbox.height;
        attr.width = bbox.height * ratio;
      }

      attr.y = attr.height ? ((bbox.height - Number(attr.height)) / 2) + bbox.y : bbox.height / 2 + bbox.y;
    }

    // Function: addSvgElementFromJson
    // Create a new SVG element based on the given object keys/values and add it to the current layer
    // The element will be ran through cleanupElement before being returned
    //
    // Parameters:
    // data - Object with the following keys/values:
    // * element - tag name of the SVG element to create
    // * attr - Object with attributes key-values to assign to the new element
    // * curStyles - Boolean indicating that current style attributes should be applied first
    //
    // Returns: The new element
    const addSvgElementFromJson = this.addSvgElementFromJson = function (data, parentElem) {
      let shape = svgedit.utilities.getElem(data.attr.id);
      parentElem = parentElem || null;

      // if shape is a path but we need to create a rect/ellipse, then remove the path
      const currentLayer = getCurrentDrawing().getCurrentLayer();
      if (shape && data.element !== shape.tagName) {
        currentLayer.removeChild(shape);
        shape = null;
      }
      if (!shape) {
        shape = svgdoc.createElementNS(NS.SVG, data.element);
        if (parentElem) {
          parentElem.appendChild(shape);
        } else {
          if (currentLayer) {
            (currentGroup || currentLayer).appendChild(shape);
          }
        }
      }
      if (data.curStyles) {
        svgedit.utilities.assignAttributes(shape, {
          fill: curShape.fill,
          stroke: curShape.stroke,
          'stroke-width': curShape.stroke_width,
          'stroke-dasharray': curShape.stroke_dasharray,
          'stroke-linejoin': curShape.stroke_linejoin,
          'stroke-linecap': curShape.stroke_linecap,
          'stroke-opacity': curShape.stroke_opacity,
          'fill-opacity': curShape.fill_opacity,
          opacity: curShape.opacity / 2,
          style: 'pointer-events:inherit'
        }, 100);
      }
      svgedit.utilities.assignAttributes(shape, data.attr, 100);
      svgedit.utilities.cleanupElement(shape);
      return shape;
    };

    // import svgtransformlist.js
    const getTransformList = canvas.getTransformList = svgedit.transformlist.getTransformList;

    // import from math.js.
    const transformPoint = svgedit.math.transformPoint;
    const matrixMultiply = canvas.matrixMultiply = svgedit.math.matrixMultiply;
    const hasMatrixTransform = canvas.hasMatrixTransform = svgedit.math.hasMatrixTransform;
    const transformListToTransform = canvas.transformListToTransform = svgedit.math.transformListToTransform;

    // initialize from units.js
    // send in an object implementing the ElementContainer interface (see units.js)
    svgedit.units.init({
      getBaseUnit: function () { return curConfig.baseUnit; },
      getElement: svgedit.utilities.getElem,
      getHeight: function () { return svgcontent.getAttribute('height') / currentZoom; },
      getWidth: function () { return svgcontent.getAttribute('width') / currentZoom; },
      getRoundDigits: function () { return saveOptions.round_digits; }
    });
    // import from units.js
    canvas.convertToNum = svgedit.units.convertToNum;

    // import from svgutils.js
    svgedit.utilities.init({
      getDOMDocument: function () { return svgdoc; },
      getDOMEditor: function () { return editor; },
      getDOMContainer: function () { return container; },
      getSVGRoot: function () { return svgroot; },
      // TODO: replace this mostly with a way to get the current drawing.
      getSelectedElements: function () { return selectedElements; },
      getSVGContent: function () { return svgcontent; },
      getBaseUnit: function () { return curConfig.baseUnit; },
      getStepSize: function () { return curConfig.stepSize || 10; },
      getZoomMultiplier: function () { return canvas.zoomMultiplier; }
    });
    const findDefs = canvas.findDefs = svgedit.utilities.findDefs;
    const getUrlFromAttr = canvas.getUrlFromAttr = svgedit.utilities.getUrlFromAttr;
    const getHref = canvas.getHref = svgedit.utilities.getHref;
    const setHref = canvas.setHref = svgedit.utilities.setHref;
    const getPathBBox = svgedit.utilities.getPathBBox;
    canvas.getBBox = svgedit.utilities.getBBox;
    canvas.getRotationAngle = svgedit.utilities.getRotationAngle;
    const getElem = canvas.getElem = svgedit.utilities.getElem;
    canvas.getRefElem = svgedit.utilities.getRefElem;
    const assignAttributes = canvas.assignAttributes = svgedit.utilities.assignAttributes;
    const cleanupElement = this.cleanupElement = svgedit.utilities.cleanupElement;

    // import from coords.js
    svgedit.coords.init({
      getDrawing: function () { return getCurrentDrawing(); },
      getGridSnapping: function () { return curConfig.gridSnapping; }
    });
    const remapElement = this.remapElement = svgedit.coords.remapElement;

    // import from recalculate.js
    svgedit.recalculate.init({
      getSVGRoot: function () { return svgroot; },
      getStartTransform: function () { return canvas.startTransform; },
      setStartTransform: function (transform) { canvas.startTransform = transform; }
    });
    const recalculateDimensions = this.recalculateDimensions = svgedit.recalculate.recalculateDimensions;

    // import from sanitize.js
    const nsMap = svgedit.getReverseNS();
    const sanitizeSvg = canvas.sanitizeSvg = svgedit.sanitize.sanitizeSvg;

    // import from history.js
    const MoveElementCommand = svgedit.history.MoveElementCommand;
    const InsertElementCommand = svgedit.history.InsertElementCommand;
    const RemoveElementCommand = svgedit.history.RemoveElementCommand;
    const ChangeElementCommand = svgedit.history.ChangeElementCommand;
    const BatchCommand = svgedit.history.BatchCommand;
    // Implement the svgedit.history.HistoryEventHandler interface.

    canvas.undoMgr = new svgedit.history.UndoManager({
      handleHistoryEvent: function (eventType, cmd) {
        const EventTypes = svgedit.history.HistoryEventTypes;

        // Special case for simple change element command
        if (eventType === EventTypes.BEFORE_UNAPPLY || eventType === EventTypes.BEFORE_APPLY) {
          canvas.clearSelection();
        } else if (eventType === EventTypes.AFTER_APPLY || eventType === EventTypes.AFTER_UNAPPLY) {
          const elems = cmd.elements();
          canvas.pathActions.clear();
          call('editor.changed', elems);
          const cmdType = cmd.type();
          const isApply = (eventType === EventTypes.AFTER_APPLY);
          if (cmdType === MoveElementCommand.type()) {
            const parent = isApply
              ? cmd.newParent
              : cmd.oldParent;
            if (parent === svgcontent) {
              canvas.identifyLayers();
            }
          } else if (cmdType === InsertElementCommand.type() ||
            cmdType === RemoveElementCommand.type()) {
            if (cmd.parent === svgcontent) {
              canvas.identifyLayers();
            }
            if (cmdType === InsertElementCommand.type()) {
              if (isApply) { restoreRefElems(cmd.elem); }
            } else {
              if (!isApply) { restoreRefElems(cmd.elem); }
            }
            if (cmd.elem.tagName === 'use') {
              setUseData(cmd.elem);
            }
          } else if (cmdType === ChangeElementCommand.type()) {
            // if we are changing layer names, re-identify all layers
            if (cmd.elem.tagName === 'title' && cmd.elem.parentNode.parentNode === svgcontent) {
              canvas.identifyLayers();
            }
            const values = isApply
              ? cmd.newValues
              : cmd.oldValues;
            // If stdDeviation was changed, update the blur.
            if (values.stdDeviation) {
              canvas.setBlurOffsets(cmd.elem.parentNode, values.stdDeviation);
            }
            // This is resolved in later versions of webkit, perhaps we should
            // have a featured detection for correct 'use' behavior?
            // ——————————
            // Remove & Re-add hack for Webkit (issue 775)
            // if (cmd.elem.tagName === 'use' && svgedit.browser.isWebkit()) {
            //  const elem = cmd.elem;
            //  if (!elem.getAttribute('x') && !elem.getAttribute('y')) {
            //    const parent = elem.parentNode;
            //    const sib = elem.nextSibling;
            //    parent.removeChild(elem);
            //    parent.insertBefore(elem, sib);
            //  }
            // }
          }
        }
      }
    });
    const addCommandToHistory = function (cmd) {
      canvas.undoMgr.addCommandToHistory(cmd);
    };

    // import from select.js

    svgedit.select.init(curConfig, {
      createSVGElement: function (jsonMap) { return canvas.addSvgElementFromJson(jsonMap); },
      svgRoot: function () { return svgroot; },
      svgPanel: function () { return svgpanel; },
      svgContent: function () { return svgcontent; },
      currentZoom: function () { return currentZoom; }
    });
    // this object manages selectors for us
    const selectorManager = this.selectorManager = svgedit.select.getSelectorManager();

    // Import from path.js
    svgedit.path.init({
      getCurrentZoom: function () { return currentZoom; },
      getSVGRoot: function () { return svgroot; }
    });

    // Interface strings, usually for title elements
    const uiStrings = {
      exportNoBlur: 'Blurred elements will appear as un-blurred',
      exportNoforeignObject: 'foreignObject elements will not appear',
      exportNoDashArray: 'Strokes will appear filled',
      exportNoText: 'Text may not appear as expected'
    };

    const visElems = 'a,circle,ellipse,foreignObject,g,image,line,path,polygon,polyline,rect,svg,text,tspan,use';
    const refAttrs = ['clip-path', 'fill', 'filter', 'marker-end', 'marker-mid', 'marker-start', 'mask', 'stroke'];

    const elData = $.data;

    const restoreRefElems = function (elem) {
      // Look for missing reference elements, restore any found
      let o;
      const attrs = $(elem).attr(refAttrs);
      for (o in attrs) {
        const val = attrs[o];
        if (val && val.indexOf('url(') === 0) {
          const id = svgedit.utilities.getUrlFromAttr(val).substr(1);
          const ref = getElem(id);
          if (!ref) {
            svgedit.utilities.findDefs().appendChild(removedElements[id]);
            delete removedElements[id];
          }
        }
      }

      const childs = elem.getElementsByTagName('*');

      if (childs.length) {
        for (let i = 0, l = childs.length; i < l; i++) {
          restoreRefElems(childs[i]);
        }
      }
    };

    // Object to contain image data for raster images that were found encodable
    const encodableImages = {};

    // String with image URL of last loadable image
    // let lastGoodImgUrl = curConfig.imgPath + 'logo.png';

    // Array with current disabled elements (for in-group editing)
    let disabledElems = [];

    // Object with save options
    const saveOptions = { round_digits: 5 };

    // String indicating the current editor mode
    let currentMode = 'select';

    // Object with IDs for imported files, to see if one was already added
    const importIds = {};

    // Current text style properties
    const curText = allProperties.text;

    // Array with selected elements' Bounding box object
    //  selectedBBoxes = new Array(1),

    // Object to contain all included extensions
    const extensions = {};

    // Map of deleted reference elements
    const removedElements = {};

    // Temporary storage Object
    this._t = {};

    /**
     * Clipboard for cut, copy&pasted elements
     * @type Clipboard
     */
    canvas.clipBoard = [];

    canvas.broadcastChannel = new BroadcastChannel('canvas');

    // Canvas point for the most recent right click
    canvas.lastClickPoint = null;

    // String with the current direction in which an element is being resized
    canvas.current_resize_mode = 'none';

    // String with an element's initial transform attribute value
    canvas.startTransform = null;

    // Boolean indicating whether or not a draw action has been started
    canvas.started = false;

    // DOM element for selection rectangle drawn by the user
    canvas.rubberBox = null;

    // Current general properties
    canvas.cur_properties = curShape;

    // Array of current BBoxes (still needed?)
    canvas.curBBoxes = [];

    // Should this return an array by default, so extension results aren't overwritten?
    const runExtensions = this.runExtensions = function (action, vars, returnArray) {
      const result = Object.entries(extensions).reduce((acc, [name, opts]) => {
        if (!opts || !(action in opts)) return acc;

        const returnValue = opts[action](vars);

        return [...acc, returnValue];
      }, []);

      return returnArray ? result : (result[0] || false);
    };

    this.validateSelectedElems = function () {
      const ar = [];
      for (let i = 0; i < selectedElements.length; i++) {
        if (selectedElements[i]) ar.push(selectedElements[i]);
      }
      selectedElements = ar;
    };
    // Function: copyElem
    // Create a clone of an element, updating its ID and its children's IDs when needed
    //
    // Parameters:
    // el - DOM element to clone
    //
    // Returns: The cloned element
    const copyElem = this.copyElem = function (element) {
      const originalId = element.getAttribute('id');

      const newElement = document.createElementNS(element.namespaceURI, element.nodeName);
      Object.values(element.attributes).forEach((attr) => {
        newElement.setAttributeNS(attr.namespaceURI, attr.nodeName, attr.value);
      });
      // set the copied element's new id
      newElement.removeAttribute('id');
      newElement.setAttribute('data-old-id', originalId);
      newElement.removeAttribute('data-parent');
      newElement.removeAttribute('data-child');

      newElement.id = getNextId();

      // now create copies of all children
      element.childNodes.forEach(function (child) {
        if (child.dataset && child.dataset.sanitize === 'ignore') {
          return;
        }

        switch (child.nodeType) {
          case 1: // element node
            newElement.append(copyElem(child));
            break;
          case 3: // text node
            newElement.textContent = child.nodeValue;
            break;
          default:
            break;
        }
      });

      if (element.dataset.gsvg) {
        newElement.dataset.gsvg = newElement.firstChild;
      } else if (element.dataset.symbol) {
        const ref = element.dataset.symbol;
        newElement.dataset.ref = ref;
        newElement.dataset.symbol = ref;
      } else if (newElement.tagName === 'image') {
        // eslint-disable-next-line no-use-before-define
        preventClickDefault(newElement);
      }

      if (INKIVE.RestrictionsFactory) {
        // eslint-disable-next-line promise/catch-or-return
        INKIVE.RestrictionsFactory.getGlobalRestrictionElementPermissions(originalId).then((permissions) => {
          // eslint-disable-next-line promise/always-return
          if (permissions && permissions.attributes) {
            const restrictions = permissions.attributes.restrictions;
            if (restrictions) {
              setTimeout(() => {
                INKIVE.RestrictionsFactory.setGlobalElementRestriction(newElement.id, restrictions);
              }, 1000);
            }
          }
        });
      }

      return newElement;
    };

    // Function: addExtension
    // Add an extension to the editor
    //
    // Parameters:
    // name - String with the ID of the extension
    // ext_func - Function supplied by the extension with its data
    this.addExtension = function (name, extFunc) {
      let ext;
      if (!(name in extensions)) {
        // Provide private vars/funcs here. Is there a better way to do this?
        if ($.isFunction(extFunc)) {
          ext = extFunc($.extend(canvas.getPrivateMethods(), {
            svgroot,
            svgcontent,
            nonce: getCurrentDrawing().getNonce(),
            selectorManager,
            curConfig,
            canvas
          }));
        } else {
          ext = extFunc;
        }

        extensions[name] = ext;
        call('editor.extension_added', ext);
      } else {
        console.log('Cannot add extension "' + name + '", an extension by that name already exists"');
      }
    };

    // This method rounds the incoming value to the nearest value based on the current_zoom
    const round = this.round = function (val) {
      return parseInt(val * currentZoom, 10) / currentZoom;
    };

    // This method sends back an array or a NodeList full of elements that
    // intersect the multi-select rubber-band-box on the current_layer only.
    //
    // Since the only browser that supports the SVG DOM getIntersectionList is Opera,
    // we need to provide an implementation here. We brute-force it for now.
    //
    // Reference:
    // Firefox does not implement getIntersectionList(), see https://bugzilla.mozilla.org/show_bug.cgi?id=501421
    // Webkit does not implement getIntersectionList(), see https://bugs.webkit.org/show_bug.cgi?id=11274
    const getIntersectionList = this.getIntersectionList = function (rect) {
      if (canvas.rubberBox === null) { return null; }

      const parent = currentGroup || getCurrentDrawing().getCurrentLayer();

      if (!canvas.curBBoxes.length) {
        canvas.curBBoxes = getVisibleElementsAndBBoxes(parent);
      }

      let resultList = null;
      try {
        resultList = parent.getIntersectionList(rect, null);
      } catch (e) { }

      if (resultList === null || typeof (resultList.item) !== 'function') {
        resultList = [];
        let rubberBBox;
        if (!rect) {
          rubberBBox = canvas.rubberBox.getBBox();
          let o;
          const bb = {};

          for (o in rubberBBox) {
            bb[o] = rubberBBox[o] / currentZoom;
          }
          rubberBBox = bb;
        } else {
          rubberBBox = rect;
        }
        let i = canvas.curBBoxes.length;
        while (i--) {
          if (!rubberBBox.width || !canvas.curBBoxes[i].bbox) { continue; }
          if (svgedit.math.rectsIntersect(rubberBBox, canvas.curBBoxes[i].bbox)) {
            resultList.push(canvas.curBBoxes[i].elem);
          }
        }
      }
      // addToSelection expects an array, but it's ok to pass a NodeList
      // because using square-bracket notation is allowed:
      // http://www.w3.org/TR/DOM-Level-2-Core/ecma-script-binding.html
      return resultList;
    };

    this.getSvgContent = function () {
      return svgcontent;
    };

    // Function: getVisibleElements
    // Get all elements that have a BBox (excludes <defs>, <title>, etc).
    // Note that 0-opacity, off-screen etc elements are still considered "visible"
    // for this function
    //
    // Parameters:
    // parent - The parent DOM element to search within
    //
    // Returns:
    // An array with all "visible" elements.
    const getVisibleElements = this.getVisibleElements = function (parent) {
      if (!parent) {
        parent = $(svgcontent).children(); // Prevent layers from being included
      }

      const contentElems = [];
      $(parent).children().each(function (i, elem) {
        try {
          if (elem.getBBox()) {
            contentElems.push(elem);
          }
        } catch (e) { }
      });
      return contentElems.reverse();
    };

    // Function: getVisibleElementsAndBBoxes
    // Get all elements that have a BBox (excludes <defs>, <title>, etc).
    // Note that 0-opacity, off-screen etc elements are still considered "visible"
    // for this function
    //
    // Parameters:
    // parent - The parent DOM element to search within
    //
    // Returns:
    // An array with objects that include:
    // * elem - The element
    // * bbox - The element's BBox as retrieved from getStrokedBBox
    const getVisibleElementsAndBBoxes = this.getVisibleElementsAndBBoxes = function (parent) {
      if (!parent) {
        parent = $(svgcontent).children(); // Prevent layers from being included
      }
      const contentElems = [];
      $(parent).children().each(function (i, elem) {
        try {
          if (elem.getBBox()) {
            contentElems.push({
              elem,
              bbox: svgedit.utilities.getStrokedBBox([elem])
            });
          }
        } catch (e) { }
      });
      return contentElems.reverse();
    };

    // Function: groupSvgElem
    // Wrap an SVG element into a group element, mark the group as 'gsvg'
    //
    // Parameters:
    // elem - SVG element to wrap
    const groupSvgElem = this.groupSvgElem = function (elem) {
      const g = document.createElementNS(NS.SVG, 'g');
      elem.parentNode.replaceChild(g, elem);
      $(g).append(elem).data('gsvg', elem)[0].id = getNextId();
    };

    // Set scope for these functions
    let getId;
    let getNextId;
    let call;

    (function (c) {
      // Object to contain editor event names and callback functions
      const events = {};

      const getPrefix = function (parentElement) {
        const { header, footer, main } = headerAndFooter;

        if (parentElement && parentElement.closest(`g.${main.class}`)) {
          return 'layout_';
        }

        return [main.layer, header.layer, footer.layer]
          .includes(canvas.getCurrentLayerName())
          ? 'layout_'
          : '';
      };

      getId = c.getId = function (parentElement) { return getCurrentDrawing().getId(getPrefix(parentElement)); };

      getNextId = c.getNextId = function (parentElement) {
        return getCurrentDrawing().getNextId(getPrefix(parentElement));
      };

      // Function: call
      // Run the callback function associated with the given event
      //
      // Parameters:
      // event - String with the event name
      // arg - Argument to pass through to the callback function
      call = c.call = function (event, arg, params) {
        if (events[event]) {
          return events[event](this, arg, params);
        }
      };

      // Function: bind
      // Attaches a callback function to an event
      //
      // Parameters:
      // event - String indicating the name of the event
      // f - The callback function to bind to the event
      //
      // Return:
      // The previous event
      c.bind = function (event, f) {
        const old = events[event];
        events[event] = f;
        return old;
      };

      // Extra event
      $(document).on('editor.runExtensions', function (e, o) {
        c.runExtensions(o.name, o.data);
      });
    }(canvas));

    // Function: ffClone
    // Hack for Firefox bugs where text element features aren't updated or get
    // messed up. See issue 136 and issue 137.
    // This function clones the element and re-selects it
    // TODO: Test for this bug on load and add it to "support" object instead of
    // browser sniffing
    //
    // Parameters:
    // elem - The (text) DOM element to clone
    const ffClone = function (elem) {
      if (!svgedit.browser.isGecko()) { return elem; }
      const clone = elem.cloneNode(true);
      elem.parentNode.insertBefore(clone, elem);
      elem.parentNode.removeChild(elem);
      selectorManager.releaseSelector(elem);
      selectedElements[0] = clone;
      selectorManager.requestSelector(clone).showGrips(true);
      return clone;
    };

    // this.each is deprecated, if any extension used this it can be recreated by doing this:
    // $(canvas.getRootElem()).children().each(...)

    // this.each = function(cb) {
    //  $(svgroot).children().each(cb);
    // };

    // Function: setRotationAngle
    // Removes any old rotations if present, prepends a new rotation at the
    // transformed center
    //
    // Parameters:
    // val - The new rotation angle in degrees
    // preventUndo - Boolean indicating whether the action should be undoable or not
    this.setRotationAngle = function (val, preventUndo, element) {
      // ensure val is the proper type
      val = Math.round(parseFloat(val));

      const elem = element || selectedElements[0];
      const oldTransform = elem.getAttribute('transform') || '';
      const bbox = svgedit.utilities.getBBox(elem);
      const cx = bbox.x + bbox.width / 2;
      const cy = bbox.y + bbox.height / 2;
      const tlist = svgedit.transformlist.getTransformList(elem);

      // only remove the real rotational transform if present (i.e. at index=0)
      if (tlist.numberOfItems > 0) {
        for (let i = 0; i < tlist.numberOfItems; i++) {
          if (tlist.getItem(i).type === 4) {
            tlist.removeItem(i);
            break;
          }
        }
      }
      // find R_nc and insert it
      if (val !== 0 && Math.abs(val) !== 360) {
        const center = svgedit.math.transformPoint(cx, cy, svgedit.math.transformListToTransform(tlist).matrix);
        const rNc = svgroot.createSVGTransform();
        rNc.setRotate(val, center.x, center.y);
        if (tlist.numberOfItems) {
          tlist.insertItemBefore(rNc, 0);
        } else {
          tlist.appendItem(rNc);
        }
      } else if (tlist.numberOfItems === 0) {
        elem.removeAttribute('transform');
      }

      if (!preventUndo) {
        // we need to undo it, then redo it so it can be undo-able! :)
        // TODO: figure out how to make changes to transform list undo-able cross-browser?
        const newTransform = elem.getAttribute('transform') || '';
        elem.setAttribute('transform', oldTransform);
        changeSelectedAttribute('transform', newTransform, selectedElements);
        call('editor.changed', selectedElements);
      }

      svgedit.utilities.getElem('pathpointgrip_container');

      const selector = selectorManager.requestSelector(elem);
      selector.resize();
      selector.updateGripCursors(val);
    };

    this.setFlipImage = function (imageId, flipType, url, rootElem, afterProcessing) {
      let rootElemIsCurrPage;
      if (!rootElem) {
        rootElem = document;
        rootElemIsCurrPage = true;
      }
      const image = rootElem.querySelector('image#' + imageId);
      if (!image) return;

      const {
        VERTICAL: flippedVerticalClass,
        HORIZONTAL: flippedHorizontalClass
      } = INKIVE.C.FLIP_IMAGE;

      const imageUrl = INKIVE.ImagesFactory.getImageUrl(image);
      if (imageUrl) image.setAttribute('data-original-href', imageUrl);

      const isBackground = image.parentElement.classList.contains('background');
      const isFirstLoad = typeof url === 'string';

      if (rootElemIsCurrPage) {
        INKIVE.DesignFacade.trigger('flipping_image', flipType);
      }

      if (isFirstLoad || !rootElemIsCurrPage) {
        addFlippedAttributes();
      } else {
        const flippedImage = isBackground
          ? INKIVE.DesignFacade.get('background').flipped
          : INKIVE.DesignFacade.get(imageId).flipped;

        if (flipType.horizontal) {
          flippedImage.horizontal
            ? removeFlippedAttributes(flippedHorizontalClass, flippedImage)
            : addFlippedAttributes();
        }

        if (flipType.vertical) {
          flippedImage.vertical
            ? removeFlippedAttributes(flippedVerticalClass, flippedImage)
            : addFlippedAttributes();
        }
      }

      function getClipPathCoords () {
        let newX = null;
        let newY = null;
        const clipPath = image.getAttribute('clip-path');
        const imageX = Number(image.getAttribute('x')) || 0;
        const imageY = Number(image.getAttribute('y')) || 0;
        const imageWidth = Number(image.getAttribute('width')) || 0;
        const imageHeight = Number(image.getAttribute('height')) || 0;

        if (clipPath && !isFirstLoad) {
          const clipPathSelector = clipPath.match(/url\(["']?([^)"']+)/)[1];
          const rect = rootElem.querySelector(`${clipPathSelector} rect`);

          if (rect) {
            const rectX = Number(rect.getAttribute('x')) || 0;
            const rectY = Number(rect.getAttribute('y')) || 0;
            const rectWidth = Number(rect.getAttribute('width')) || 0;
            const rectHeight = Number(rect.getAttribute('height')) || 0;

            const imageRightMargin = imageX + imageWidth;
            const rectRightMargin = rectX + rectWidth;
            const imageBottomMargin = imageY + imageHeight;
            const rectBottomMargin = rectY + rectHeight;

            newX = flipType.horizontal
              ? rectX - (imageRightMargin - rectRightMargin)
              : imageX;
            newY = flipType.vertical
              ? rectY - (imageBottomMargin - rectBottomMargin)
              : imageY;
          }
        } else if (isBackground && !isFirstLoad) {
          const svgContent = rootElem.querySelector('#svgcontent') || rootElem;
          const sWidth = Number(svgContent.getAttribute('width'));
          const sHeight = Number(svgContent.getAttribute('height'));

          newX = flipType.horizontal
            ? -(imageX + imageWidth - sWidth)
            : imageX;
          newY = flipType.vertical
            ? -(imageY + imageHeight - sHeight)
            : imageY;
        }

        return { newX, newY };
      }

      function addClipPathCoords () {
        const { newX, newY } = getClipPathCoords();

        if (newX !== null || newY !== null) {
          image.setAttribute('x', newX);
          image.setAttribute('y', newY);
        }
      }

      function getFlippedAttributes () {
        return new Promise((resolve, reject) => {
          const imageObj = new Image();

          imageObj.setAttribute('crossOrigin', 'Anonymous');
          imageObj.src = url || image.getAttribute('xlink:href');

          imageObj.onerror = reject;
          imageObj.onload = function () {
            const myCanvas = document.createElement('canvas');

            myCanvas.width = this.naturalWidth;
            myCanvas.height = this.naturalHeight;
            const context = myCanvas.getContext('2d');
            const originalUrl = image.dataset.originalHref || imageObj.src;

            context.save();

            const flipHScale = flipType.horizontal ? -1 : 1;
            const flipVScale = flipType.vertical ? -1 : 1;

            context.scale(flipHScale, flipVScale);
            context.drawImage(imageObj, 0, 0, imageObj.width * flipHScale, imageObj.height * flipVScale);
            context.restore();

            resolve({
              dataURL: myCanvas.toDataURL(),
              originalURL: originalUrl
            });
          };
        });
      }

      function addFlippedAttributes (isHorizontalAndVertical) {
        getFlippedAttributes().then(({ dataURL, originalURL }) => {
          if (isFirstLoad) {
            svgedit.utilities.assignAttributes(image, {
              'xlink:href': dataURL,
              'data-original-href': originalURL
            });
          } else {
            image.setAttribute('xlink:href', dataURL);
            image.setAttribute('data-original-href', originalURL);
          }

          if (!isHorizontalAndVertical) {
            flipType.horizontal && image.classList.add(flippedHorizontalClass);
            flipType.vertical && image.classList.add(flippedVerticalClass);
          }

          // Cache original url
          const cachedUrl = INKIVE.Helpers.isDataURL(originalURL) ? null : originalURL;
          INKIVE.ImagesFactory.setImageUrl(image, cachedUrl);

          addClipPathCoords();

          if (rootElemIsCurrPage) {
            const popupFlipV = document.querySelector('.popupmenu .icon-flipv');
            const popupFlipH = document.querySelector('.popupmenu .icon-fliph');
            if (popupFlipV) popupFlipV.parentElement.classList.toggle('applied', image.classList.contains(flippedVerticalClass));
            if (popupFlipH) popupFlipH.parentElement.classList.toggle('applied', image.classList.contains(flippedHorizontalClass));

            INKIVE.DesignFacade.trigger('flipped_image', flipType);
          }

          // Refactor to promises when I have time
          if (afterProcessing) afterProcessing();
        });
      }

      function removeFlippedAttributes (orientation, flippedImage) {
        image.classList.remove(orientation);

        if (!flippedImage.horizontal && !flippedImage.vertical) {
          image.setAttribute('xlink:href', image.getAttribute('data-original-href'));
          image.removeAttribute('data-original-href');
          image.addEventListener('load', () => addClipPathCoords(), { once: true });
        } else {
          addFlippedAttributes(true);
        }
      }
    };

    // Function: recalculateAllSelectedDimensions
    // Runs recalculateDimensions on the selected elements,
    // adding the changes to a single batch command
    const recalculateAllSelectedDimensions = this.recalculateAllSelectedDimensions = function () {
      const changes = [];
      let i = selectedElements.length;

      while (i--) {
        const elem = selectedElements[i];
        if (!elem) continue;

        const cmd = svgedit.recalculate.recalculateDimensions(elem);
        cmd && changes.push(cmd);
      }

      changes.length && call('editor.changed', selectedElements);

      return changes;
    };

    // this is how we map paths to our preferred relative segment types
    const pathMap = [0, 'z', 'M', 'm', 'L', 'l', 'C', 'c', 'Q', 'q', 'A', 'a',
      'H', 'h', 'V', 'v', 'S', 's', 'T', 't'];

    // Debug tool to easily see the current matrix in the browser's console
    const logMatrix = function (m) {
      console.log([m.a, m.b, m.c, m.d, m.e, m.f]);
    };

    // Group: Selection

    const toggleRestrictedOverlayIcons = this.toggleRestrictedOverlayIcons = function (type) {
      type
        ? doToggle(type)
        : _.each(['image', 'icon', 'logo', 'video'], doToggle);

      function doToggle (type) {
        const $overlayIcons = $('.overlay.' + type);

        if (!$overlayIcons.length || INKIVE.C.BOOK_TYPE.RESTRICTED_DESIGN !== INKIVE.C.BOOK.book_type) {
          return;
        }

        const hideAdd = !INKIVE.RestrictionsFacade.isFunctionalityAllowed(type + '_add');
        const hideBackground = !INKIVE.RestrictionsFacade.isReplaceAllowed('background');
        const replaceRestricted = INKIVE.RestrictionsFacade.isReplaceRestrictedSelected();
        const hideReplace = !INKIVE.RestrictionsFacade.isReplaceAllowed(replaceRestricted);

        $overlayIcons.find('[data-type=background]').toggleClass('hidden', hideBackground);
        $overlayIcons.find('li[data-type=replace-' + type + ']').toggleClass('hidden', hideReplace);
        $overlayIcons.find('li[data-type=' + type + ']').toggleClass('hidden', hideAdd);
      }
    };

    // Function: clearSelection
    // Clears the selection. The 'selected' handler is then called.
    // Parameters:
    // noCall - Optional boolean that when true does not call the "selected" handler
    const clearSelection = this.clearSelection = (noCall) => {
      toggleRestrictedOverlayIcons();

      $('.overlay [data-type=replace-image], .overlay.text, .overlay.icon, .overlay.logo, .overlay.video [data-type=replace-video]')
        .addClass('hidden');
      this.toggleKWLSTextReplace && this.toggleKWLSTextReplace(true);

      if (selectedElements[0] !== null) {
        let i;
        let elem;
        const len = selectedElements.length;
        for (i = 0; i < len; ++i) {
          elem = selectedElements[i];
          if (elem === null) { break; }
          selectorManager.releaseSelector(elem);
          selectedElements[i] = null;
        }
        //    selectedBBoxes[0] = null;
      }
      if (!noCall) { call('editor.selected', selectedElements); }

      if (selectedElements.length === 0) {
        INKIVE.Menus.elementPopupMenu?.hide?.();
      }
    };

    // TODO: refactor. Canvas should not know about menus
    const showOverlayElements = this.showOverlayElements = () => {
      if (!this.getSelectedElems) {
        return;
      }

      const elemsToAdd = this.getSelectedElems() || [];
      const isOneElement = elemsToAdd.length === 1 && elemsToAdd[0];
      const isFrame = isOneElement && $(elemsToAdd).is('.sticker') && $(elemsToAdd).find('image.subelement').length;

      [
        ['.overlay.image', !!this.getMode().match('replace-')],
        ['.overlay.image [data-type=replace-image]', !(isOneElement && ($(elemsToAdd).is('image') || isFrame))],
        ['.overlay.text', !(isOneElement && $(elemsToAdd).is('.textarea'))],
        ['.overlay.logo', !(isOneElement && $(elemsToAdd).is('.logo'))],
        ['.overlay.icon, .overlay.sticker', !(isOneElement && $(elemsToAdd).is('.icon, .sticker'))],
        ['.overlay.video [data-type=replace-video]', !(isOneElement && $(elemsToAdd).is('.video'))]
      ].forEach(([selector, shouldHide]) => {
        toggleHidden(selector, shouldHide);
      });

      toggleRestrictedOverlayIcons();

      this.toggleKWLSTextReplace && this.toggleKWLSTextReplace(!(isOneElement && $(elemsToAdd).is('.textarea')));

      function toggleHidden (selector, shouldHide) {
        const items = document.querySelectorAll(selector);
        items.forEach(element => {
          element.classList.toggle('hidden', shouldHide);
        });
      }
    };

    this.toggleKWLSTextReplace = function (hide) {
      const $kwlsTab = $('#tabKWLS, #tabMLS, #tabRedfin');
      if (!$kwlsTab.length) {
        return;
      }

      $kwlsTab.find('.replace-text').toggleClass('hidden', hide);
      $kwlsTab.find('.add-text').toggleClass('stand-alone', hide);
    };

    // Function: addToSelection
    // Adds a list of elements to the selection. The 'selected' handler is then called.
    //
    // Parameters:
    // elemsToAdd - an array of DOM elements to add to the selection
    // showGrips - a boolean flag indicating whether the resize grips should be shown
    const addToSelection = this.addToSelection = function (elemsToAdd, showGrips, quiet) {
      if (elemsToAdd.length === 0) { return; }
      if ($(elemsToAdd).hasClass('svgLoader')) { return; }

      // find the first null in our selectedElements array
      let j = 0;

      while (j < selectedElements.length) {
        if (selectedElements[j] === null) {
          break;
        }
        ++j;
      }

      function addElement (elem) {
        // Make "a" element's child be the selected element
        if (elem.tagName === 'a' && elem.childNodes.length === 1) {
          elem = elem.firstChild;
        }

        // if it's not already there, add it
        if (selectedElements.indexOf(elem) === -1) {
          selectedElements[j] = elem;

          j++;
          const sel = selectorManager.requestSelector(elem);

          if (selectedElements.length > 1) {
            sel.showGrips(false);
          }
        }
      }

      // now add each element consecutively
      let i = elemsToAdd.length;
      while (i--) {
        const elem = elemsToAdd[i];

        if (!elem || !svgedit.utilities.getBBox(elem)) {
          continue;
        }

        addElement(elem);
      }

      // Need to check (temporary fix) when it blank and loaded first time come out a error
      if (!selectedElements[0]) {
        call('editor.selected', selectedElements);
        return 1;
      }

      if (showGrips || selectedElements.length === 1) {
        selectorManager.requestSelector(selectedElements[0]).showGrips(true);
      } else {
        // For groups
        selectorManager.requestSelector(selectedElements[0]).showGrips(false);
      }

      quiet || call('editor.selected', selectedElements);

      // make sure the elements are in the correct order
      // See: http://www.w3.org/TR/DOM-Level-3-Core/core.html#Node3-compareDocumentPosition
      selectedElements.sort(function (a, b) {
        if (a && b && a.compareDocumentPosition) {
          return 3 - (b.compareDocumentPosition(a) & 6);
        }
        if (a === null) {
          return 1;
        }

        return 0;
      });

      // Make sure first elements are not null
      while (selectedElements[0] === null) {
        selectedElements.shift(0);
      }

      _.bind(showOverlayElements, this)();
    };

    // Function: selectOnly()
    // Selects only the given elements, shortcut for clearSelection(); addToSelection()
    //
    // Parameters:
    // elems - an array of DOM elements to be selected
    const selectOnly = this.selectOnly = function (elems, showGrips) {
      _.bind(_.partial(clearSelection, true), this)();
      _.bind(_.partial(addToSelection, elems, showGrips), this)();
    };

    // Function: clearHighlight
    // Clears the highlight. The 'highligh' handler is then called.
    this.clearHighlight = () => {
      if (!highlightedElement) return;

      highlightedElement.classList.remove('highlighted');

      if (!highlightedElement.classList.contains('selected')) {
        selectorManager.releaseSelector(highlightedElement);
        const selectedIndex = selectedElements.indexOf(highlightedElement);

        if (selectedIndex !== -1) {
          selectedElements.splice(selectedIndex, 1);
        }
      }

      highlightedElement = null;
    };

    // Function: highlightElement
    // selects the highlightElement
    this.highlightElement = function (element) {
      if (!element) return;

      element.classList.add('highlighted');

      const selector = selectorManager.requestSelector(element);
      selector.showGrips(false);

      if (selectedElements.indexOf(element) === -1) {
        selectedElements.push(element);
      }

      highlightedElement = element;
    };

    // TODO: could use slice here to make this faster?
    // TODO: should the 'selected' handler

    // Function: removeFromSelection
    // Removes elements from the selection.
    //
    // Parameters:
    // elemsToRemove - an array of elements to remove from selection
    const removeFromSelection = this.removeFromSelection = function (elemsToRemove, skipReselection) {
      if (selectedElements[0] === null) { return; }
      if (elemsToRemove.length === 0) { return; }

      // find every element and remove it from our array copy
      let i;
      let j = 0;
      const newSelectedItems = new Array(selectedElements.length);
      const len = selectedElements.length;
      for (i = 0; i < len; ++i) {
        const elem = selectedElements[i];
        if (elem) {
          // keep the item
          if (elemsToRemove.indexOf(elem) === -1) {
            newSelectedItems[j] = elem;
            j++;
          } else { // remove the item and its selector
            selectorManager.releaseSelector(elem);
          }
        }
      }
      // the copy becomes the master now
      selectedElements = newSelectedItems;
      skipReselection || call('editor.selected', selectedElements);
    };

    // Function: selectAllInCurrentLayer
    // Clears the selection, then adds all elements in the current layer to the selection.
    this.selectAllInCurrentLayer = function () {
      const currentLayer = getCurrentDrawing().getCurrentLayer();
      if (currentLayer) {
        currentMode = 'select';
        selectOnly.call(this, $(currentGroup || currentLayer).children());
      }
    };

    // Function: getMouseTarget
    // Gets the desired element from a mouse event
    //
    // Parameters:
    // evt - Event object from the mouse event
    //
    // Returns:
    // DOM element we want
    const getMouseTarget = this.getMouseTarget = function (evt, point, stop) {
      if (evt === null || !evt.target) {
        return null;
      }

      let mouseTarget = evt.target;

      if (mouseTarget.correspondingUseElement) {
        mouseTarget = mouseTarget.correspondingUseElement;
      }

      if ([NS.MATH, NS.HTML].indexOf(mouseTarget.namespaceURI) >= 0 && mouseTarget.id !== 'svgcanvas') {
        while (mouseTarget.nodeName !== 'foreignObject') {
          mouseTarget = mouseTarget.parentNode;
          if (!mouseTarget) return svgroot;
        }
      }

      const currentLayer = getCurrentDrawing().getCurrentLayer();
      if ([svgroot, container, svgcontent, currentLayer].indexOf(mouseTarget) >= 0) {
        return svgroot;
      }

      const $target = $(mouseTarget);

      if ($target.closest('#outsideCanvasSelectorGroup').length) {
        const relatedElement = $($target.attr('data-related-id'))[0];

        if (relatedElement) return relatedElement;
      }

      const isEdge = /Edge/.test(navigator.userAgent);

      if ($target.closest('#selectorParentGroup').length || (isEdge && $target.closest('#arrow').length)) {
        return selectorManager.selectorParentGroup;
      }

      while (mouseTarget && mouseTarget.parentNode !== (currentGroup || currentLayer)) {
        mouseTarget = mouseTarget.parentNode;
        if (stop && mouseTarget.parentNode === stop) break;
      }

      //
      //  // go up until we hit a child of a layer
      //  while (mouseTarget.parentNode.parentNode.tagName === 'g') {
      //    mouseTarget = mouseTarget.parentNode;
      //  }
      // Webkit bubbles the mouse event all the way up to the div, so we
      // set the mouseTarget to the svgroot like the other browsers
      //  if (mouseTarget.nodeName.toLowerCase() === "div") {
      //    mouseTarget = svgroot;
      //  }

      return mouseTarget;// || getMouseTargetBasedOnPosition(p, current_group || current_layer);
    };

    this.getMouseTargetBasedOnPosition = function (p, container) {
      if (!p) return;

      return _.chain($(container).contents()).reverse().find(function (element) {
        const box = svgedit.utilities.getBBox(element);
        if (!box) return null;

        return p.x > box.x * currentZoom && p.x < (box.x * currentZoom + box.width * currentZoom) &&
          p.y > box.y * currentZoom && p.y < (box.y * currentZoom + box.height * currentZoom);
      }).value();
    };

    this.replaceElement = function (from, to, keepId, keepProportions, skipReselection) {
      keepId = typeof keepId === 'undefined'
        ? true
        : keepId;
      keepProportions = typeof keepProportions === 'undefined'
        ? true
        : keepProportions;

      if (from) {
        const nextElementSibling = from?.nextElementSibling;
        const currentLayer = getCurrentDrawing().getCurrentLayer();
        nextElementSibling
          ? currentLayer.insertBefore(to, nextElementSibling)
          : currentLayer.appendChild(to);

        const bb = from.getBBox();
        let nBb = to.getBBox();

        let translate = svgroot.createSVGTransform();
        const scale = svgroot.createSVGTransform();
        const rtranslate = svgroot.createSVGTransform();

        const tlist = getTransformList(to);

        let sx = bb.width / nBb.width;
        let sy = bb.height / nBb.height;

        translate.setTranslate(0, 0);

        if (keepProportions) {
          const min = sx < sy
            ? sx
            : sy;
          scale.setScale(min, min);
        } else {
          scale.setScale(sx, sy);
        }

        rtranslate.setTranslate(0, 0);

        tlist.appendItem(translate);
        tlist.appendItem(scale);
        tlist.appendItem(rtranslate);

        recalculateDimensions(to, to.getBBox());

        translate = svgroot.createSVGTransform();

        if (keepProportions) {
          nBb = to.getBBox();
          sx = bb.x - nBb.x + (bb.width - nBb.width) / 2;
          sy = bb.y - nBb.y + (bb.height - nBb.height) / 2;
          translate.setTranslate(sx, sy);
        } else {
          translate.setTranslate(bb.x, bb.y);
        }

        tlist.appendItem(translate);
        recalculateDimensions(to);

        const selected = getSelectedElems();

        for (let i = 0; i < selected.length; i++) {
          if (selected[i] && selected[i].id === from.id) {
            removeFromSelection([from], skipReselection);
            keepId && to.setAttribute('id', selected[i].id);
            addToSelection([to], false, skipReselection);
            break;
          }
        }

        from.remove();
      }
    };

    // Loading
    this.loadingElement = function (boolen) {
      const $svgLoader = $('.svgLoader');

      if (boolen && !$svgLoader.length) {
        setTimeout(() => document.querySelector('.svgLoader')?.remove(), 5000);
        const svg = document.createElementNS(NS.SVG, 'svg');
        const lw = '10%';
        const lh = '10%';

        svg.setAttribute('class', 'svgLoader');
        svg.setAttribute('x', '45%');
        svg.setAttribute('y', '45%');
        svg.setAttribute('width', lw);
        svg.setAttribute('height', lh);
        svg.setAttribute('viewBox', '0 0 50 50');
        svg.setAttribute('style', 'pointer-events: none;');

        const path = document.createElementNS(NS.SVG, 'path');
        path.setAttribute('fill', INKIVE.C.WHITE_LABEL_CONFIGURATION.colors.highlight);
        path.setAttribute('d',
          'M43.935,25.145c0-10.318-8.364-18.683-18.683-18.683c-10.318,0-18.683,8.365-18.683,18.683h4.068c0-8.071,6.543-14.615,14.615-14.615c8.072,0,14.615,6.543,14.615,14.615H43.935z');

        const ani = document.createElementNS(NS.SVG, 'animateTransform');
        ani.setAttribute('attributeType', 'xml');
        ani.setAttribute('attributeName', 'transform');
        ani.setAttribute('type', 'rotate');
        ani.setAttribute('from', '0 25 25');
        ani.setAttribute('to', '360 25 25');
        ani.setAttribute('dur', '0.6s');
        ani.setAttribute('repeatCount', 'indefinite');

        path.appendChild(ani);
        svg.appendChild(path);

        $('#svgroot').get(0).appendChild(svg);
      } else if (!boolen) {
        $svgLoader.remove();
      }
    };

    this.addElement = function (type, attr) {
      function background (attr) {
        let background;
        const defaults = {
          type: 'color',
          data: '#FFFFFF'
        };
        attr = $.extend(defaults, attr);

        let g = $('g.background').get(0);

        if (!g) {
          g = document.createElementNS(NS.SVG, 'g');
          $(g).addClass('background');

          g.setAttribute('style', 'pointer-events:none');

          svgcontent.insertBefore(g, svgcontent.firstChild);
        }

        backgroundReplacement(g, attr);

        let flipProperties;
        let elem = null;

        switch (attr.type) {
          case 'color':
            elem = document.createElementNS(NS.SVG, 'rect');
            elem.setAttribute('id', 'svg_background_color');
            elem.setAttribute('fill', attr.data);
            elem.setAttribute('x', 0);
            elem.setAttribute('y', 0);
            elem.setAttribute('width', '100%');
            elem.setAttribute('height', '100%');

            if (attr.smart_asset) {
              elem.setAttribute(
                constants.smartAssets.attributes.definedAsset,
                attr.smart_asset
              );
            }

            background = $(g).prepend(elem);
            break;

          case 'image':
            elem = document.createElementNS(NS.SVG, 'image');
            elem.setAttribute('id', 'svg_background_image');
            _.isNumber(attr.opacity) && elem.setAttribute('opacity', attr.opacity);
            attr.favouiritesId && elem.setAttribute('class', 'favourites-background-image-id-' + attr.favouiritesId);
            attr.old_image_id && elem.setAttribute('data-old-id', attr.old_image_id);

            if (attr.smart_asset) {
              elem.setAttribute(
                constants.smartAssets.attributes.definedAsset,
                attr.smart_asset
              );
            }
            if (attr.custom_placeholder) {
              elem.setAttribute(
                constants.smartAssets.attributes.userInput,
                attr.custom_placeholder
              );
            }

            if (attr.attrs) {
              elem.setAttribute('x', attr.attrs.x);
              elem.setAttribute('y', attr.attrs.y);
              elem.setAttribute('width', attr.attrs.width);
              elem.setAttribute('height', attr.attrs.height);

              if (attr.attrs.class) {
                elem.setAttribute('class', attr.attrs.class);
                if (/image-flip/.test(attr.attrs.class)) {
                  flipProperties = {
                    vertical: attr.attrs.class.indexOf(INKIVE.C.FLIP_IMAGE.VERTICAL) > -1,
                    horizontal: attr.attrs.class.indexOf(INKIVE.C.FLIP_IMAGE.HORIZONTAL) > -1
                  };
                }
              }
            }
            background = g.appendChild(elem);

            if (flipProperties) {
              INKIVE.Tool.getInstance().editor.canvas.setFlipImage(elem.id, flipProperties, attr.data);
            } else {
              elem.setAttributeNS(NS.XLINK, 'xlink:href', attr.data);
            }

            break;
        }
        g.setAttribute('class', 'background');

        const title = document.createElementNS(NS.SVG, 'title');
        title.setAttribute('style', 'pointer-events:inherit');
        title.textContent = 'Background';
        g.appendChild(title);

        return background;
      }

      function backgroundReplacement (g, attr) {
        const gNodes = $(g).children();
        $.each(gNodes, function () {
          const elementInDOM = $(this);
          let removeElement = false;

          switch (elementInDOM.prop('tagName')) {
            case 'title':
              removeElement = true;
              break;
            case 'rect':
              removeElement = attr.type === 'color';
              break;
            case 'image':
              if (attr.type === 'image') {
                attr.opacity = elementInDOM.attr('opacity');
                removeElement = true;
              } else if (attr.type === 'color') {
                if (!elementInDOM.attr('opacity') || elementInDOM.attr('opacity') === 1) {
                  removeElement = false;
                  INKIVE.EditorFacade.canvas.call('advice.show', 'background-color-add');
                }
              }
              break;
          }
          removeElement && elementInDOM.remove();
        });
      }

      function sticker (attr) {
        const defaults = {
          x: dimensions[0] - 100 / 2,
          y: dimensions[1] - 100 / 2,
          width: 100,
          height: 100
        };
        attr = $.extend(defaults, attr);

        return addSvgElementFromJson({
          element: 'image',
          attr: {
            id: attr.replacementId || getNextId(),
            class: 'element sticker' +
              (attr.favouiritesId
                ? ' favourites-sticker-id-' + attr.favouiritesId
                : '') +
              (attr.placeholder
                ? ' placeholder-frame'
                : '') +
              (attr.banner
                ? ' text-banner'
                : ''),
            'xlink:href': attr.url,
            x: attr.x,
            y: attr.y,
            width: attr.width,
            height: attr.height
          }
        });
      }

      function layout (attr) {
        if (attr.background) {
          if (attr.background.startsWith('http')) {
            background({
              type: 'image',
              data: attr.background
            });
          } else if (attr.background.startsWith('#')) {
            background({
              type: 'color',
              data: attr.background
            });
          } else {
            let g = $('g.background').get(0);

            if (!g) {
              g = document.createElementNS(NS.SVG, 'g');
              $(g).addClass('background');
              g.setAttribute('style', 'pointer-events:none');
              svgcontent.insertBefore(g, svgcontent.firstChild);
            }

            g.innerHTML = attr.background;

            const $backgroundImage = $(g).find('image');
            $backgroundImage.length && processBackgroundImage();

            function processBackgroundImage () {
              const hasSizeProperties = parseFloat($backgroundImage[0].getAttribute('width')) &&
                parseFloat($backgroundImage[0].getAttribute('height'));
              const image = new Image();

              image.src = INKIVE.Helpers.getCORSSecureUrl($backgroundImage.attr('xlink:href'));

              $(image).load(function () {
                $backgroundImage.attr('xlink:href', image.src);

                if (!hasSizeProperties) {
                  const size = INKIVE.DesignFacade.setters.background._getBackgroundSize(image);

                  $backgroundImage.attr('x', (canvas.contentW - size.width) / 2);
                  $backgroundImage.attr('y', (canvas.contentH - size.height) / 2);
                  $backgroundImage.attr('width', size.width);
                  $backgroundImage.attr('height', size.height);
                }
              });
            }
          }
        } else {
          background({
            type: 'color',
            data: 'none'
          });
        }

        runExtensions('setLayout', attr);
      }

      function image (at) {
        const attr = {
          id: getNextId(),
          class: 'element image',
          'xlink:href': at.url,
          x: getDefined(at.x, dimensions[0] - 100 / 2),
          y: getDefined(at.y, dimensions[1] - 100 / 2),
          width: getDefined(at.width, 100),
          height: getDefined(at.height, 100)
        };

        if (at.smart_asset) {
          attr[constants.smartAssets.attributes.definedAsset] = at.smart_asset;
          attr[constants.smartAssets.attributes.definedContentType] = constants.smartAssets.typeToProperty.images;
        }

        return addSvgElementFromJson({ attr, element: 'image' });
      }

      function getDefined (value, fallbackValue) {
        return typeof value === 'undefined' ? fallbackValue : value;
      }

      function video (attr) {
        return INKIVE.Video.addElement(attr, canvas);
      }

      function textarea (attr) {
        return new INKIVE.Text.Textarea(attr).render(canvas);
      }

      let elem = null;

      recalculatePositionBasedOnLayer(attr);

      switch (type) {
        case 'background':
          elem = background(attr);
          break;
        case 'sticker':
          elem = sticker(attr);
          break;
        case 'layout':
          layout(attr);
          break;
        case 'textarea':
          elem = textarea(attr);
          break;
        case 'image':
          elem = image(attr);
          break;
        case 'video':
          elem = video(attr);
          break;
        default:
          break;
      }

      return elem;
    };

    // Function: preventClickDefault
    // Prevents default browser click behaviour on the given element
    //
    // Parameters:
    // img - The DOM element to prevent the cilck on
    const preventClickDefault = function (img) {
      $(img).click(function (e) { e.preventDefault(); });
    };

    // Group: Text edit functions
    // Functions relating to editing text elements

    // TODO: Migrate all of this code into path.js
    // Group: Path edit functions
    // Functions relating to editing path elements
    const pathActions = canvas.pathActions = (function () {
      let subpath = false;
      let currentPath;
      let newPoint,
        firstCtrl;

      function resetD (p) {
        p.setAttribute('d', pathActions.convertPath(p));
      }

      // TODO: Move into path.js
      svgedit.path.Path.prototype.endChanges = function () {
        if (svgedit.browser.isWebkit()) { resetD(this.elem); }
        call('editor.changed', [this.elem]);
      };

      svgedit.path.Path.prototype.addPtsToSelection = function (indexes) {
        let i,
          seg;
        if (!$.isArray(indexes)) { indexes = [indexes]; }
        for (i = 0; i < indexes.length; i++) {
          const index = indexes[i];
          seg = this.segs[index];
          if (seg.ptgrip) {
            if (this.selected_pts.indexOf(index) === -1 && index >= 0) {
              this.selected_pts.push(index);
            }
          }
        }
        this.selected_pts.sort();
        i = this.selected_pts.length;
        const grips = new Array(i);
        // Loop through points to be selected and highlight each
        while (i--) {
          const pt = this.selected_pts[i];
          seg = this.segs[pt];
          seg.select(true);
          grips[i] = seg.ptgrip;
        }
        // TODO: Correct this:
        pathActions.canDeleteNodes = true;

        pathActions.closed_subpath = this.subpathIsClosed(this.selected_pts[0]);

        call('editor.selected', grips);
      };

      currentPath = null;
      let drawnPath = null;
      let hasMoved = false;

      // This function converts a polyline (created by the fh_path tool) into
      // a path element and coverts every three line segments into a single bezier
      // curve in an attempt to smooth out the free-hand
      const smoothPolylineIntoPath = function (element) {
        let i;
        const points = element.points;
        const N = points.numberOfItems;

        if (N >= 4) {
          // loop through every 3 points and convert to a cubic bezier curve segment
          //
          // NOTE: this is cheating, it means that every 3 points has the potential to
          // be a corner instead of treating each point in an equal manner. In general,
          // this technique does not look that good.
          //
          // I am open to better ideas!
          //
          // Reading:
          // - http://www.efg2.com/Lab/Graphics/Jean-YvesQueinecBezierCurves.htm
          // - http://www.codeproject.com/KB/graphics/BezierSpline.aspx?msg=2956963
          // - http://www.ian-ko.com/ET_GeoWizards/UserGuide/smooth.htm
          // - http://www.cs.mtu.edu/~shene/COURSES/cs3621/NOTES/spline/Bezier/bezier-der.html
          let curpos = points.getItem(0);
          let prevCtlPt = null;
          let d = [];
          d.push(['M', curpos.x, ',', curpos.y, ' C'].join(''));
          for (i = 1; i <= (N - 4); i += 3) {
            let ct1 = points.getItem(i);
            const ct2 = points.getItem(i + 1);
            const end = points.getItem(i + 2);

            // if the previous segment had a control point, we want to smooth out
            // the control points on both sides
            if (prevCtlPt) {
              const newpts = svgedit.path.smoothControlPoints(prevCtlPt, ct1, curpos);
              if (newpts && newpts.length === 2) {
                const prevArr = d[d.length - 1].split(',');
                prevArr[2] = newpts[0].x;
                prevArr[3] = newpts[0].y;
                d[d.length - 1] = prevArr.join(',');
                ct1 = newpts[1];
              }
            }

            d.push([ct1.x, ct1.y, ct2.x, ct2.y, end.x, end.y].join(','));

            curpos = end;
            prevCtlPt = ct2;
          }
          // handle remaining line segments
          d.push('L');
          while (i < N) {
            const pt = points.getItem(i);
            d.push([pt.x, pt.y].join(','));
            i++;
          }
          d = d.join(' ');

          // create new path element
          element = addSvgElementFromJson({
            element: 'path',
            curStyles: true,
            attr: {
              id: getId(),
              d,
              fill: 'none'
            }
          });
          // No need to call "changed", as this is already done under mouseUp
        }
        return element;
      };

      return {
        mouseDown: function (evt, mouseTarget, startX, startY) {
          let id;
          if (currentMode === 'path') {
            let mouseX = startX;
            let mouseY = startY;

            let x = mouseX / currentZoom;
            let y = mouseY / currentZoom;
            let stretchy = svgedit.utilities.getElem('path_stretch_line');
            newPoint = [x, y];

            if (curConfig.gridSnapping) {
              x = svgedit.utilities.snapToGrid(x);
              y = svgedit.utilities.snapToGrid(y);
              mouseX = svgedit.utilities.snapToGrid(mouseX);
              mouseY = svgedit.utilities.snapToGrid(mouseY);
            }

            if (!stretchy) {
              stretchy = document.createElementNS(NS.SVG, 'path');
              svgedit.utilities.assignAttributes(stretchy, {
                id: 'path_stretch_line',
                stroke: '#2222CC',
                'stroke-width': '0.5',
                fill: 'none'
              });
              stretchy = svgedit.utilities.getElem('selectorParentGroup').appendChild(stretchy);
            }
            stretchy.setAttribute('display', 'inline');

            let keep = null;
            let index;
            // if pts array is empty, create path element with M at current point
            if (!drawnPath) {
              const dAttr = 'M' + x + ',' + y + ' ';
              drawnPath = addSvgElementFromJson({
                element: 'path',
                curStyles: true,
                attr: {
                  d: dAttr,
                  id: getNextId(),
                  opacity: curShape.opacity / 2
                }
              });
              // set stretchy line to first point
              stretchy.setAttribute('d', ['M', mouseX, mouseY, mouseX, mouseY].join(' '));
              index = subpath
                ? svgedit.path.path.segs.length
                : 0;
              svgedit.path.addPointGrip(index, mouseX, mouseY);
            } else {
              // determine if we clicked on an existing point
              const seglist = drawnPath.pathSegList;
              let i = seglist.numberOfItems;
              const FUZZ = 6 / currentZoom;
              let clickOnPoint = false;
              while (i) {
                i--;
                const item = seglist.getItem(i);
                const px = item.x;
                const py = item.y;
                // found a matching point
                if (x >= (px - FUZZ) && x <= (px + FUZZ) && y >= (py - FUZZ) && y <= (py + FUZZ)) {
                  clickOnPoint = true;
                  break;
                }
              }

              // get path element that we are in the process of creating
              id = getId();

              // Remove previous path object if previously created
              svgedit.path.removePath_(id);

              const newpath = svgedit.utilities.getElem(id);
              let newseg;
              let sSeg;
              const len = seglist.numberOfItems;
              // if we clicked on an existing point, then we are done this path, commit it
              // (i, i+1) are the x,y that were clicked on
              if (clickOnPoint) {
                // if clicked on any other point but the first OR
                // the first point was clicked on and there are less than 3 points
                // then leave the path open
                // otherwise, close the path
                if (i <= 1 && len >= 2) {
                  // Create end segment
                  const absX = seglist.getItem(0).x;
                  const absY = seglist.getItem(0).y;

                  sSeg = stretchy.pathSegList.getItem(1);
                  if (sSeg.pathSegType === 4) {
                    newseg = drawnPath.createSVGPathSegLinetoAbs(absX, absY);
                  } else {
                    newseg = drawnPath.createSVGPathSegCurvetoCubicAbs(
                      absX,
                      absY,
                      sSeg.x1 / currentZoom,
                      sSeg.y1 / currentZoom,
                      absX,
                      absY
                    );
                  }

                  const endseg = drawnPath.createSVGPathSegClosePath();
                  seglist.appendItem(newseg);
                  seglist.appendItem(endseg);
                } else if (len < 3) {
                  keep = false;
                  return keep;
                }
                $(stretchy).remove();

                // This will signal to commit the path
                drawnPath = null;
                canvas.started = false;

                if (subpath) {
                  if (svgedit.path.path.matrix) {
                    svgedit.coords.remapElement(newpath, {}, svgedit.path.path.matrix.inverse());
                  }

                  const newD = newpath.getAttribute('d');
                  const origD = $(svgedit.path.path.elem).attr('d');
                  $(svgedit.path.path.elem).attr('d', origD + newD);
                  $(newpath).remove();
                  if (svgedit.path.path.matrix) {
                    svgedit.path.recalcRotatedPath();
                  }
                  svgedit.path.path.init();
                  pathActions.toEditMode(svgedit.path.path.elem);
                  svgedit.path.path.selectPt();
                  return false;
                }
              } else {
                // else, create a new point, update path element
                // Checks if current target or parents are #svgcontent
                const target = getMouseTarget(evt);
                if (!$.contains(container, target) && !$(target).parents('#selectorParentGroup').andSelf().length) {
                  // Clicked outside canvas, so don't make point
                  console.log('Clicked outside canvas');
                  return false;
                }

                const num = drawnPath.pathSegList.numberOfItems;
                const last = drawnPath.pathSegList.getItem(num - 1);
                const lastx = last.x;
                const lasty = last.y;

                if (evt.shiftKey) {
                  const xya = svgedit.math.snapToAngle(lastx, lasty, x, y);
                  x = xya.x;
                  y = xya.y;
                }

                // Use the segment defined by stretchy
                sSeg = stretchy.pathSegList.getItem(1);
                if (sSeg.pathSegType === 4) {
                  newseg = drawnPath.createSVGPathSegLinetoAbs(round(x), round(y));
                } else {
                  newseg = drawnPath.createSVGPathSegCurvetoCubicAbs(
                    round(x),
                    round(y),
                    sSeg.x1 / currentZoom,
                    sSeg.y1 / currentZoom,
                    sSeg.x2 / currentZoom,
                    sSeg.y2 / currentZoom
                  );
                }

                drawnPath.pathSegList.appendItem(newseg);

                x *= currentZoom;
                y *= currentZoom;

                // set stretchy line to latest point
                stretchy.setAttribute('d', ['M', x, y, x, y].join(' '));
                index = num;
                if (subpath) { index += svgedit.path.path.segs.length; }
                svgedit.path.addPointGrip(index, x, y);
              }
              //          keep = true;
            }

            return;
          }

          // TODO: Make sure current_path isn't null at this point
          if (!svgedit.path.path) { return; }

          svgedit.path.path.storeD();

          id = evt.target.id;
          let curPt;
          if (id.substr(0, 14) === 'pathpointgrip_') {
            // Select this point
            curPt = svgedit.path.path.cur_pt = parseInt(id.substr(14));
            svgedit.path.path.dragging = [startX, startY];
            const seg = svgedit.path.path.segs[curPt];

            // only clear selection if shift is not pressed (otherwise, add
            // node to selection)
            if (!evt.shiftKey) {
              if (svgedit.path.path.selected_pts.length <= 1 || !seg.selected) {
                svgedit.path.path.clearSelection();
              }
              svgedit.path.path.addPtsToSelection(curPt);
            } else if (seg.selected) {
              svgedit.path.path.removePtFromSelection(curPt);
            } else {
              svgedit.path.path.addPtsToSelection(curPt);
            }
          } else if (id.indexOf('ctrlpointgrip_') === 0) {
            svgedit.path.path.dragging = [startX, startY];

            const parts = id.split('_')[1].split('c');
            curPt = Number(parts[0]);
            const ctrlNum = Number(parts[1]);
            svgedit.path.path.selectPt(curPt, ctrlNum);
          }

          // Start selection box
          if (!svgedit.path.path.dragging) {
            if (canvas.rubberBox === null) {
              canvas.rubberBox = selectorManager.getRubberBandBox();
            }
            svgedit.utilities.assignAttributes(canvas.rubberBox, {
              x: startX * currentZoom,
              y: startY * currentZoom,
              width: 0,
              height: 0,
              display: 'inline'
            }, 100);
          }
        },
        mouseMove: function (mouseX, mouseY) {
          hasMoved = true;
          if (currentMode === 'path') {
            if (!drawnPath) { return; }
            const seglist = drawnPath.pathSegList;
            const index = seglist.numberOfItems - 1;

            if (newPoint) {
              // First point
              //          if (!index) {return;}

              // Set control points
              const pointGrip1 = svgedit.path.addCtrlGrip('1c1');
              const pointGrip2 = svgedit.path.addCtrlGrip('0c2');

              // dragging pointGrip1
              pointGrip1.setAttribute('cx', mouseX);
              pointGrip1.setAttribute('cy', mouseY);
              pointGrip1.setAttribute('display', 'inline');

              const ptX = newPoint[0];
              const ptY = newPoint[1];

              // set curve
              seglist.getItem(index);
              const curX = mouseX / currentZoom;
              const curY = mouseY / currentZoom;
              const altX = (ptX + (ptX - curX));
              const altY = (ptY + (ptY - curY));

              pointGrip2.setAttribute('cx', altX * currentZoom);
              pointGrip2.setAttribute('cy', altY * currentZoom);
              pointGrip2.setAttribute('display', 'inline');

              const ctrlLine = svgedit.path.getCtrlLine(1);
              svgedit.utilities.assignAttributes(ctrlLine, {
                x1: mouseX,
                y1: mouseY,
                x2: altX * currentZoom,
                y2: altY * currentZoom,
                display: 'inline'
              });

              if (index === 0) {
                firstCtrl = [mouseX, mouseY];
              } else {
                const last = seglist.getItem(index - 1);
                let lastX = last.x;
                let lastY = last.y;

                if (last.pathSegType === 6) {
                  lastX += (lastX - last.x2);
                  lastY += (lastY - last.y2);
                } else if (firstCtrl) {
                  lastX = firstCtrl[0] / currentZoom;
                  lastY = firstCtrl[1] / currentZoom;
                }
                svgedit.path.replacePathSeg(6, index, [ptX, ptY, lastX, lastY, altX, altY], drawnPath);
              }
            } else {
              const stretchy = svgedit.utilities.getElem('path_stretch_line');
              if (stretchy) {
                const prev = seglist.getItem(index);
                if (prev.pathSegType === 6) {
                  const prevX = prev.x + (prev.x - prev.x2);
                  const prevY = prev.y + (prev.y - prev.y2);
                  svgedit.path.replacePathSeg(6, 1,
                    [mouseX, mouseY, prevX * currentZoom, prevY * currentZoom, mouseX, mouseY], stretchy);
                } else if (firstCtrl) {
                  svgedit.path.replacePathSeg(6, 1, [mouseX, mouseY, firstCtrl[0], firstCtrl[1], mouseX, mouseY],
                    stretchy);
                } else {
                  svgedit.path.replacePathSeg(4, 1, [mouseX, mouseY], stretchy);
                }
              }
            }
            return;
          }
          // if we are dragging a point, let's move it
          if (svgedit.path.path.dragging) {
            const pt = svgedit.path.getPointFromGrip({
              x: svgedit.path.path.dragging[0],
              y: svgedit.path.path.dragging[1]
            }, svgedit.path.path);
            const mpt = svgedit.path.getPointFromGrip({
              x: mouseX,
              y: mouseY
            }, svgedit.path.path);
            const diffX = mpt.x - pt.x;
            const diffY = mpt.y - pt.y;
            svgedit.path.path.dragging = [mouseX, mouseY];

            if (svgedit.path.path.dragctrl) {
              svgedit.path.path.moveCtrl(diffX, diffY);
            } else {
              svgedit.path.path.movePts(diffX, diffY);
            }
          } else {
            svgedit.path.path.selected_pts = [];
            svgedit.path.path.eachSeg(function () {
              const seg = this;
              if (!seg.next && !seg.prev) { return; }

              const rbb = canvas.rubberBox.getBBox();

              const pt = svgedit.path.getGripPt(seg);
              const ptBb = {
                x: pt.x,
                y: pt.y,
                width: 0,
                height: 0
              };

              const sel = svgedit.math.rectsIntersect(rbb, ptBb);

              this.select(sel);
              // Note that addPtsToSelection is not being run
              if (sel) { svgedit.path.path.selected_pts.push(seg.index); }
            });
          }
        },
        mouseUp: function (evt, element) {
          // Create mode
          if (currentMode === 'path') {
            newPoint = null;
            if (!drawnPath) {
              element = svgedit.utilities.getElem(getId());
              canvas.started = false;
              firstCtrl = null;
            }

            return {
              keep: true,
              element
            };
          }

          // Edit mode

          if (svgedit.path.path.dragging) {
            const lastPt = svgedit.path.path.cur_pt;

            svgedit.path.path.dragging = false;
            svgedit.path.path.dragctrl = false;
            svgedit.path.path.update();

            if (hasMoved) {
              svgedit.path.path.endChanges();
            }

            if (!evt.shiftKey && !hasMoved) {
              svgedit.path.path.selectPt(lastPt);
            }
          } else if (canvas.rubberBox && canvas.rubberBox.getAttribute('display') !== 'none') {
            // Done with multi-node-select
            canvas.rubberBox.setAttribute('display', 'none');

            if (canvas.rubberBox.getAttribute('width') <= 2 && canvas.rubberBox.getAttribute('height') <= 2) {
              pathActions.toSelectMode(evt.target);
            }

            // else, move back to select mode
          } else {
            pathActions.toSelectMode(evt.target);
          }
          hasMoved = false;
        },
        toEditMode: function (element) {
          svgedit.path.path = svgedit.path.getPath_(element);
          currentMode = 'pathedit';
          clearSelection();
          svgedit.path.path.show(true).update();
          svgedit.path.path.oldbbox = svgedit.utilities.getBBox(svgedit.path.path.elem);
          subpath = false;
        },
        toSelectMode: function (elem) {
          const selPath = (elem === svgedit.path.path.elem);
          currentMode = 'select';
          svgedit.path.path.show(false);
          currentPath = false;
          clearSelection();

          if (svgedit.path.path.matrix) {
            // Rotated, so may need to re-calculate the center
            svgedit.path.recalcRotatedPath();
          }

          if (selPath) {
            call('editor.selected', [elem]);
            addToSelection([elem], true);
          }
        },
        addSubPath: function (on) {
          if (on) {
            // Internally we go into "path" mode, but in the UI it will
            // still appear as if in "pathedit" mode.
            currentMode = 'path';
            subpath = true;
          } else {
            pathActions.clear(true);
            pathActions.toEditMode(svgedit.path.path.elem);
          }
        },
        select: function (target) {
          if (currentPath === target) {
            pathActions.toEditMode(target);
            currentMode = 'pathedit';
          } else {
            // going into pathedit mode
            currentPath = target;
          }
        },
        reorient: function () {
          const elem = selectedElements[0];
          if (!elem) { return; }
          const angle = svgedit.utilities.getRotationAngle(elem);
          if (angle === 0) { return; }
          elem.getAttribute('d');
          elem.getAttribute('transform');
          clearSelection();
          this.resetOrientation(elem);

          // Set matrix to null
          svgedit.path.getPath_(elem).show(false).matrix = null;

          this.clear();

          addToSelection([elem], true);
          call('editor.changed', selectedElements);
        },

        clear: function (remove) {
          currentPath = null;
          if (drawnPath) {
            const elem = svgedit.utilities.getElem(getId());
            $(svgedit.utilities.getElem('path_stretch_line')).remove();
            $(elem).remove();
            $(svgedit.utilities.getElem('pathpointgrip_container')).find('*').attr('display', 'none');
            drawnPath = firstCtrl = null;
            canvas.started = false;
          } else if (currentMode === 'pathedit') {
            this.toSelectMode();
          }
          if (svgedit.path.path) { svgedit.path.path.init().show(false); }
        },
        resetOrientation: function (path) {
          if (path === null || path.nodeName !== 'path') { return false; }
          const tlist = svgedit.transformlist.getTransformList(path);
          const m = svgedit.math.transformListToTransform(tlist).matrix;
          tlist.clear();
          path.removeAttribute('transform');
          const segList = path.pathSegList;

          // Opera/win/non-EN throws an error here.
          // TODO: Find out why!
          // Presumed fixed in Opera 10.5, so commented out for now

          //      try {
          const len = segList.numberOfItems;
          //      } catch(err) {
          //        const fixed_d = pathActions.convertPath(path);
          //        path.setAttribute('d', fixed_d);
          //        segList = path.pathSegList;
          //        const len = segList.numberOfItems;
          //      }
          let i;

          for (i = 0; i < len; ++i) {
            const seg = segList.getItem(i);
            const type = seg.pathSegType;
            if (type === 1) { continue; }
            const pts = [];
            $.each(['', 1, 2], function (j, n) {
              const x = seg['x' + n];
              const y = seg['y' + n];
              if (x !== undefined && y !== undefined) {
                const pt = svgedit.math.transformPoint(x, y, m);
                pts.splice(pts.length, 0, pt.x, pt.y);
              }
            });
            svgedit.path.replacePathSeg(type, i, pts, path);
          }

          reorientGrads(path, m);
        },
        zoomChange: function () {
          if (currentMode === 'pathedit') {
            svgedit.path.path.update();
          }
        },
        getNodePoint: function () {
          const selPt = svgedit.path.path.selected_pts.length
            ? svgedit.path.path.selected_pts[0]
            : 1;

          const seg = svgedit.path.path.segs[selPt];
          return {
            x: seg.item.x,
            y: seg.item.y,
            type: seg.type
          };
        },
        linkControlPoints: function (linkPoints) {
          svgedit.path.setLinkControlPoints(linkPoints);
        },
        clonePathNode: function () {
          svgedit.path.path.storeD();

          const selPts = svgedit.path.path.selected_pts;

          let i = selPts.length;
          const nums = [];

          while (i--) {
            const pt = selPts[i];
            svgedit.path.path.addSeg(pt);

            nums.push(pt + i);
            nums.push(pt + i + 1);
          }
          svgedit.path.path.init().addPtsToSelection(nums);

          svgedit.path.path.endChanges();
        },
        opencloseSubPath: function () {
          const selOts = svgedit.path.path.selected_pts;
          // Only allow one selected node for now
          if (selOts.length !== 1) { return; }

          const elem = svgedit.path.path.elem;
          const list = elem.pathSegList;

          const index = selOts[0];

          let openPt = null;
          let startItem = null;

          // Check if subpath is already open
          svgedit.path.path.eachSeg(function (i) {
            if (this.type === 2 && i <= index) {
              startItem = this.item;
            }
            if (i <= index) { return true; }
            if (this.type === 2) {
              // Found M first, so open
              openPt = i;
              return false;
            }
            if (this.type === 1) {
              // Found Z first, so closed
              openPt = false;
              return false;
            }
          });

          if (openPt === null) {
            // Single path, so close last seg
            openPt = svgedit.path.path.segs.length - 1;
          }

          if (openPt !== false) {
            // Close this path

            // Create a line going to the previous "M"
            const newseg = elem.createSVGPathSegLinetoAbs(startItem.x, startItem.y);

            const closer = elem.createSVGPathSegClosePath();
            if (openPt === svgedit.path.path.segs.length - 1) {
              list.appendItem(newseg);
              list.appendItem(closer);
            } else {
              svgedit.path.insertItemBefore(elem, closer, openPt);
              svgedit.path.insertItemBefore(elem, newseg, openPt);
            }

            svgedit.path.path.init().selectPt(openPt + 1);
            return;
          }

          // M 1,1 L 2,2 L 3,3 L 1,1 z // open at 2,2
          // M 2,2 L 3,3 L 1,1

          // M 1,1 L 2,2 L 1,1 z M 4,4 L 5,5 L6,6 L 5,5 z
          // M 1,1 L 2,2 L 1,1 z [M 4,4] L 5,5 L(M)6,6 L 5,5 z

          const seg = svgedit.path.path.segs[index];

          if (seg.mate) {
            list.removeItem(index); // Removes last "L"
            list.removeItem(index); // Removes the "Z"
            svgedit.path.path.init().selectPt(index - 1);
            return;
          }

          let i;
          let lastM;
          let zSeg;

          // Find this sub-path's closing point and remove
          for (i = 0; i < list.numberOfItems; i++) {
            const item = list.getItem(i);

            if (item.pathSegType === 2) {
              // Find the preceding M
              lastM = i;
            } else if (i === index) {
              // Remove it
              list.removeItem(lastM);
              //            index--;
            } else if (item.pathSegType === 1 && index < i) {
              // Remove the closing seg of this subpath
              zSeg = i - 1;
              list.removeItem(i);
              break;
            }
          }

          let num = (index - lastM) - 1;

          while (num--) {
            svgedit.path.insertItemBefore(elem, list.getItem(lastM), zSeg);
          }

          const pt = list.getItem(lastM);

          // Make this point the new "M"
          svgedit.path.replacePathSeg(2, lastM, [pt.x, pt.y]);
          svgedit.path.path.init().selectPt(0);
        },
        deletePathNode: function () {
          if (!pathActions.canDeleteNodes) { return; }
          svgedit.path.path.storeD();

          const selPts = svgedit.path.path.selected_pts;
          let i = selPts.length;

          while (i--) {
            const pt = selPts[i];
            svgedit.path.path.deleteSeg(pt);
          }

          // Cleanup
          const cleanup = function () {
            const segList = svgedit.path.path.elem.pathSegList;
            let len = segList.numberOfItems;

            const remItems = function (pos, count) {
              while (count--) {
                segList.removeItem(pos);
              }
            };

            if (len <= 1) { return true; }

            while (len--) {
              const item = segList.getItem(len);
              if (item.pathSegType === 1) {
                const prev = segList.getItem(len - 1);
                const nprev = segList.getItem(len - 2);
                if (prev.pathSegType === 2) {
                  remItems(len - 1, 2);
                  cleanup();
                  break;
                } else if (nprev.pathSegType === 2) {
                  remItems(len - 2, 3);
                  cleanup();
                  break;
                }
              } else if (item.pathSegType === 2) {
                if (len > 0) {
                  const prevType = segList.getItem(len - 1).pathSegType;
                  // Path has M M
                  if (prevType === 2) {
                    remItems(len - 1, 1);
                    cleanup();
                    break;
                    // Entire path ends with Z M
                  } else if (prevType === 1 && segList.numberOfItems - 1 === len) {
                    remItems(len, 1);
                    cleanup();
                    break;
                  }
                }
              }
            }
            return false;
          };

          cleanup();

          // Completely delete a path with 1 or 0 segments
          if (svgedit.path.path.elem.pathSegList.numberOfItems <= 1) {
            pathActions.toSelectMode(svgedit.path.path.elem);
            canvas.deleteSelectedElements();
            return;
          }

          svgedit.path.path.init();
          svgedit.path.path.clearSelection();

          // TODO: Find right way to select point now
          // path.selectPt(sel_pt);
          if (window.opera) { // Opera repaints incorrectly
            const cp = $(svgedit.path.path.elem);
            cp.attr('d', cp.attr('d'));
          }
          svgedit.path.path.endChanges();
        },
        smoothPolylineIntoPath,
        setSegType: function (v) {
          svgedit.path.path.setSegType(v);
        },
        moveNode: function (attr, newValue) {
          const selOpts = svgedit.path.path.selected_pts;
          if (!selOpts.length) { return; }

          svgedit.path.path.storeD();

          // Get first selected point
          const seg = svgedit.path.path.segs[selOpts[0]];
          const diff = {
            x: 0,
            y: 0
          };
          diff[attr] = newValue - seg.item[attr];

          seg.move(diff.x, diff.y);
          svgedit.path.path.endChanges();
        },
        fixEnd: function (elem) {
          // Adds an extra segment if the last seg before a Z doesn't end
          // at its M point
          // M0,0 L0,100 L100,100 z
          const segList = elem.pathSegList;
          const len = segList.numberOfItems;
          let i,
            lastM;
          for (i = 0; i < len; ++i) {
            const item = segList.getItem(i);
            if (item.pathSegType === 2) {
              lastM = item;
            }

            if (item.pathSegType === 1) {
              const prev = segList.getItem(i - 1);
              if (prev.x !== lastM.x || prev.y !== lastM.y) {
                // Add an L segment here
                const newseg = elem.createSVGPathSegLinetoAbs(lastM.x, lastM.y);
                svgedit.path.insertItemBefore(elem, newseg, i);
                // Can this be done better?
                pathActions.fixEnd(elem);
                break;
              }
            }
          }
          if (svgedit.browser.isWebkit()) { resetD(elem); }
        },

        // Convert a path to one with only absolute or relative values
        convertPath: function (path, toRel) {
          const segList = path.pathSegList;
          const len = segList.numberOfItems;
          let curx = 0;
          let cury = 0;
          let d = '';
          let lastM = null;

          for (let i = 0; i < len; ++i) {
            const seg = segList.getItem(i); // if these properties are not in the segment, set them to zero

            let x = seg.x || 0;
            let y = seg.y || 0;
            let x1 = seg.x1 || 0;
            let y1 = seg.y1 || 0;
            let x2 = seg.x2 || 0;
            let y2 = seg.y2 || 0;
            const type = seg.pathSegType;
            let letter = pathMap[type]['to' + (toRel
              ? 'Lower'
              : 'Upper') + 'Case']();

            function pathDSegment (letter, points, morePoints, lastPoint) {
              $.each(points, function (i, pnt) {
                points[i] = svgedit.units.shortFloat(pnt);
              });
              let segment = letter + points.join(' ');

              if (morePoints) {
                segment += ' ' + morePoints.join(' ');
              }

              if (lastPoint) {
                segment += ' ' + svgedit.units.shortFloat(lastPoint);
              }

              return segment;
            }

            switch (type) {
              case 1:
                // z,Z closepath (Z/z)
                d += 'z';

                if (lastM && !toRel) {
                  curx = lastM[0];
                  cury = lastM[1];
                }

                break;

              case 12:
                // absolute horizontal line (H)
                x -= curx;
                // Fallthrough

              case 13:
                // relative horizontal line (h)
                if (toRel) {
                  curx += x;
                  letter = 'l';
                } else {
                  x += curx;
                  curx = x;
                  letter = 'L';
                } // Convert to "line" for easier editing

                d += pathDSegment(letter, [[x, cury]]);
                break;

              case 14:
                // absolute vertical line (V)
                y -= cury;
                // Fallthrough

              case 15:
                // relative vertical line (v)
                if (toRel) {
                  cury += y;
                  letter = 'l';
                } else {
                  y += cury;
                  cury = y;
                  letter = 'L';
                } // Convert to "line" for easier editing

                d += pathDSegment(letter, [[curx, y]]);
                break;

              case 2: // absolute move (M)
              case 4: // absolute line (L)
              case 18:
                // absolute smooth quad (T)
                x -= curx;
                y -= cury;
                // Fallthrough

              case 5: // relative line (l)
              case 3: // relative move (m)
              case 19:
                // relative smooth quad (t)
                if (toRel) {
                  curx += x;
                  cury += y;
                } else {
                  x += curx;
                  y += cury;
                  curx = x;
                  cury = y;
                }

                if (type === 2 || type === 3) {
                  lastM = [curx, cury];
                }

                d += pathDSegment(letter, [[x, y]]);
                break;

              case 6:
                // absolute cubic (C)
                x -= curx;
                x1 -= curx;
                x2 -= curx;
                y -= cury;
                y1 -= cury;
                y2 -= cury;
                // Fallthrough

              case 7:
                // relative cubic (c)
                if (toRel) {
                  curx += x;
                  cury += y;
                } else {
                  x += curx;
                  x1 += curx;
                  x2 += curx;
                  y += cury;
                  y1 += cury;
                  y2 += cury;
                  curx = x;
                  cury = y;
                }

                d += pathDSegment(letter, [[x1, y1], [x2, y2], [x, y]]);
                break;

              case 8:
                // absolute quad (Q)
                x -= curx;
                x1 -= curx;
                y -= cury;
                y1 -= cury;
                // Fallthrough

              case 9:
                // relative quad (q)
                if (toRel) {
                  curx += x;
                  cury += y;
                } else {
                  x += curx;
                  x1 += curx;
                  y += cury;
                  y1 += cury;
                  curx = x;
                  cury = y;
                }

                d += pathDSegment(letter, [[x1, y1], [x, y]]);
                break;

              case 10:
                // absolute elliptical arc (A)
                x -= curx;
                y -= cury;
                // Fallthrough

              case 11:
                // relative elliptical arc (a)
                if (toRel) {
                  curx += x;
                  cury += y;
                } else {
                  x += curx;
                  y += cury;
                  curx = x;
                  cury = y;
                }

                d += pathDSegment(letter, [[seg.r1, seg.r2]],
                  [seg.angle, seg.largeArcFlag
                    ? 1
                    : 0, seg.sweepFlag
                    ? 1
                    : 0], [x, y]);
                break;

              case 16:
                // absolute smooth cubic (S)
                x -= curx;
                x2 -= curx;
                y -= cury;
                y2 -= cury;
                // Fallthrough

              case 17:
                // relative smooth cubic (s)
                if (toRel) {
                  curx += x;
                  cury += y;
                } else {
                  x += curx;
                  x2 += curx;
                  y += cury;
                  y2 += cury;
                  curx = x;
                  cury = y;
                }

                d += pathDSegment(letter, [[x2, y2], [x, y]]);
                break;
            } // switch on path segment type
          } // for each segment

          return d;
        }
      };
    }());
    // end pathActions

    // Group: Serialization

    // Function: removeUnusedDefElems
    // Looks at DOM elements inside the <defs> to see if they are referred to,
    // removes them from the DOM if they are not.
    //
    // Returns:
    // The amount of elements that were removed
    const removeUnusedDefElems = this.removeUnusedDefElems = function (content) {
      content = content || svgcontent;

      const defs = content.getElementsByTagNameNS(NS.SVG, 'defs');
      if (!defs || !defs.length) { return 0; }

      const defelemUses = [];
      let numRemoved = 0;
      const attrs = ['fill', 'stroke', 'filter', 'marker-start', 'marker-mid', 'marker-end'];
      const alen = attrs.length;

      const allEls = content.getElementsByTagNameNS(NS.SVG, '*');
      const allLen = allEls.length;

      let i,
        j;
      for (i = 0; i < allLen; i++) {
        const el = allEls[i];
        for (j = 0; j < alen; j++) {
          const ref = svgedit.utilities.getUrlFromAttr(el.getAttribute(attrs[j]));
          if (ref) {
            defelemUses.push(ref.substr(1));
          }
        }

        // gradients can refer to other gradients
        const href = getHref(el);
        if (href && href.indexOf('#') === 0) {
          defelemUses.push(href.substr(1));
        }
      }

      const defelems = $(defs).find('linearGradient, radialGradient, filter, marker, svg, symbol');
      i = defelems.length;
      while (i--) {
        const defelem = defelems[i];
        const id = defelem.id;
        if (defelemUses.indexOf(id) < 0) {
          // Not found, so remove (but remember)
          removedElements[id] = defelem;
          defelem.parentNode.removeChild(defelem);
          numRemoved++;
        }
      }

      return numRemoved;
    };

    // Function: svgCanvasToString
    // Main function to set up the SVG content for output
    //
    // Returns:
    // String containing the SVG image for output
    this.svgCanvasToString = function (content) {
      content = content || svgcontent;
      const localContent = content.cloneNode(true);

      // keep calling it until there are none to remove
      while (removeUnusedDefElems(localContent) > 0) { _.noop(); }

      pathActions.clear(true);

      // Move out of in-group editing mode
      if (currentGroup) {
        leaveContext();
        selectOnly([currentGroup]);
      }

      cleanRGBATransparency();

      INKIVE.Helpers.sanitizeIgnoredTagsSvg(localContent);
      INKIVE.Helpers.sanitizeIgnoredSaveTagsSvg(localContent);

      return this.svgToString(localContent, 0);

      function cleanRGBATransparency () {
        localContent.querySelectorAll('*[fill]')
          .forEach(item => rgbaToNone('fill', item));
        localContent.querySelectorAll('*[stroke]')
          .forEach(item => rgbaToNone('stroke', item));

        function rgbaToNone (attributeName, item) {
          const attributeValue = item.getAttribute(attributeName);
          if (attributeValue.startsWith('rgba')) {
            item.setAttribute(attributeName, 'none');
          }
        }
      }
    };

    // Function: svgToString
    // Sub function ran on each SVG element to convert it to a string as desired
    //
    // Parameters:
    // elem - The SVG element to convert
    // indent - Integer with the amount of spaces to indent this tag
    //
    // Returns:
    // String with the given element as an SVG tag
    this.svgToString = function (elem, indent) {
      const out = [];
      const toXml = svgedit.utilities.toXml;
      const unit = curConfig.baseUnit;
      const unitRe = new RegExp('^-?[\\d\\.]+' + unit + '$');

      if (elem) {
        cleanupElement(elem);
        const attrs = elem.attributes;
        const childs = elem.childNodes;
        let attr;
        let i;

        for (i = 0; i < indent; i++) { out.push(' '); }
        out.push('<');
        out.push(elem.nodeName);
        if (elem.id === 'svgcontent') {
          // Process root element separately
          const res = getResolution();

          const vb = '';

          if (unit !== 'px') {
            res.w = svgedit.units.convertUnit(res.w, unit) + unit;
            res.h = svgedit.units.convertUnit(res.h, unit) + unit;
          }

          out.push(' width="' + res.w + '" height="' + res.h + '"' + vb + ' xmlns="' + NS.SVG + '"');

          const nsuris = {};

          // Check elements for namespaces, add if found
          $(elem).find('*').andSelf().each(function () {
            // for some elements have no attribute
            const uri = this.namespaceURI;
            if (uri && !nsuris[uri] && nsMap[uri] && nsMap[uri] !== 'xmlns' && nsMap[uri] !== 'xml') {
              nsuris[uri] = true;
              nsMap[uri] && out.push(' xmlns:' + nsMap[uri] + '="' + uri + '"');
            }

            $.each(this.attributes, function (i, attr) {
              const uri = attr.namespaceURI;
              if (uri && !nsuris[uri] && nsMap[uri] !== 'xmlns' && nsMap[uri] !== 'xml') {
                nsuris[uri] = true;
                nsMap[uri] && out.push(' xmlns:' + nsMap[uri] + '="' + uri + '"');
              }
            });
          });

          i = attrs.length;
          const attrNames = ['width', 'height', 'xmlns', 'x', 'y', 'viewBox', 'id', 'overflow'];
          while (i--) {
            attr = attrs.item(i);
            const attrVal = toXml(attr.nodeValue);
            const attrNode = svgedit.sanitize.getAttributeName(elem, attr);

            // Namespaces have already been dealt with, so skip
            if (attrNode.indexOf('xmlns:') === 0) continue;

            // Delete clip-path just svgcontent (canvas)
            if (attrNode.indexOf('clip-path') === 0) continue;

            // only serialize attributes we don't use internally
            if (attrVal !== '' && attrNames.indexOf(attr.localName.toLowerCase()) === -1) {
              if (!attr.namespaceURI || nsMap[attr.namespaceURI]) {
                out.push(' ');
                out.push(attrNode);
                out.push('="');
                out.push(attrVal);
                out.push('"');
              }
            }
          }
        } else {
          // Skip empty defs
          if (elem.nodeName === 'defs' && !elem.firstChild) { return; }

          const needStrokeFixe = hasAttribute(attrs, 'stroke') &&
            !hasAttribute(attrs, 'stroke-width');

          needStrokeFixe && elem.setAttribute('stroke-width', 1);

          // if($(elem).is('image') && INKIVE.ImagesFactory.getImageUrl(elem)) {
          //   isHrefProcessed = true;
          //   out.push(' xlink:href'); out.push("=\"");
          //   out.push(INKIVE.ImagesFactory.getImageUrl(elem)); out.push("\"");
          // }

          const mozAttrs = ['-moz-math-font-style', '_moz-math-font-style'];
          const attrList = [];
          for (i = attrs.length - 1; i >= 0; i--) {
            attr = attrs.item(i);
            let attrVal = toXml(attr.nodeValue.replace(/"/g, ''));
            const attrNode = svgedit.sanitize.getAttributeName(elem, attr);
            const containsNumber = /\b[0-9]+\b/g;

            if (attrNode === 'font-family' && !containsNumber.test(attrVal)) {
              const apostropheChar = '&#x27;';
              const isBetweenApostrophes = new RegExp('^' + apostropheChar + '.*' + apostropheChar + '$');
              if (isBetweenApostrophes.test(attrVal)) {
                attrVal = attrVal.substring(apostropheChar.length, attrVal.length - apostropheChar.length);
              }
            }

            // remove bogus attributes added by Gecko
            if (mozAttrs.indexOf(attr.localName) >= 0) { continue; }
            if (attrVal !== '') {
              if (attrVal.indexOf('pointer-events') === 0) { continue; }
              // if (attr.localName === 'href' && isHrefProcessed) continue;
              if (attr.localName === 'class') {
                attrVal = attrVal.replace(/selected/g, '').trim();
                if (!attrVal) continue;
              }

              if (attr.localName === 'class' && attrVal.indexOf('se_') === 0) { continue; }

              // remove main group clip-path
              if (elem.getAttribute('class') && elem.getAttribute('class')
                .indexOf('main') > -1 && attr.localName === 'clip-path') {
                continue;
              }

              out.push(' ');
              // This make sticker is broken.
              // if (attr.localName === 'd') {attrVal = pathActions.convertPath(elem, true);}
              if (!isNaN(attrVal) && (_.isNumber(attrVal) || _.contains(attrVal, '.'))) {
                attrVal = svgedit.units.shortFloat(attrVal);
              } else if (unitRe.test(attrVal)) {
                attrVal = svgedit.units.shortFloat(attrVal) + unit;
              }

              // Embed images when saving
              if (saveOptions.apply &&
                elem.nodeName === 'image' &&
                attr.localName === 'href' &&
                saveOptions.images &&
                saveOptions.images === 'embed') {
                const img = encodableImages[attrVal];
                if (img) { attrVal = img; }
              }

              // map various namespaces to our fixed namespace prefixes
              // (the default xmlns attribute itself does not get a prefix)
              if (!attr.namespaceURI || attr.namespaceURI === NS.SVG || nsMap[attr.namespaceURI]) {
                // remove duplicates
                if ($.inArray(attrNode.toLowerCase(), attrList) === -1) {
                  attrList.push(attrNode.toLowerCase());
                  out.push(attrNode);
                  out.push('="');
                  out.push(attrVal);
                  out.push('"');
                }
              }
            }
          }
        }

        if (elem.hasChildNodes()) {
          out.push('>');
          indent++;
          let bOneLine = false;

          for (i = 0; i < childs.length; i++) {
            const child = childs.item(i);
            let str = child.nodeValue;
            switch (child.nodeType) {
              case 1: // element node
                out.push('\n');
                out.push(this.svgToString(childs.item(i), indent));
                break;
              case 3: // text node
                // TODO: Why do we need this?
                //          const str = child.nodeValue.replace(/^\s+|\s+$/g, "");
                str = str.replace(/\s/g, '\u00A0');
                if (str !== '') {
                  bOneLine = true;
                  out.push(String(toXml(str)));
                }
                break;
              case 4: // cdata node
                out.push('\n');
                out.push(new Array(indent + 1).join(' '));
                out.push('<![CDATA[');
                out.push(child.nodeValue);
                out.push(']]>');
                break;
              case 8: // comment
                out.push('\n');
                out.push(new Array(indent + 1).join(' '));
                out.push('<!--');
                out.push(child.data);
                out.push('-->');
                break;
            } // switch on node type
          }
          indent--;
          if (!bOneLine) {
            out.push('\n');
            for (i = 0; i < indent; i++) { out.push(' '); }
          }
          out.push('</');
          out.push(elem.nodeName);
          out.push('>');
        } else {
          out.push('/>');
        }
      }

      return sanitizeContent(out.join(''));

      function hasAttribute (attrs, attributeName) {
        return _.any(attrs, function (node) {
          return node.localName === attributeName &&
            node.nodeValue !== null &&
            node.nodeValue !== 'null';
        });
      }
    }; // end svgToString()

    // Function: embedImage
    // Converts a given image file to a data URL when possible, then runs a given callback
    //
    // Parameters:
    // val - String with the path/URL of the image
    // callback - Optional function to run when image data is found, supplies the
    // result (data URL or false) as first parameter.
    this.embedImage = function (val, callback) {
      // load in the image and once it's loaded, get the dimensions
      $(new Image()).load(function () {
        // create a canvas the same size as the raster image
        const canvas = document.createElement('canvas');
        canvas.width = this.width;
        canvas.height = this.height;
        // load the raster image into the canvas
        canvas.getContext('2d').drawImage(this, 0, 0);
        // retrieve the data: URL
        try {
          let urldata = ';svgedit_url=' + encodeURIComponent(val);
          urldata = canvas.toDataURL().replace(';base64', urldata + ';base64');
          encodableImages[val] = urldata;
        } catch (e) {
          encodableImages[val] = false;
        }
        if (callback) { callback(encodableImages[val]); }
      }).attr('src', val);
    };

    // Function: setGoodImage
    // Sets a given URL to be a "last good image" URL
    this.setGoodImage = function (val) {
    };

    this.open = function () {
      // Nothing by default, handled by optional widget/extension
    };

    // Function: save
    // Serializes the current drawing into SVG XML text and returns it to the 'saved' handler.
    // This function also includes the XML prolog. Clients of the SvgCanvas bind their save
    // function to the 'saved' event.
    //
    // Returns:
    // Nothing
    this.save = function (opts) {
      // remove the selected outline before serializing
      clearSelection();
      // Update save options if provided
      if (opts) { $.extend(saveOptions, opts); }
      saveOptions.apply = true;

      // no need for doctype, see http://jwatt.org/svg/authoring/#doctype-declaration
      const str = this.svgCanvasToString();
      call('editor.saved', str);
    };

    // Function: rasterExport
    // Generates a Data URL based on the current image, then calls "exported"
    // with an object including the string, image information, and any issues found
    this.rasterExport = function (imgType, quality) {
      const mimeType = 'image/' + imgType.toLowerCase();

      // remove the selected outline before serializing
      clearSelection();

      // Check for known CanVG issues
      const issues = [];

      // Selector and notice
      const issueList = {
        feGaussianBlur: uiStrings.exportNoBlur,
        foreignObject: uiStrings.exportNoforeignObject,
        '[stroke-dasharray]': uiStrings.exportNoDashArray
      };
      const content = $(svgcontent);

      // Add font/text check if Canvas Text API is not implemented
      if (!('font' in $('<canvas>')[0].getContext('2d'))) {
        issueList.text = uiStrings.exportNoText;
      }

      $.each(issueList, function (sel, descr) {
        if (content.find(sel).length) {
          issues.push(descr);
        }
      });

      const str = this.svgCanvasToString();
      call('editor.exported', {
        svg: str,
        issues,
        type: imgType,
        mimeType,
        quality
      });
    };

    // Function: getSvgString
    // Returns the current drawing as raw SVG XML text.
    //
    // Returns:
    // The current drawing as raw SVG XML text.
    this.getSvgString = function (node) {
      saveOptions.apply = false;
      return this.svgCanvasToString(node);
    };

    // Function: randomizeIds
    // This function determines whether to use a nonce in the prefix, when
    // generating IDs for future documents in SVG-Edit.
    //
    // Parameters:
    // an optional boolean, which, if true, adds a nonce to the prefix. Thus
    // svgCanvas.randomizeIds() <==> svgCanvas.randomizeIds(true)
    //
    // if you're controlling SVG-Edit externally, and want randomized IDs, call
    // this BEFORE calling svgCanvas.setSvgString
    //
    this.randomizeIds = function (enableRandomization) {
      if (arguments.length > 0 && enableRandomization === false) {
        svgedit.draw.randomizeIds(false, getCurrentDrawing());
      } else {
        svgedit.draw.randomizeIds(true, getCurrentDrawing());
      }
    };

    // Function: uniquifyElems
    // Ensure each element has a unique ID
    //
    // Parameters:
    // g - The parent element of the tree to give unique IDs
    const uniquifyElems = this.uniquifyElems = function (g) {
      const ids = {};
      // TODO: Handle markers and connectors. These are not yet re-identified properly
      // as their referring elements do not get remapped.
      //
      // <marker id='se_marker_end_svg_7'/>
      // <polyline id='svg_7' se:connector='svg_1 svg_6' marker-end='url(#se_marker_end_svg_7)'/>
      //
      // Problem #1: if svg_1 gets renamed, we do not update the polyline's se:connector attribute
      // Problem #2: if the polyline svg_7 gets renamed, we do not update the marker id nor the polyline's marker-end attribute
      const refElems = ['filter', 'linearGradient', 'pattern', 'radialGradient', 'symbol', 'textPath', 'use'];

      svgedit.utilities.walkTree(g, function (n) {
        // if it's an element node
        if (n.nodeType === 1) {
          // and the element has an ID
          if (n.id) {
            // and we haven't tracked this ID yet
            if (!(n.id in ids)) {
              // add this id to our map
              ids[n.id] = {
                elem: null,
                attrs: [],
                hrefs: []
              };
            }
            ids[n.id].elem = n;
          }

          // now search for all attributes on this element that might refer
          // to other elements
          $.each(refAttrs, function (i, attr) {
            const attrnode = n.getAttributeNode(attr);
            if (attrnode) {
              // the incoming file has been sanitized, so we should be able to safely just strip off the leading #
              const url = svgedit.utilities.getUrlFromAttr(attrnode.value);
              const refid = url
                ? url.substr(1)
                : null;
              if (refid) {
                if (!(refid in ids)) {
                  // add this id to our map
                  ids[refid] = {
                    elem: null,
                    attrs: [],
                    hrefs: []
                  };
                }
                ids[refid].attrs.push(attrnode);
              }
            }
          });

          // check xlink:href now
          const href = svgedit.utilities.getHref(n);
          // TODO: what if an <image> or <a> element refers to an element internally?
          if (href && refElems.indexOf(n.nodeName) >= 0) {
            const refid = href.substr(1);
            if (refid) {
              if (!(refid in ids)) {
                // add this id to our map
                ids[refid] = {
                  elem: null,
                  attrs: [],
                  hrefs: []
                };
              }
              ids[refid].hrefs.push(n);
            }
          }
        }
      });

      // in ids, we now have a map of ids, elements and attributes, let's re-identify
      let oldid;
      for (oldid in ids) {
        if (!oldid) { continue; }
        const elem = ids[oldid].elem;
        if (elem) {
          const newid = getNextId();

          // assign element its new id
          elem.id = newid;

          // remap all url() attributes
          const attrs = ids[oldid].attrs;
          let j = attrs.length;
          while (j--) {
            const attr = attrs[j];
            attr.ownerElement.setAttribute(attr.name, 'url(#' + newid + ')');
          }

          // remap all href attributes
          const hreffers = ids[oldid].hrefs;
          let k = hreffers.length;
          while (k--) {
            const hreffer = hreffers[k];
            svgedit.utilities.setHref(hreffer, '#' + newid);
          }
        }
      }
    };

    // Function setUseData
    // Assigns reference data for each use element
    const setUseData = this.setUseData = function (parent) {
      let elems = $(parent);

      if (parent.tagName !== 'use') {
        elems = elems.find('use');
      }

      elems.each(function () {
        const id = getHref(this).substr(1);
        const refElem = svgedit.utilities.getElem(id);
        if (!refElem) { return; }
        $(this).data('ref', refElem);
        if (refElem.tagName === 'symbol' || refElem.tagName === 'svg') {
          $(this).data('symbol', refElem).data('ref', refElem);
        }
      });
    };

    // Function convertGradients
    // Converts gradients from userSpaceOnUse to objectBoundingBox
    const convertGradients = this.convertGradients = function (elem) {
      let elems = $(elem).find('linearGradient, radialGradient');
      if (!elems.length && svgedit.browser.isWebkit()) {
        // Bug in webkit prevents regular *Gradient selector search
        elems = $(elem).find('*').filter(function () {
          return (this.tagName.indexOf('Gradient') >= 0);
        });
      }

      elems.each(function () {
        const grad = this;
        if ($(grad).attr('gradientUnits') === 'userSpaceOnUse') {
          // TODO: Support more than one element with this ref by duplicating parent grad
          const elems = $(svgcontent).find('[fill="url(#' + grad.id + ')"],[stroke="url(#' + grad.id + ')"]');
          if (!elems.length) { return; }

          // get object's bounding box
          const bb = svgedit.utilities.getBBox(elems[0]);

          // This will occur if the element is inside a <defs> or a <symbol>,
          // in which we shouldn't need to convert anyway.
          if (!bb) { return; }

          if (grad.tagName === 'linearGradient') {
            const gCoords = $(grad).attr(['x1', 'y1', 'x2', 'y2']);

            // If has transform, convert
            const tlist = grad.gradientTransform.baseVal;
            if (tlist && tlist.numberOfItems > 0) {
              const m = svgedit.math.transformListToTransform(tlist).matrix;
              const pt1 = svgedit.math.transformPoint(gCoords.x1, gCoords.y1, m);
              const pt2 = svgedit.math.transformPoint(gCoords.x2, gCoords.y2, m);

              gCoords.x1 = pt1.x;
              gCoords.y1 = pt1.y;
              gCoords.x2 = pt2.x;
              gCoords.y2 = pt2.y;
              grad.removeAttribute('gradientTransform');
            }

            $(grad).attr({
              x1: (gCoords.x1 - bb.x) / bb.width,
              y1: (gCoords.y1 - bb.y) / bb.height,
              x2: (gCoords.x2 - bb.x) / bb.width,
              y2: (gCoords.y2 - bb.y) / bb.height
            });
            grad.removeAttribute('gradientUnits');
          }
          // else {
          // Note: radialGradient elements cannot be easily converted
          // because userSpaceOnUse will keep circular gradients, while
          // objectBoundingBox will x/y scale the gradient according to
          // its bbox.

          // For now we'll do nothing, though we should probably have
          // the gradient be updated as the element is moved, as
          // inkscape/illustrator do.

          //            const g_coords = $(grad).attr(['cx', 'cy', 'r']);
          //
          //            $(grad).attr({
          //              cx: (g_coords.cx - bb.x) / bb.width,
          //              cy: (g_coords.cy - bb.y) / bb.height,
          //              r: g_coords.r
          //            });
          //
          //            grad.removeAttribute('gradientUnits');
          // }
        }
      });
    };

    // Function: convertToGroup
    // Converts selected/given <use> or child SVG element to a group
    const convertToGroup = this.convertToGroup = function (elem) {
      if (!elem) {
        elem = selectedElements[0];
      }
      const $elem = $(elem);
      let ts;

      if ($elem.data('gsvg')) {
        // Use the gsvg as the new group
        const svg = elem.firstChild;
        const pt = $(svg).attr(['x', 'y']);

        $(elem.firstChild.firstChild).unwrap();
        $(elem).removeData('gsvg');

        const tlist = svgedit.transformlist.getTransformList(elem);
        const xform = svgroot.createSVGTransform();
        xform.setTranslate(pt.x, pt.y);
        tlist.appendItem(xform);
        svgedit.recalculate.recalculateDimensions(elem);
        call('editor.selected', [elem]);
      } else if ($elem.data('symbol')) {
        elem = $elem.data('symbol');

        ts = $elem.attr('transform');
        const pos = $elem.attr(['x', 'y']);

        const vb = elem.getAttribute('viewBox');

        if (vb) {
          const nums = vb.split(' ');
          pos.x -= +nums[0];
          pos.y -= +nums[1];
        }

        // Not ideal, but works
        ts += ' translate(' + (pos.x || 0) + ',' + (pos.y || 0) + ')';

        const prev = $elem.prev();

        // Remove <use> element
        $elem.remove();

        // See if other elements reference this symbol
        const hasMore = $(svgcontent).find('use:data(symbol)').length;

        const g = svgdoc.createElementNS(NS.SVG, 'g');
        const childs = elem.childNodes;

        let i;
        for (i = 0; i < childs.length; i++) {
          g.appendChild(childs[i].cloneNode(true));
        }

        // Duplicate the gradients for Gecko, since they weren't included in the <symbol>
        if (svgedit.browser.isGecko()) {
          const dupeGrads = $(svgedit.utilities.findDefs()).children('linearGradient,radialGradient,pattern').clone();
          $(g).append(dupeGrads);
        }

        if (ts) {
          g.setAttribute('transform', ts);
        }

        const parent = elem.parentNode;

        uniquifyElems(g);

        // Put the dupe gradients back into <defs> (after uniquifying them)
        if (svgedit.browser.isGecko()) {
          $(findDefs()).append($(g).find('linearGradient,radialGradient,pattern'));
        }

        // now give the g itself a new id
        g.id = getNextId();

        prev.after(g);

        if (parent) {
          if (!hasMore) {
            // remove symbol/svg element
            parent.removeChild(elem);
          }
        }

        setUseData(g);

        if (svgedit.browser.isGecko()) {
          convertGradients(svgedit.utilities.findDefs());
        } else {
          convertGradients(g);
        }

        // recalculate dimensions on the top-level children so that unnecessary transforms
        // are removed
        svgedit.utilities.walkTreePost(g, function (n) {
          try {
            svgedit.recalculate.recalculateDimensions(n);
          } catch (e) {
            console.log(e);
          }
        });

        // Give ID for any visible element missing one
        $(g).find(visElems).each(function () {
          if (!this.id) { this.id = getNextId(); }
        });

        selectOnly([g]);
        pushGroupProperties(g);
      } else {
        console.log('Unexpected element to ungroup:', elem);
      }
    };

    this.setSvgDefs = function () { };

    //
    // Function: setSvgString
    // This function sets the current drawing as the input SVG XML.
    //
    // Parameters:
    // xmlString - The SVG as XML text.
    //
    // Returns:
    // This function returns false if the set was unsuccessful, true otherwise.
    this.setSvgString = function (xmlString) {
      try {
        // convert string into XML document
        const newDoc = svgedit.utilities.text2xml(xmlString);

        // remove old svg document
        svgroot.removeChild(svgcontent);

        // set new svg document
        // If DOM3 adoptNode() available, use it. Otherwise fall back to DOM2 importNode()
        if (svgdoc.adoptNode) {
          svgcontent = svgdoc.adoptNode(newDoc.documentElement);
        } else {
          svgcontent = svgdoc.importNode(newDoc.documentElement, true);
        }

        if (!svgcontent.id) {
          svgcontent.id = 'svgcontent';
        }
        svgroot.appendChild(svgcontent);
        this.sanitizeSvg(svgcontent);

        const content = $(svgcontent);

        INKIVE.Helpers.imagesSanitize.setImagePositionedAsBackground(content);

        this.runExtensions('initBleedObject', { content });

        canvas.current_drawing_ = new svgedit.draw.Drawing(svgcontent, idprefix);

        // retrieve or set the nonce
        const nonce = getCurrentDrawing().getNonce();
        if (nonce) {
          call('editor.setnonce', nonce);
        } else {
          call('editor.unsetnonce');
        }

        // change image href vals if possible
        content.find('image').each(function () {
          const image = this;
          preventClickDefault(image);
          const val = getHref(this);

          if (val && val.indexOf('data:') === 0) {
            // Check if an SVG-edit data URI
            const m = val.match(/svgedit_url=(.*?);/);
            if (m) {
              const url = decodeURIComponent(m[1]);
              $(new Image()).load(function () {
                image.setAttributeNS(NS.XLINK, 'xlink:href', url);
              }).attr('src', url);
              // Add to encodableImages if it loads
              canvas.embedImage(val);
            }
          }
        });

        const loadCustomFonts = function () {
          const customFonts = [];
          const fontsToLoad = [];

          content.find('[custom-font-id]').each(function () {
            const customFontId = $(this).attr('custom-font-id');

            if (!_.contains(fontsToLoad, customFontId)) {
              customFonts.push({
                id: $(this).parent().attr('id'),
                customFontId
              });

              fontsToLoad.push(customFontId);
            }
          });

          return Promise.all(
            fontsToLoad.map(fontId => INKIVE.FontsFactory.getCustomFont(fontId))
          ).then(() => {
            return INKIVE.EditorFacade.trigger('custom-font.loaded', { fonts: fontsToLoad });
          }).catch(() => {
            logError('loadCustomFonts');
            return INKIVE.EditorFacade.trigger('custom-font.loaded', { fonts: fontsToLoad, error: true });
          });
        };

        INKIVE.FontsFactory.fontListLoaded
          ? loadCustomFonts()
          : INKIVE.EditorFacade.on('fontlist.loaded', loadCustomFonts);
        content.removeClass('order-print-active');

        // Wrap child SVGs in group elements
        content.find('svg').each(function () {
          // Skip if it's in a <defs>
          if ($(this).closest('defs').length) { return; }

          uniquifyElems(this);

          // Check if it already has a gsvg group
          const pa = this.parentNode;
          if (pa.childNodes.length === 1 && pa.nodeName === 'g') {
            $(pa).data('gsvg', this);
            pa.id = pa.id || getNextId();
          } else {
            groupSvgElem(this);
          }
        });

        // For Firefox: Put all paint elems in defs
        if (svgedit.browser.isGecko()) {
          content.find('linearGradient, radialGradient, pattern').appendTo(svgedit.utilities.findDefs());
        }

        // Set ref element for <use> elements

        // TODO: This should also be done if the object is re-added through "redo"
        setUseData(content);

        convertGradients(content[0]);

        // recalculate dimensions on the top-level children so that unnecessary transforms
        // are removed
        svgedit.utilities.walkTreePost(svgcontent, function (n) {
          try {
            svgedit.recalculate.recalculateDimensions(n);
          } catch (e) {
            console.log(e);
          }
        });

        const attrs = {
          id: 'svgcontent',
          overflow: curConfig.show_outside_canvas
            ? 'visible'
            : 'hidden'
        };

        let percs = false;

        // determine proper size
        if (content.attr('viewBox')) {
          const vb = content.attr('viewBox').split(' ');
          attrs.width = vb[2];
          attrs.height = vb[3];
        } else {
          // handle content that doesn't have a viewBox
          $.each(['width', 'height'], function (i, dim) {
            // Set to 100 if not given
            let val = content.attr(dim);

            if (!val) { val = '100%'; }

            if (String(val).substr(-1) === '%') {
              // Use user units if percentage given
              percs = true;
            } else {
              attrs[dim] = svgedit.units.convertToNum(dim, val);
            }
          });
        }

        // identify layers
        identifyLayers();

        // Give ID for any visible layer children missing one
        content.children().find(visElems).each(function () {
          if (!this.id) { this.id = getNextId(this.parentNode); }
        });

        // Percentage width/height, so let's base it on visible elements
        if (percs) {
          const bb = svgedit.utilities.getStrokedBBox(getVisibleElements());
          attrs.width = bb.width + bb.x;
          attrs.height = bb.height + bb.y;
        }

        // Just in case negative numbers are given or
        // result from the percs calculation
        if (attrs.width <= 0) { attrs.width = 100; }
        if (attrs.height <= 0) { attrs.height = 100; }

        content.attr(attrs);
        this.contentW = attrs.width;
        this.contentH = attrs.height;
        currentZoom = 1;
        // reset transform lists
        svgedit.transformlist.resetListMap();
        svgedit.path.clearData();
        svgpanel.appendChild(selectorManager.selectorParentGroup);
        canvas.setZoom(currentZoom);
        call('editor.changed', [svgcontent]);
      } catch (e) {
        console.log(e);
        return false;
      }

      return true;
    };

    // Function: importSvgString
    // This function imports the input SVG XML as a <symbol> in the <defs>, then adds a
    // <use> to the current layer.
    //
    // Parameters:
    // xmlString - The SVG as XML text.
    //
    // Returns:
    // This function returns false if the import was unsuccessful, true otherwise.
    // TODO:
    // * properly handle if namespace is introduced by imported content (must add to svgcontent
    // and update all prefixes in the imported node)
    // * properly handle recalculating dimensions, recalculateDimensions() doesn't handle
    // arbitrary transform lists, but makes some assumptions about how the transform list
    // was obtained
    // * import should happen in top-left of current zoomed viewport
    this.importSvgString = function (xmlString) {
      let j,
        ts;
      try {
        // Get unique ID
        const uid = svgedit.utilities.encode64(xmlString.length + xmlString).substr(0, 32);

        let useExisting = false;

        // Look for symbol and make sure symbol exists in image
        if (importIds[uid]) {
          if ($(importIds[uid].symbol).parents('#svgroot').length) {
            useExisting = true;
          }
        }

        let symbol;
        if (useExisting) {
          symbol = importIds[uid].symbol;
          ts = importIds[uid].xform;
        } else {
          // convert string into XML document
          const newDoc = svgedit.utilities.text2xml(xmlString);

          // If DOM3 adoptNode() available, use it. Otherwise fall back to DOM2 importNode()
          const svg = svgdoc.adoptNode
            ? svgdoc.adoptNode(newDoc.documentElement)
            : svgdoc.importNode(newDoc.documentElement, true);

          this.sanitizeSvg(svg);
          uniquifyElems(svg);

          const innerw = svgedit.units.convertToNum('width', svg.getAttribute('width'));
          const innerh = svgedit.units.convertToNum('height', svg.getAttribute('height'));
          const innervb = svg.getAttribute('viewBox');
          // if no explicit viewbox, create one out of the width and height
          const vb = innervb
            ? innervb.split(' ')
            : [0, 0, innerw, innerh];
          for (j = 0; j < 4; ++j) {
            vb[j] = +(vb[j]);
          }

          // TODO: properly handle preserveAspectRatio
          const canvasw = +svgcontent.getAttribute('width');
          const canvash = +svgcontent.getAttribute('height');
          // imported content should be 1/3 of the canvas on its largest dimension

          if (innerh > innerw) {
            ts = 'scale(' + (canvash / 3) / vb[3] + ')';
          } else {
            ts = 'scale(' + (canvasw / 3) / vb[2] + ')';
          }

          // Hack to make recalculateDimensions understand how to scale
          ts = 'translate(0) ' + ts + ' translate(0)';

          symbol = svgdoc.createElementNS(NS.SVG, 'symbol');
          const defs = svgedit.utilities.findDefs();

          if (svgedit.browser.isGecko()) {
            // Move all gradients into root for Firefox, workaround for this bug:
            // https://bugzilla.mozilla.org/show_bug.cgi?id=353575
            // TODO: Make this properly undo-able.
            $(svg).find('linearGradient, radialGradient, pattern').appendTo(defs);
          }

          while (svg.firstChild) {
            const first = svg.firstChild;
            symbol.appendChild(first);
          }
          const attrs = svg.attributes;
          let i;
          for (i = 0; i < attrs.length; i++) {
            const attr = attrs[i];
            symbol.setAttribute(attr.nodeName, attr.nodeValue);
          }
          symbol.id = getNextId();

          // Store data
          importIds[uid] = {
            symbol,
            xform: ts
          };

          svgedit.utilities.findDefs().appendChild(symbol);
        }

        const useEl = svgdoc.createElementNS(NS.SVG, 'use');
        useEl.id = getNextId();
        setHref(useEl, '#' + symbol.id);

        (currentGroup || getCurrentDrawing().getCurrentLayer()).appendChild(useEl);
        clearSelection();

        useEl.setAttribute('transform', ts);
        svgedit.recalculate.recalculateDimensions(useEl);
        $(useEl).data('symbol', symbol).data('ref', symbol);
        addToSelection([useEl]);
        call('editor.changed', [svgcontent]);
      } catch (e) {
        console.log(e);
        return false;
      }

      return true;
    };

    // TODO(codedread): Move all layer/context functions in draw.js
    // Layer API Functions

    // Group: Layers

    // Function: identifyLayers
    // Updates layer system
    const identifyLayers = canvas.identifyLayers = function () {
      leaveContext();
      getCurrentDrawing().identifyLayers();
    };

    // Function: createLayer
    // Creates a new top-level layer in the drawing with the given name, sets the current layer
    // to it, and then clears the selection. This function then calls the 'changed' handler.
    // This is an undoable action.
    //
    // Parameters:
    // name - The given name
    // options - options object attributes
    this.createLayer = function (name, options) {
      clearSelection();
      const newLayer = getCurrentDrawing().createLayer(name, options);
      call('editor.changed', [newLayer]);
      return newLayer;
    };

    // Function: getCurrentLayerName
    this.getCurrentLayerName = function () {
      const allLayers = getCurrentDrawing().all_layers;
      const currentLayer = getCurrentDrawing().getCurrentLayer();
      const [name] = allLayers.find(([name, layer]) => layer === currentLayer) || [];

      return name;
    };

    // Function: cloneLayer
    // Creates a new top-level layer in the drawing with the given name, copies all the current layer's contents
    // to it, and then clears the selection. This function then calls the 'changed' handler.
    // This is an undoable action.
    //
    // Parameters:
    // name - The given name
    this.cloneLayer = function (name) {
      const newLayer = svgdoc.createElementNS(NS.SVG, 'g');
      const layerTitle = svgdoc.createElementNS(NS.SVG, 'title');
      layerTitle.textContent = name;
      newLayer.appendChild(layerTitle);
      const currentLayer = getCurrentDrawing().getCurrentLayer();
      $(currentLayer).after(newLayer);
      const childs = currentLayer.childNodes;
      let i;
      for (i = 0; i < childs.length; i++) {
        const ch = childs[i];
        if (ch.localName === 'title') { continue; }
        newLayer.appendChild(copyElem(ch));
      }

      clearSelection();
      identifyLayers();

      canvas.setCurrentLayer(name);
      call('editor.changed', [newLayer]);
    };

    // Function: deleteCurrentLayer
    // Deletes the current layer from the drawing and then clears the selection. This function
    // then calls the 'changed' handler. This is an undoable action.
    this.deleteCurrentLayer = function () {
      let currentLayer = getCurrentDrawing().getCurrentLayer();
      const parent = currentLayer.parentNode;
      currentLayer = getCurrentDrawing().deleteCurrentLayer();

      if (currentLayer) {
        clearSelection();
        call('editor.changed', [parent]);
        return true;
      }
      return false;
    };

    // Function: setCurrentLayer
    // Sets the current layer. If the name is not a valid layer name, then this function returns
    // false. Otherwise it returns true. This is not an undo-able action.
    //
    // Parameters:
    // name - the name of the layer you want to switch to.
    //
    // Returns:
    // true if the current layer was switched, otherwise false
    this.setCurrentLayer = function (name) {
      const result = getCurrentDrawing().setCurrentLayer(svgedit.utilities.toXml(name));
      if (result) {
        INKIVE.EditorFacade.trigger('canvas.changeLayer', { name });
        clearSelection();
      }
      return result;
    };

    // Function: renameCurrentLayer
    // Renames the current layer. If the layer name is not valid (i.e. unique), then this function
    // does nothing and returns false, otherwise it returns true. This is an undo-able action.
    //
    // Parameters:
    // newname - the new name you want to give the current layer. This name must be unique
    // among all layer names.
    //
    // Returns:
    // true if the rename succeeded, false otherwise.
    this.renameCurrentLayer = function (newname) {
      let i;
      const drawing = getCurrentDrawing();
      if (drawing.current_layer) {
        const oldLayer = drawing.current_layer;
        // setCurrentLayer will return false if the name doesn't already exist
        // this means we are free to rename our oldLayer
        if (!canvas.setCurrentLayer(newname)) {
          // find the index of the layer
          for (i = 0; i < drawing.getNumLayers(); ++i) {
            if (drawing.all_layers[i][1] === oldLayer) { break; }
          }
          drawing.all_layers[i][0] = svgedit.utilities.toXml(newname);

          // now change the underlying title element contents
          const len = oldLayer.childNodes.length;
          for (i = 0; i < len; ++i) {
            const child = oldLayer.childNodes.item(i);
            // found the <title> element, now append all the
            if (child && child.tagName === 'title') {
              // wipe out old name
              while (child.firstChild) { child.removeChild(child.firstChild); }
              child.textContent = newname;
              call('editor.changed', [oldLayer]);
              return true;
            }
          }
        }
        drawing.current_layer = oldLayer;
      }
      return false;
    };

    // Function: setCurrentLayerPosition
    // Changes the position of the current layer to the new value. If the new index is not valid,
    // this function does nothing and returns false, otherwise it returns true. This is an
    // undo-able action.
    //
    // Parameters:
    // newpos - The zero-based index of the new position of the layer. This should be between
    // 0 and (number of layers - 1)
    //
    // Returns:
    // true if the current layer position was changed, false otherwise.
    this.setCurrentLayerPosition = function (newpos) {
      let oldpos;
      const drawing = getCurrentDrawing();
      if (drawing.current_layer && newpos >= 0 && newpos < drawing.getNumLayers()) {
        for (oldpos = 0; oldpos < drawing.getNumLayers(); ++oldpos) {
          if (drawing.all_layers[oldpos][1] === drawing.current_layer) { break; }
        }
        // some unknown error condition (current_layer not in all_layers)
        if (oldpos === drawing.getNumLayers()) { return false; }

        if (oldpos !== newpos) {
          // if our new position is below us, we need to insert before the node after newpos
          let refLayer = null;
          const oldNextSibling = drawing.current_layer.nextSibling;
          if (newpos > oldpos) {
            if (newpos < drawing.getNumLayers() - 1) {
              refLayer = drawing.all_layers[newpos + 1][1];
            }
          } else {
            // if our new position is above us, we need to insert before the node at newpos
            refLayer = drawing.all_layers[newpos][1];
          }
          svgcontent.insertBefore(drawing.current_layer, refLayer);
          addCommandToHistory(new svgedit.history.MoveElementCommand(drawing.current_layer, oldNextSibling, svgcontent));

          identifyLayers();
          canvas.setCurrentLayer(drawing.getLayerName(newpos));

          return true;
        }
      }

      return false;
    };

    // Function: setLayerVisibility
    // Sets the visibility of the layer. If the layer name is not valid, this function return
    // false, otherwise it returns true. This is an undo-able action.
    //
    // Parameters:
    // layername - the name of the layer to change the visibility
    // bVisible - true/false, whether the layer should be visible
    //
    // Returns:
    // true if the layer's visibility was set, false otherwise
    this.setLayerVisibility = function (layername, bVisible) {
      const drawing = getCurrentDrawing();
      const prevVisibility = drawing.getLayerVisibility(layername);
      const layer = drawing.setLayerVisibility(layername, bVisible);
      if (layer) {
        const oldDisplay = prevVisibility
          ? 'inline'
          : 'none';
        addCommandToHistory(
          new svgedit.history.ChangeElementCommand(layer, { display: oldDisplay }, 'Layer Visibility'));
      } else {
        return false;
      }

      if (layer === drawing.getCurrentLayer()) {
        clearSelection();
        pathActions.clear();
      }
      //    call("editor.changed", [selected]);
      return true;
    };

    // Function: moveSelectedToLayer
    // Moves the selected elements to layername. If the name is not a valid layer name, then false
    // is returned. Otherwise it returns true. This is an undo-able action.
    //
    // Parameters:
    // layername - the name of the layer you want to which you want to move the selected elements
    //
    // Returns:
    // true if the selected elements were moved to the layer, false otherwise.
    this.moveSelectedToLayer = function (layername) {
      // find the layer
      let i;
      let layer = null;
      const drawing = getCurrentDrawing();
      for (i = 0; i < drawing.getNumLayers(); ++i) {
        if (drawing.getLayerName(i) === layername) {
          layer = drawing.all_layers[i][1];
          break;
        }
      }
      if (!layer) { return false; }

      // loop for each selected element and move it
      const selElems = selectedElements;
      i = selElems.length;
      while (i--) {
        const elem = selElems[i];
        if (!elem) { continue; }
        layer.appendChild(elem);
      }

      return true;
    };

    this.mergeLayer = function (skipHistory) {
      const batchCmd = new svgedit.history.BatchCommand('Merge Layer');
      const drawing = getCurrentDrawing();
      const prev = $(drawing.current_layer).prev()[0];
      if (!prev) { return; }
      const layerNextSibling = drawing.current_layer.nextSibling;
      batchCmd.addSubCommand(
        new svgedit.history.RemoveElementCommand(drawing.current_layer, layerNextSibling, svgcontent));

      while (drawing.current_layer.firstChild) {
        const ch = drawing.current_layer.firstChild;
        if (ch.localName === 'title') {
          const chNextSibling = ch.nextSibling;
          batchCmd.addSubCommand(new svgedit.history.RemoveElementCommand(ch, chNextSibling, drawing.current_layer));
          drawing.current_layer.removeChild(ch);
          continue;
        }
        const oldNextSibling = ch.nextSibling;
        prev.appendChild(ch);
        batchCmd.addSubCommand(new svgedit.history.MoveElementCommand(ch, oldNextSibling, drawing.current_layer));
      }

      // Remove current layer
      svgcontent.removeChild(drawing.current_layer);

      if (!skipHistory) {
        clearSelection();
        identifyLayers();

        call('editor.changed', [svgcontent]);

        addCommandToHistory(batchCmd);
      }

      drawing.current_layer = prev;
      return batchCmd;
    };

    this.mergeAllLayers = function () {
      const drawing = getCurrentDrawing();
      drawing.current_layer = drawing.all_layers[drawing.getNumLayers() - 1][1];

      while ($(svgcontent).children('g').length > 1) {
        canvas.mergeLayer(true);
      }

      clearSelection();
      identifyLayers();
      call('editor.changed', [svgcontent]);
    };

    // Function: leaveContext
    // Return from a group context to the regular kind, make any previously
    // disabled elements enabled again
    const leaveContext = this.leaveContext = function () {
      let i;
      const len = disabledElems.length;
      if (len) {
        for (i = 0; i < len; i++) {
          const elem = disabledElems[i];
          const orig = elData(elem, 'orig_opac');
          if (orig !== 1) {
            elem.setAttribute('opacity', orig);
          } else {
            elem.removeAttribute('opacity');
          }
          elem.setAttribute('style', 'pointer-events: inherit');
        }
        disabledElems = [];
        clearSelection(true);
        call('editor.contextset', null);
      }
      currentGroup = null;
    };

    // Function: setContext
    // Set the current context (for in-group editing)
    this.setContext = function (elem) {
      leaveContext();
      if (typeof elem === 'string') {
        elem = svgedit.utilities.getElem(elem);
      }

      // Edit inside this group
      currentGroup = elem;

      // Disable other elements
      $(elem).parentsUntil('#svgcontent').andSelf().siblings().each(function () {
        const opac = this.getAttribute('opacity') || 1;
        // Store the original's opacity
        elData(this, 'orig_opac', opac);
        this.setAttribute('opacity', opac * 0.33);
        this.setAttribute('style', 'pointer-events: none');
        disabledElems.push(this);
      });

      clearSelection();
      call('editor.contextset', currentGroup);
    };

    // Group: Document functions

    // Function: clear
    // Clears the current document. This is not an undoable action.
    this.clear = function () {
      pathActions.clear();

      // clear the svgcontent node
      canvas.clearSvgContentElement();

      // create new document
      canvas.current_drawing_ = new svgedit.draw.Drawing(svgcontent);

      // create empty first layer
      canvas.createLayer('Layer 1');

      // reset the selector manager
      selectorManager.initGroup();

      // reset the rubber band box
      canvas.rubberBox = selectorManager.getRubberBandBox();

      call('editor.cleared');
    };

    // Function: linkControlPoints
    // Alias function
    this.linkControlPoints = pathActions.linkControlPoints;

    // Function: getContentElem
    // Returns the content DOM element
    this.getContentElem = function () { return svgcontent; };

    // Function: getRootElem
    // Returns the root DOM element
    this.getRootElem = function () { return svgroot; };

    this.getSvgPanel = function () { return svgpanel; };

    this.getWorkarea = function () { return workarea; };

    // Function: getSelectedElems
    // Returns the array with selected DOM elements
    const getSelectedElems = this.getSelectedElems = function () { return selectedElements; };

    // Function: getResolution
    // Returns the current dimensions and zoom level in an object
    const getResolution = this.getResolution = function () {
      //    const vb = svgcontent.getAttribute("viewBox").split(' ');
      //    return {'w':vb[2], 'h':vb[3], 'zoom': current_zoom};

      const width = svgcontent.getAttribute('width');// current_zoom;
      const height = svgcontent.getAttribute('height');// current_zoom;

      return {
        w: width,
        h: height,
        zoom: currentZoom
      };
    };

    // Function: getZoom
    // Returns the current zoom level
    this.getZoom = function () { return parseFloat(currentZoom); };

    // Function: getVersion
    // Returns a string which describes the revision number of SvgCanvas.
    this.getVersion = function () {
      return 'svgcanvas.js ($Rev: 2705 $)';
    };

    // Function: setUiStrings
    // Update interface strings with given values
    //
    // Parameters:
    // strs - Object with strings (see uiStrings for examples)
    this.setUiStrings = function (strs) {
      $.extend(uiStrings, strs.notification);
    };

    // Function: setConfig
    // Update configuration options with given values
    //
    // Parameters:
    // opts - Object with options (see curConfig for examples)
    this.setConfig = function (opts) {
      $.extend(curConfig, opts);
    };

    // Function: getTitle
    // Returns the current group/SVG's title contents
    this.getTitle = function (elem) {
      let i;
      elem = elem || selectedElements[0];
      if (!elem) { return; }
      elem = $(elem).data('gsvg') || $(elem).data('symbol') || elem;
      const childs = elem.childNodes;
      for (i = 0; i < childs.length; i++) {
        if (childs[i].nodeName === 'title') {
          return childs[i].textContent;
        }
      }
      return '';
    };

    // Function: setGroupTitle
    // Sets the group/SVG's title content
    // TODO: Combine this with setDocumentTitle
    this.setGroupTitle = function (val) {
      let elem = selectedElements[0];
      elem = $(elem).data('gsvg') || elem;
      let title;
      const ts = $(elem).children('title');

      if (!val.length) {
        // Remove title element
        ts.remove();
      } else if (ts.length) {
        // Change title contents
        title = ts[0];
        title.textContent = val;
      } else {
        // Add title element
        title = svgdoc.createElementNS(NS.SVG, 'title');
        title.textContent = val;
        $(elem).prepend(title);
      }
    };

    // Function: getDocumentTitle
    // Returns the current document title or an empty string if not found
    this.getDocumentTitle = function () {
      return canvas.getTitle(svgcontent);
    };

    // Function: setDocumentTitle
    // Adds/updates a title element for the document with the given name.
    // This is an undoable action
    //
    // Parameters:
    // newtitle - String with the new title
    this.setDocumentTitle = function (newtitle) {
      const childs = svgcontent.childNodes;
      let i;
      let docTitle = false;

      for (i = 0; i < childs.length; i++) {
        if (childs[i].nodeName === 'title') {
          docTitle = childs[i];
          break;
        }
      }
      if (!docTitle) {
        docTitle = svgdoc.createElementNS(NS.SVG, 'title');
        svgcontent.insertBefore(docTitle, svgcontent.firstChild);
      }

      if (newtitle.length) {
        docTitle.textContent = newtitle;
      } else {
        // No title given, so element is not necessary
        docTitle.parentNode.removeChild(docTitle);
      }
    };

    // Function: getEditorNS
    // Returns the editor's namespace URL, optionally adds it to root element
    //
    // Parameters:
    // add - Boolean to indicate whether or not to add the namespace value
    this.getEditorNS = function (add) {
      if (add) {
        svgcontent.setAttribute('xmlns:se', NS.SE);
      }
      return NS.SE;
    };

    // Function: setResolution
    // Changes the document's dimensions to the given size
    //
    // Parameters:
    // x - Number with the width of the new dimensions in user units.
    // Can also be the string "fit" to indicate "fit to content"
    // y - Number with the height of the new dimensions in user units.
    //
    // Returns:
    // Boolean to indicate if resolution change was succesful.
    // It will fail on "fit to content" option with no content to fit to.
    this.setResolution = function (x, y) {
      const res = getResolution();
      const w = res.w;
      const h = res.h;

      if (x === 'fit') {
        // Get bounding box
        const bbox = svgedit.utilities.getStrokedBBox(getVisibleElements());

        if (bbox) {
          const visEls = getVisibleElements();
          addToSelection(visEls);
          const dx = [];
          const dy = [];
          $.each(visEls, function () {
            dx.push(bbox.x * -1);
            dy.push(bbox.y * -1);
          });

          canvas.moveSelectedElements(dx, dy, true);
          clearSelection();

          x = Math.round(bbox.width);
          y = Math.round(bbox.height);
        } else {
          return false;
        }
      }

      if (x !== w || y !== h) {
        const handle = svgroot.suspendRedraw(1000);

        x = svgedit.units.convertToNum('width', x);
        y = svgedit.units.convertToNum('height', y);

        svgcontent.setAttribute('width', x);
        svgcontent.setAttribute('height', y);

        this.contentW = x;
        this.contentH = y;

        svgcontent.setAttribute('viewBox', [0, 0, x / currentZoom, y / currentZoom].join(' '));

        svgroot.unsuspendRedraw(handle);
        call('editor.changed', [svgcontent]);
      }

      return true;
    };

    // Function: getOffset
    // Returns an object with x, y values indicating the svgcontent element's
    // position in the editor's canvas.
    this.getOffset = function () {
      return $(svgcontent).attr(['x', 'y']);
    };

    // Function: setBBoxZoom
    // Sets the zoom level on the canvas-side based on the given value
    //
    // Parameters:
    // val - Bounding box object to zoom to or string indicating zoom option
    // editor_w - Integer with the editor's workarea box's width
    // editor_h - Integer with the editor's workarea box's height
    /* this.setZoomCanvas = function(val, editor_w, editor_h) {
    const spacer = 0.85;
    const bb;
    const calcZoom = function(bb) {
      if (!bb) {return false;}
      const w_zoom = Math.round((editor_w / bb.width)*100 * spacer)/100;
      const h_zoom = Math.round((editor_h / bb.height)*100 * spacer)/100;
      const zoomlevel = Math.min(w_zoom, h_zoom);
      canvas.setZoom(zoomlevel);
      return {'zoom': zoomlevel, 'bbox': bb};
    };

    if (typeof val === 'object') {
      bb = val;
      if (bb.width === 0 || bb.height === 0) {
        const newzoom = bb.zoom ? bb.zoom : current_zoom * bb.factor;
        canvas.setZoom(newzoom);
        return {'zoom': current_zoom, 'bbox': bb};
      }
      return calcZoom(bb);
    }

    switch (val) {
      case 'selection':
        if (!selectedElements[0]) {return;}
        const sel_elems = $.map(selectedElements, function(n){ if (n) {return n;} });
        bb = getStrokedBBox(sel_elems);
        break;
      case 'canvas':
        const res = getResolution();
        spacer = 0.95;
        bb = {width:res.w, height:res.h , x:0, y:0};
        break;
      case 'content':
        bb = getStrokedBBox();
        break;
      case 'layer':
        bb = getStrokedBBox(getVisibleElements(getCurrentDrawing().getCurrentLayer()));
        break;
      default:
        return;
    }
    return calcZoom(bb);
  }; */

    // Function: setZoom
    // Sets the zoom to the given level
    //
    // Parameters:
    // zoom - Float indicating the zoom level to change to
    this.zoomMultipliers = [0.01, 0.05, 0.1, 0.2, 0.5, 1, 2, 5, 10];
    this.zoomMultiplierDefault = 1;
    this.zoomMultiplier = this.zoomMultiplierDefault;
    this.zoomUnitSizeDefault = 100; // px
    this.zoomUnitSizeMin = 50; // px

    this.setZoom = function (zoom) {
      if (zoom <= 0) {
        return false;
      }

      function prepareMultiplier (size) {
        this.zoomMultiplier = this.zoomMultiplierDefault;
        const numOfUnits = (size / this.zoomUnitSizeDefault) | 0;
        const sizeOfUnit = size * zoom / numOfUnits;

        if (sizeOfUnit > this.zoomUnitSizeDefault) {
          for (let i = this.zoomMultipliers.indexOf(this.zoomMultiplierDefault); i > 0; i--) {
            if (this.zoomMultipliers[i] * sizeOfUnit < this.zoomUnitSizeMin) {
              break;
            }

            this.zoomMultiplier = this.zoomMultipliers[i];
          }
        } else {
          for (let i = this.zoomMultipliers.indexOf(this.zoomMultiplierDefault); i < this.zoomMultipliers.length; i++) {
            if (this.zoomMultipliers[i] * sizeOfUnit > this.zoomUnitSizeDefault) {
              break;
            }

            this.zoomMultiplier = this.zoomMultipliers[i];
          }
        }
      }

      const isOrderPrintShown = $('[data-action=order-print]').is('.order-print-shown');
      const orderPrintOpts = { show: true };

      isOrderPrintShown && toggleOrderPrintComponent(orderPrintOpts);
      $(svgcanvas).css('transform', 'scale(' + zoom + ')');

      prepareMultiplier.call(this, Math.max(this.contentW, this.contentH));

      currentZoom = zoom;
      const w = this.contentW;
      const h = this.contentH;
      this.updateCanvas(w, h);

      $.each(selectedElements, function (i, elem) {
        if (!elem) { return; }
        selectorManager.requestSelector(elem).resize();
      });

      pathActions.zoomChange();
      runExtensions('zoomChanged', zoom);
      call('editor.zoomChanged', zoom);
      INKIVE.EditorFacade.trigger('zoom.changed', { zoom });

      isOrderPrintShown && toggleOrderPrintComponent(_.extend(orderPrintOpts, { show: false }));
    };

    function toggleOrderPrintComponent (opts) {
      const $canvasPanel = $('#canvas-panel');
      const transitionPanels = $('#canvas-panel, #order-print-panel, #element-popupmenu');

      if (opts.show) {
        transitionPanels.addClass('disable-transition');
        $canvasPanel.css('left', '').css('opacity', 0);
      } else {
        $canvasPanel.css('opacity', '').css('left', $('#tool-panel').width() / 2);
        _.defer(function () {
          transitionPanels.removeClass('disable-transition');
        }, 200);
      }
    }

    // Function: getMode
    // Returns the current editor mode string
    this.getMode = function () {
      return currentMode;
    };

    // Function: setMode
    // Sets the editor's mode to the given string
    //
    // Parameters:
    // name - String with the new mode to change to
    this.setMode = function (name) {
      //  pathActions.clear(true);
      // /  textActions.clear();
      canvas.cur_properties = (selectedElements[0] && selectedElements[0].nodeName === 'text')
        ? curText
        : curShape;
      currentMode = name;

      call('editor.modeChanged', name);
      runExtensions('clear', {
        elems: selectedElements,
        name
      });
    };

    // Group: Element Styling

    // Function: getColor
    // Returns the current fill/stroke option
    this.getColor = function (type) {
      return canvas.cur_properties[type];
    };

    // Function: setColor
    // Change the current stroke/fill color/gradient value
    //
    // Parameters:
    // type - String indicating fill or stroke
    // val - The value to set the stroke attribute to
    // preventUndo - Boolean indicating whether or not this should be and undoable option
    this.setColor = function (type, val, preventUndo) {
      curShape[type] = val;
      canvas.cur_properties[type + '_paint'] = { type: 'solidColor' };
      const elems = [];

      function addNonG (e) {
        if (e.nodeName !== 'g') {
          elems.push(e);
        }
      }

      let i = selectedElements.length;
      while (i--) {
        const elem = selectedElements[i];
        if (elem) {
          if (elem.tagName === 'g') {
            svgedit.utilities.walkTree(elem, addNonG);
          } else {
            if (type === 'fill') {
              if (elem.tagName !== 'polyline' && elem.tagName !== 'line') {
                elems.push(elem);
              }
            } else {
              elems.push(elem);
            }
          }
        }
      }
      if (elems.length > 0) {
        if (!preventUndo) {
          changeSelectedAttribute(type, val, elems);
          call('editor.changed', elems);
        } else {
          changeSelectedAttributeNoUndo(type, val, elems);
        }
      }
    };

    // Function: setGradient
    // Apply the current gradient to selected element's fill or stroke
    //
    // Parameters
    // type - String indicating "fill" or "stroke" to apply to an element
    const setGradient = this.setGradient = function (type) {
      if (!canvas.cur_properties[type + '_paint'] || canvas.cur_properties[type + '_paint'].type === 'solidColor') { return; }
      let grad = canvas[type + 'Grad'];
      // find out if there is a duplicate gradient already in the defs
      const duplicateGrad = findDuplicateGradient(grad);
      const defs = svgedit.utilities.findDefs();
      // no duplicate found, so import gradient into defs
      if (!duplicateGrad) {
        grad = defs.appendChild(svgdoc.importNode(grad, true));
        // get next id and set it on the grad
        grad.id = getNextId();
      } else { // use existing gradient
        grad = duplicateGrad;
      }
      canvas.setColor(type, 'url(#' + grad.id + ')');
    };

    // Function: findDuplicateGradient
    // Check if exact gradient already exists
    //
    // Parameters:
    // grad - The gradient DOM element to compare to others
    //
    // Returns:
    // The existing gradient if found, null if not
    const findDuplicateGradient = function (grad) {
      const defs = svgedit.utilities.findDefs();
      const existingGrads = $(defs).find('linearGradient, radialGradient');
      let i = existingGrads.length;
      const radAttrs = ['r', 'cx', 'cy', 'fx', 'fy'];
      while (i--) {
        const og = existingGrads[i];
        if (grad.tagName === 'linearGradient') {
          if (grad.getAttribute('x1') !== og.getAttribute('x1') ||
            grad.getAttribute('y1') !== og.getAttribute('y1') ||
            grad.getAttribute('x2') !== og.getAttribute('x2') ||
            grad.getAttribute('y2') !== og.getAttribute('y2')) {
            continue;
          }
        } else {
          const gradAttrs = $(grad).attr(radAttrs);
          const ogAttrs = $(og).attr(radAttrs);

          let diff = false;
          $.each(radAttrs, function (i, attr) {
            if (gradAttrs[attr] !== ogAttrs[attr]) { diff = true; }
          });

          if (diff) { continue; }
        }

        // else could be a duplicate, iterate through stops
        const stops = grad.getElementsByTagNameNS(NS.SVG, 'stop');
        const ostops = og.getElementsByTagNameNS(NS.SVG, 'stop');

        if (stops.length !== ostops.length) {
          continue;
        }

        let j = stops.length;
        while (j--) {
          const stop = stops[j];
          const ostop = ostops[j];

          if (stop.getAttribute('offset') !== ostop.getAttribute('offset') ||
            stop.getAttribute('stop-opacity') !== ostop.getAttribute('stop-opacity') ||
            stop.getAttribute('stop-color') !== ostop.getAttribute('stop-color')) {
            break;
          }
        }

        if (j === -1) {
          return og;
        }
      } // for each gradient in defs

      return null;
    };

    function reorientGrads (elem, m) {
      let i;
      const bb = svgedit.utilities.getBBox(elem);
      for (i = 0; i < 2; i++) {
        const type = i === 0
          ? 'fill'
          : 'stroke';
        const attrVal = elem.getAttribute(type);
        if (attrVal && attrVal.indexOf('url(') === 0) {
          const grad = svgedit.utilities.getRefElem(attrVal);
          if (grad.tagName === 'linearGradient') {
            let x1 = grad.getAttribute('x1') || 0;
            let y1 = grad.getAttribute('y1') || 0;
            let x2 = grad.getAttribute('x2') || 1;
            let y2 = grad.getAttribute('y2') || 0;

            // Convert to USOU points
            x1 = (bb.width * x1) + bb.x;
            y1 = (bb.height * y1) + bb.y;
            x2 = (bb.width * x2) + bb.x;
            y2 = (bb.height * y2) + bb.y;

            // Transform those points
            const pt1 = svgedit.math.transformPoint(x1, y1, m);
            const pt2 = svgedit.math.transformPoint(x2, y2, m);

            // Convert back to BB points
            const gCoords = {};

            gCoords.x1 = (pt1.x - bb.x) / bb.width;
            gCoords.y1 = (pt1.y - bb.y) / bb.height;
            gCoords.x2 = (pt2.x - bb.x) / bb.width;
            gCoords.y2 = (pt2.y - bb.y) / bb.height;

            const newgrad = grad.cloneNode(true);
            $(newgrad).attr(gCoords);

            newgrad.id = getNextId();
            svgedit.utilities.findDefs().appendChild(newgrad);
            elem.setAttribute(type, 'url(#' + newgrad.id + ')');
          }
        }
      }
    }

    // Function: setPaint
    // Set a color/gradient to a fill/stroke
    //
    // Parameters:
    // type - String with "fill" or "stroke"
    // paint - The jGraduate paint object to apply
    this.setPaint = function (type, paint) {
      // make a copy
      const p = new $.jGraduate.Paint(paint);
      this.setPaintOpacity(type, p.alpha / 100, true);

      // now set the current paint objectsetCurStyle
      canvas.cur_properties[type + '_paint'] = p;
      switch (p.type) {
        case 'solidColor':
          this.setColor(type, p.solidColor !== 'none'
            ? '#' + p.solidColor
            : 'none');
          break;
        case 'linearGradient':
        case 'radialGradient':
          canvas[type + 'Grad'] = p[p.type];
          setGradient(type);
          break;
      }
    };

    // alias
    this.setStrokePaint = function (paint) {
      this.setPaint('stroke', paint);
    };

    this.setFillPaint = function (paint) {
      this.setPaint('fill', paint);
    };

    // Function: getStrokeWidth
    // Returns the current stroke-width value
    this.getStrokeWidth = function () {
      return canvas.cur_properties.stroke_width;
    };

    // Function: setStrokeWidth
    // Sets the stroke width for the current selected elements
    // When attempting to set a line's width to 0, this changes it to 1 instead
    //
    // Parameters:
    // val - A Float indicating the new stroke width value
    this.setStrokeWidth = function (val, elems) {
      if (val === 0 && ['line', 'path'].indexOf(currentMode) >= 0) {
        canvas.setStrokeWidth(1);
        return;
      }
      canvas.cur_properties.stroke_width = val;

      if (elems.length > 0) {
        changeSelectedAttributeNoUndo('stroke-width', val, elems);
        call('editor.changed', selectedElements);
      }
    };

    // Function: setStrokeAttr
    // Set the given stroke-related attribute the given value for selected elements
    //
    // Parameters:
    // attr - String with the attribute name
    // val - String or number with the attribute value
    this.setStrokeAttr = function (attr, val) {
      curShape[attr.replace('-', '_')] = val;
      const elems = [];

      let i = selectedElements.length;
      while (i--) {
        const elem = selectedElements[i];
        if (elem) {
          if (elem.tagName === 'g') {
            svgedit.utilities.walkTree(elem, function (e) { if (e.nodeName !== 'g') { elems.push(e); } });
          } else {
            elems.push(elem);
          }
        }
      }
      if (elems.length > 0) {
        changeSelectedAttribute(attr, val, elems);
        call('editor.changed', selectedElements);
      }
    };

    // Function: getStyle
    // Returns current style options
    this.getStyle = function () {
      return curShape;
    };

    // Function: getOpacity
    // Returns the current opacity
    this.getOpacity = function () {
      return curShape.opacity;
    };

    // Function: setOpacity
    // Sets the given opacity to the current selected elements
    this.setOpacity = function (val) {
      curShape.opacity = val;
      changeSelectedAttribute('opacity', val);
    };

    // Function: getOpacity
    // Returns the current fill opacity
    this.getFillOpacity = function () {
      return curShape.fill_opacity;
    };

    // Function: getStrokeOpacity
    // Returns the current stroke opacity
    this.getStrokeOpacity = function () {
      return curShape.stroke_opacity;
    };

    this.setCurStyle = function (style) {
      const nowStyle = this.getCurStyle();
      $.extend(nowStyle, style);
      curShape = nowStyle;
    };
    this.getCurStyle = function () {
      return curShape;
    };

    // Function: setPaintOpacity
    // Sets the current fill/stroke opacity
    //
    // Parameters:
    // type - String with "fill" or "stroke"
    // val - Float with the new opacity value
    // preventUndo - Boolean indicating whether or not this should be an undoable action
    this.setPaintOpacity = function (type, val, preventUndo) {
      curShape[type + '_opacity'] = val;
      if (!preventUndo) {
        changeSelectedAttribute(type + '-opacity', val);
      } else {
        changeSelectedAttributeNoUndo(type + '-opacity', val);
      }
    };

    // Function: getPaintOpacity
    // Gets the current fill/stroke opacity
    //
    // Parameters:
    // type - String with "fill" or "stroke"
    this.getPaintOpacity = function (type) {
      return type === 'fill'
        ? this.getFillOpacity()
        : this.getStrokeOpacity();
    };

    // Function: getBlur
    // Gets the stdDeviation blur value of the given element
    //
    // Parameters:
    // elem - The element to check the blur value for
    this.getBlur = function (elem) {
      let val = 0;
      //  const elem = selectedElements[0];

      if (elem) {
        const filterUrl = elem.getAttribute('filter');
        if (filterUrl) {
          const blur = svgedit.utilities.getElem(elem.id + '_blur');
          if (blur) {
            val = blur.firstChild.getAttribute('stdDeviation');
          }
        }
      }
      return val;
    };

    (function () {
      let curCommand = null;
      let filter = null;
      let filterHidden = false;

      // Function: setBlurNoUndo
      // Sets the stdDeviation blur value on the selected element without being undoable
      //
      // Parameters:
      // val - The new stdDeviation value
      canvas.setBlurNoUndo = function (val) {
        if (!filter) {
          canvas.setBlur(val);
          return;
        }
        if (val === 0) {
          // Don't change the StdDev, as that will hide the element.
          // Instead, just remove the value for "filter"
          changeSelectedAttributeNoUndo('filter', '');
          filterHidden = true;
        } else {
          const elem = selectedElements[0];
          if (filterHidden) {
            changeSelectedAttributeNoUndo('filter', 'url(#' + elem.id + '_blur)');
          }
          if (svgedit.browser.isWebkit()) {
            console.log('e', elem);
            elem.removeAttribute('filter');
            elem.setAttribute('filter', 'url(#' + elem.id + '_blur)');
          }
          changeSelectedAttributeNoUndo('stdDeviation', val, [filter.firstChild]);
          canvas.setBlurOffsets(filter, val);
        }
      };

      function finishChange () {
        curCommand = null;
        filter = null;
      }

      // Function: setBlurOffsets
      // Sets the x, y, with, height values of the filter element in order to
      // make the blur not be clipped. Removes them if not neeeded
      //
      // Parameters:
      // filter - The filter DOM element to update
      // stdDev - The standard deviation value on which to base the offset size
      canvas.setBlurOffsets = function (filter, stdDev) {
        if (stdDev > 3) {
          // TODO: Create algorithm here where size is based on expected blur
          svgedit.utilities.assignAttributes(filter, {
            x: '-50%',
            y: '-50%',
            width: '200%',
            height: '200%'
          }, 100);
        } else {
          // Removing these attributes hides text in Chrome (see Issue 579)
          if (!svgedit.browser.isWebkit()) {
            filter.removeAttribute('x');
            filter.removeAttribute('y');
            filter.removeAttribute('width');
            filter.removeAttribute('height');
          }
        }
      };

      // Function: setBlur
      // Adds/updates the blur filter to the selected element
      //
      // Parameters:
      // val - Float with the new stdDeviation blur value
      // complete - Boolean indicating whether or not the action should be completed (to add to the undo manager)
      canvas.setBlur = function (val, complete) {
        if (curCommand) {
          finishChange();
          return;
        }

        // Looks for associated blur, creates one if not found
        const elem = selectedElements[0];
        const elemId = elem.id;
        filter = svgedit.utilities.getElem(elemId + '_blur');

        val -= 0;

        // Blur found!
        if (filter) {
          if (val === 0) {
            filter = null;
          }
        } else {
          // Not found, so create
          const newblur = addSvgElementFromJson({
            element: 'feGaussianBlur',
            attr: {
              in: 'SourceGraphic',
              stdDeviation: val
            }
          });

          filter = addSvgElementFromJson({
            element: 'filter',
            attr: {
              id: elemId + '_blur'
            }
          });

          filter.appendChild(newblur);
          svgedit.utilities.findDefs().appendChild(filter);
        }

        if (val === 0) {
          elem.removeAttribute('filter');
          return;
        }

        changeSelectedAttribute('filter', 'url(#' + elemId + '_blur)');
        canvas.setBlurOffsets(filter, val);

        curCommand = true;
        canvas.undoMgr.beginUndoableChange('stdDeviation', [filter
          ? filter.firstChild
          : null]);
        if (complete) {
          canvas.setBlurNoUndo(val);
          finishChange();
        }
      };
    }());

    this.setLocked = function (isLocked, selected) {
      selected = selected || selectedElements[0];

      if (selected !== null && selectedElements[1] === null) {
        $(selected).toggleClass('locked', isLocked);
        selectorManager.requestSelector(selected).showGrips(true);
        runExtensions('clear', {});
      }
    };

    // Function: getBold
    // Check whether selected element is bold or not
    //
    // Returns:
    // Boolean indicating whether or not element is bold
    this.getBold = function () {
      // should only have one element selected
      const selected = selectedElements[0];
      if (selected !== null && selected.tagName === 'text' &&
        selectedElements[1] === null) {
        return (selected.getAttribute('font-weight') === 'bold');
      }
      return false;
    };

    // Function: setBold
    // Make the selected element bold or normal
    //
    // Parameters:
    // b - Boolean indicating bold (true) or normal (false)
    this.setBold = function (b) {
      const selected = selectedElements[0];
      if (selected !== null && selected.tagName === 'text' &&
        selectedElements[1] === null) {
        changeSelectedAttribute('font-weight', b
          ? 'bold'
          : 'normal');
      }
      // if (!selectedElements[0].textContent) {
      //  //textActions.setCursor();
      // }
    };

    // Function: getItalic
    // Check whether selected element is italic or not
    //
    // Returns:
    // Boolean indicating whether or not element is italic
    this.getItalic = function () {
      const selected = selectedElements[0];
      if (selected !== null && selected.tagName === 'text' &&
        selectedElements[1] === null) {
        return (selected.getAttribute('font-style') === 'italic');
      }
      return false;
    };

    // Function: setItalic
    // Make the selected element italic or normal
    //
    // Parameters:
    // b - Boolean indicating italic (true) or normal (false)
    this.setItalic = function (i) {
      const selected = selectedElements[0];
      if (selected !== null && selected.tagName === 'text' &&
        selectedElements[1] === null) {
        changeSelectedAttribute('font-style', i
          ? 'italic'
          : 'normal');
      }
      // if (!selectedElements[0].textContent) {
      //  //textActions.setCursor();
      // }
    };

    // Function: getFontFamily
    // Returns the current font family
    this.getFontFamily = function () {
      return curText.font_family;
    };

    // Function: setFontFamily
    // Set the new font family
    //
    // Parameters:
    // val - String with the new font family
    this.setFontFamily = function (val) {
      curText.font_family = val;
      changeSelectedAttribute('font-family', val);
      // if (selectedElements[0] && !selectedElements[0].textContent) {
      //  //textActions.setCursor();
      // }
    };

    // Function: setFontColor
    // Set the new font color
    //
    // Parameters:
    // val - String with the new font color
    this.setFontColor = function (val) {
      curText.fill = val;
      changeSelectedAttribute('fill', val);
    };

    // Function: getFontColor
    // Returns the current font color
    this.getFontColor = function () {
      return curText.fill;
    };

    // Function: getFontSize
    // Returns the current font size
    this.getFontSize = function () {
      return curText.font_size;
    };

    // Function: setFontSize
    // Applies the given font size to the selected element
    //
    // Parameters:
    // val - Float with the new font size
    this.setFontSize = function (val) {
      curText.font_size = val;
      changeSelectedAttribute('font-size', val);
      // if (!selectedElements[0].textContent) {
      //  //textActions.setCursor();
      // }
    };

    // Function: setImageURL
    // Sets the new image URL for the selected image element. Updates its size if
    // a new URL is given
    //
    // Parameters:
    // val - String with the image URL/path
    this.setImageURL = function (val) {
      const elem = selectedElements[0];
      if (!elem) { return; }

      const attrs = $(elem).attr(['width', 'height']);
      let setsize = (!attrs.width || !attrs.height);

      const curHref = getHref(elem);

      // Do nothing if no URL change or size change
      if (curHref !== val) {
        setsize = true;
      } else if (!setsize) { return; }

      setHref(elem, val);

      if (setsize) {
        $(new Image()).load(function () {
          $(elem).attr({
            width: this.width,
            height: this.height
          });

          selectorManager.requestSelector(elem).resize();
          call('editor.changed', [elem]);
        }).attr('src', val);
      }
    };

    // Function: setLinkURL
    // Sets the new link URL for the selected anchor element.
    //
    // Parameters:
    // val - String with the link URL/path
    this.setLinkURL = function (val, elem) {
      elem = elem || selectedElements[0];
      if (!elem) { return; }
      if (elem.tagName !== 'a') {
        // See if parent is an anchor
        const parentsA = $(elem).parents('a');
        if (parentsA.length) {
          elem = parentsA[0];
        } else {
          return;
        }
      }

      const curHref = getHref(elem);
      if (curHref === val) return;
      setHref(elem, val);
    };

    // Function: setRectRadius
    // Sets the rx & ry values to the selected rect element to change its corner radius
    //
    // Parameters:
    // val - The new radius
    this.setRectRadius = function (val) {
      const selected = selectedElements[0];
      if (selected !== null && selected.tagName === 'rect') {
        const r = selected.getAttribute('rx');
        if (r !== val) {
          selected.setAttribute('rx', val);
          selected.setAttribute('ry', val);
          addCommandToHistory(new svgedit.history.ChangeElementCommand(selected, {
            rx: r,
            ry: r
          }, 'Radius'));
          call('editor.changed', [selected]);
        }
      }
    };

    // Function: makeHyperlink
    // Wraps the selected element(s) in an anchor element or converts group to one
    this.makeHyperlink = function (url) {
      canvas.groupSelectedElements('a', url);

      // TODO: If element is a single "g", convert to "a"
      //  if (selectedElements.length > 1 && selectedElements[1]) {
    };

    // Function: removeHyperlink
    this.removeHyperlink = function () {
      canvas.ungroupSelectedElement();
    };

    // Group: Element manipulation

    // Function: setSegType
    // Sets the new segment type to the selected segment(s).
    //
    // Parameters:
    // new_type - Integer with the new segment type
    // See http://www.w3.org/TR/SVG/paths.html#InterfaceSVGPathSeg for list
    this.setSegType = function (newType) {
      pathActions.setSegType(newType);
    };

    // Function: convertToPath
    // Convert selected element to a path
    //
    // Parameters:
    // elem - The DOM element to be converted
    //
    // Returns:
    // If the getBBox flag is true, the resulting path's bounding box object.
    // Otherwise the resulting path element is returned.
    this.convertToPath = function (elem) {
      if (elem === null) {
        $.each(selectedElements, function (i, elem) {
          if (elem) { canvas.convertToPath(elem); }
        });
        return;
      }

      const attrs = {
        fill: curShape.fill,
        'fill-opacity': curShape.fill_opacity,
        stroke: curShape.stroke,
        'stroke-width': curShape.stroke_width,
        'stroke-dasharray': curShape.stroke_dasharray,
        'stroke-linejoin': curShape.stroke_linejoin,
        'stroke-linecap': curShape.stroke_linecap,
        'stroke-opacity': curShape.stroke_opacity,
        opacity: curShape.opacity,
        visibility: 'hidden'
      };

      const path = svgedit.path.convertToPath(elem, attrs);

      // Replace the current element with the converted one

      const id = elem.id;

      // Reorient if it has a matrix
      const eltrans = elem.getAttribute('transform');
      if (eltrans) {
        const tlist = svgedit.transformlist.getTransformList(path);
        if (svgedit.math.hasMatrixTransform(tlist)) {
          pathActions.resetOrientation(path);
        }
      }

      clearSelection();
      elem.parentNode.removeChild(elem);
      path.setAttribute('id', id);
      path.removeAttribute('visibility');
      addToSelection([path], true);
    };

    // Function: changeSelectedAttributeNoUndo
    // This function makes the changes to the elements. It does not add the change
    // to the history stack.
    //
    // Parameters:
    // attr - String with the attribute name
    // newValue - String or number with the new attribute value
    // elems - The DOM elements to apply the change to
    const changeSelectedAttributeNoUndo = this.changeSelectedAttributeNoUndo = function (attr, newValue, elems) {
      const handle = svgroot.suspendRedraw(1000);
      if (currentMode === 'pathedit') {
        // Editing node
        pathActions.moveNode(attr, newValue);
      }
      elems = elems || selectedElements;
      let i = elems.length;
      const noXYElems = ['g', 'polyline', 'path'];

      while (i--) {
        let elem = elems[i];
        if (elem === null) { continue; }
        // Ensure object in dom is the same as the selected element. I.e: hasn't been replaced
        if (elem.id) {
          elem = document.getElementById(elem.id);
        }

        // Set x,y vals on elements that don't have them
        if ((attr === 'x' || attr === 'y') && noXYElems.indexOf(elem.tagName) >= 0) {
          const bbox = svgedit.utilities.getStrokedBBox([elem]);
          const diffX = attr === 'x'
            ? newValue - bbox.x
            : 0;
          const diffY = attr === 'y'
            ? newValue - bbox.y
            : 0;
          canvas.moveSelectedElements(diffX * currentZoom, diffY * currentZoom, true);
          continue;
        }

        // only allow the transform/opacity/filter attribute to change on <g> elements, slightly hacky
        let oldval = attr === '#text'
          ? elem.textContent
          : elem.getAttribute(attr);
        if (oldval === null) { oldval = ''; }
        if (oldval !== String(newValue)) {
          if (attr === '#text') {
            const textarea = new INKIVE.Text.Textarea().setDOM(elem);
            textarea.text.setValues({ textContent: newValue });
            textarea.render(canvas);

            // FF bug occurs on on rotated elements
            if (/rotate/.test(elem.getAttribute('transform'))) {
              elem = ffClone(elem);
            }
          } else if (attr === '#href') {
            setHref(elem, newValue);
          } else { elem.setAttribute(attr, newValue); }

          // Go into "select" mode for text changes
          // NOTE: Important that this happens AFTER elem.setAttribute() or else attributes like
          // font-size can get reset to their old value, ultimately by svgEditor.updateContextPanel(),
          // after calling textActions.toSelectMode() below
          // Need to fix
          /*      if (current_mode === "textedit" && attr !== "#text" && elem.textContent.length) {
          textActions.toSelectMode(elem);
        } */

          //      if (i==0)
          //        selectedBBoxes[0] = svgedit.utilities.getBBox(elem);
          // Use the Firefox ffClone hack for text elements with gradients or
          // where other text attributes are changed.
          if (svgedit.browser.isGecko() && elem.nodeName === 'text' && /rotate/.test(elem.getAttribute('transform'))) {
            if (String(newValue).indexOf('url') === 0 || (['font-size', 'font-family', 'x', 'y'].indexOf(
              attr) >= 0 && elem.textContent)) {
              elem = ffClone(elem);
            }
          }
          // Timeout needed for Opera & Firefox
          // codedread: it is now possible for this function to be called with elements
          // that are not in the selectedElements array, we need to only request a
          // selector if the element is in that array
          if (selectedElements.indexOf(elem) >= 0) {
            setTimeout(function () {
              // Due to element replacement, this element may no longer
              // be part of the DOM
              if (!elem.parentNode) { return; }
              selectorManager.requestSelector(elem).resize();
            }, 0);
          }
          // if this element was rotated, and we changed the position of this element
          // we need to update the rotational transform attribute
          const angle = svgedit.utilities.getRotationAngle(elem);
          if (angle !== 0 && !_.include(['transform', 'opacity'], attr)) {
            const tlist = svgedit.transformlist.getTransformList(elem);
            let n = tlist.numberOfItems;
            while (n--) {
              const xform = tlist.getItem(n);
              if (xform.type === 4) {
                // remove old rotate
                tlist.removeItem(n);

                const box = svgedit.utilities.getBBox(elem);
                const center = svgedit.math.transformPoint(box.x + box.width / 2, box.y + box.height / 2,
                  svgedit.math.transformListToTransform(tlist).matrix);
                const cx = center.x;
                const cy = center.y;
                const newrot = svgroot.createSVGTransform();
                newrot.setRotate(angle, cx, cy);
                tlist.insertItemBefore(newrot, n);
                break;
              }
            }
          }
        } // if oldValue !== newValue
      } // for each elem
      svgroot.unsuspendRedraw(handle);
    };

    // Function: changeSelectedAttribute
    // Change the given/selected element and add the original value to the history stack
    // If you want to change all selectedElements, ignore the elems argument.
    // If you want to change only a subset of selectedElements, then send the
    // subset to this function in the elems argument.
    //
    // Parameters:
    // attr - String with the attribute name
    // newValue - String or number with the new attribute value
    // elems - The DOM elements to apply the change to
    const changeSelectedAttribute = this.changeSelectedAttribute = function (attr, val, elems) {
      elems = elems || selectedElements;
      canvas.undoMgr.beginUndoableChange(attr, elems);

      changeSelectedAttributeNoUndo(attr, val, elems);
    };

    // Function: deleteSelectedElements
    // Removes all selected elements from the DOM and adds the change to the
    // history stack
    this.deleteSelectedElements = function () {
      if (getCurrentDrawing().getCurrentLayer().getAttribute('class') === 'clipperImage') return;
      let i;
      const len = selectedElements.length;
      const selectedCopy = []; // selectedElements is being deleted
      let parent;
      for (i = 0; i < len; ++i) {
        const selected = selectedElements[i];
        if (selected === null) { break; }

        call('loading.hide', selected);
        if ($(selected).hasClass('layoutBox') && $(selected).hasClass('video')) {
          parent = selected.parentNode;
          runExtensions('deleteLayout', {
            elem: selected
          });
        } else {
          parent = selected.parentNode;
          let t = selected;

          // this will unselect the element and remove the selectedOutline
          selectorManager.releaseSelector(t);

          // Remove the path if present.
          svgedit.path.removePath_(t.id);

          // Get the parent if it's a single-child anchor
          if (parent.tagName === 'a' && parent.childNodes.length === 1) {
            t = parent;
            parent = parent.parentNode;
          }

          parent.removeChild(t);
          selectedCopy.push(selected); // for the copy
          selectedElements[i] = null;
        }
        $('#selector-rect-' + selected.id).remove();
      }
      call('editor.changed', selectedCopy);
      clearSelection();
    };

    // Function: cutSelectedElements
    // Removes all selected elements from the DOM and adds the change to the
    // history stack. Remembers removed elements on the clipboard

    // TODO: Combine similar code with deleteSelectedElements
    this.cutSelectedElements = function () {
      INKIVE.DesignFacade.trigger('cut', selectedElements);
      let i;
      const len = selectedElements.length;
      const selectedCopy = []; // selectedElements is being deleted
      for (i = 0; i < len; ++i) {
        const selected = selectedElements[i];
        if (selected === null) {
          break;
        }

        const parent = selected.parentNode;
        const t = selected;

        // this will unselect the element and remove the selectedOutline
        selectorManager.releaseSelector(t);

        // Remove the path if present.
        svgedit.path.removePath_(t.id);

        parent.removeChild(t);
        selectedCopy.push(selected); // for the copy
        selectedElements[i] = null;
      }

      call('editor.changed', selectedCopy);
      clearSelection();

      canvas.clipBoard = this.fromElementsToClipBoard(selectedCopy);
    };

    /**
     *
     * @param {HTMLElement[]} elements
     * @returns {Promise<Clipboard>}
     */
    this.fromElementsToClipBoard = function (elements) {
      const isTemplate = INKIVE.C.BOOK.book_type === INKIVE.C.BOOK_TYPE.TEMPLATE;
      const elementPermissions = INKIVE.RestrictionsFactory?.getElementPermissionsCollection().toJSON().filter(permission => permission.page_number === INKIVE.Tool._current_page_number);
      return elements.map(element => ({
        selectedElement: element,
        permission: isTemplate ? (elementPermissions?.find(permission => permission.element_id === element.getAttribute('id'))?.restrictions ?? null) : null
      }));
    };

    this.getAllClipPaths = function (elements) {
      return [...elements || []].reduce((acc, element) => {
        const list = [element, ...element.querySelectorAll('[clip-path]')];

        return list.reduce((items, clipPathItem) => {
          const clipPathAttribute = clipPathItem.getAttribute('clip-path');
          const clipPathId = this.getClipPathId(clipPathAttribute, true);
          const clipPathElement = document.getElementById(clipPathId);

          if (clipPathElement) {
            return items.concat(clipPathElement);
          }

          return items;
        }, acc);
      }, []);
    };

    this.getClipPathId = function (clipPath, noHash) {
      if (!clipPath) {
        return;
      }
      clipPath = clipPath
        .replace('url(', '')
        .replace(')', '')
        .replace(/"/g, '');

      return noHash
        ? clipPath.replace('#', '')
        : clipPath;
    };

    this.updateClipPaths = function (elements) {
      elements.forEach(element => {
        if (!svgedit.utilities.getElem(element.dataset.oldId)) {
          element.id = element.dataset.oldId;
        }

        const clipElement = element.getAttribute('clip-path')
          ? element
          : element.querySelector('[clip-path]');

        if (clipElement) {
          const subElementsWithClipPath = [...clipElement.querySelectorAll(`[clip-path="${clipElement.getAttribute('clip-path')}"]`)];
          const clipElementId = this.getClipPathId(clipElement.getAttribute('clip-path'), true);
          const clipPathElement = svgcontent.querySelector(`[data-old-id="${clipElementId}"]`) || element.querySelector(`[data-old-id="${clipElementId}"]`);
          const clipPathId = 'clip_' + element.id;
          const newClipPath = `url(#${clipPathId})`;

          if (clipPathElement) {
            clipPathElement.id = clipPathId;
            clipElement.setAttribute('clip-path', newClipPath);
            subElementsWithClipPath.forEach(subElement => subElement.setAttribute('clip-path', newClipPath));
            delete clipPathElement.dataset.oldId;
          }
        }

        delete element.dataset.oldId;
      });
    };

    // Function: copySelectedElements
    // Remembers the current selected elements on the clipboard
    this.copySelectedElements = function () {
      if (getCurrentDrawing().getCurrentLayer().getAttribute('class') === 'clipperImage') return;
      if (_.any(selectedElements, cantDuplicate)) return;

      const clipPaths = this.getAllClipPaths(selectedElements);

      canvas.clipBoard = this.fromElementsToClipBoard(selectedElements.concat(clipPaths));

      function cantDuplicate (el) {
        return !INKIVE.RestrictionsFacade.canChange(el, 'duplication');
      }
    };

    this.pasteElements = function (type, x, y) {
      if (getCurrentDrawing().getCurrentLayer().getAttribute('class') === 'clipperImage') return;

      const cb = canvas.clipBoard;
      let len = cb.length;
      if (!len) {
        return;
      }

      const pasted = [];

      // Move elements to lastClickPoint

      while (len--) {
        const { selectedElement: elem, permission } = cb[len];
        // check if element is not null
        if (!elem) continue;
        if (!INKIVE.RestrictionsFacade.canChange(elem, 'add') &&
          !INKIVE.RestrictionsFacade.canChange(elem, 'duplication')) {
          continue;
        }

        const copy = copyElem(elem);

        // This change can be reverted if table grip is removed from svg content in the future
        INKIVE.Helpers.sanitizeIgnoredTagsSvg(copy);
        INKIVE.Helpers.sanitizeIgnoredSaveTagsSvg(copy);

        // // See if elem with elem ID is in the DOM already
        // if (!svgedit.utilities.getElem(elem.id))
        //  copy.id = elem.id;

        if (elem.nodeName.toLowerCase() === 'clippath') {
          // copy.setAttribute('data-old-id', elem.id);
          const defs = svgcontent.getElementsByTagName('defs');
          let def;

          if (defs && defs.length) {
            for (let i = 0; i < defs.length; i++) {
              if (defs[i].children.length) {
                def = defs[i];
                break;
              }
            }

            if (!def) {
              def = defs[defs.length - 1];
            }
          } else {
            def = document.createElementNS(svgedit.NS.SVG, 'defs');
            if (svgcontent.firstChild) {
              svgcontent.insertBefore(def, svgcontent.firstChild.nextSibling);
            } else {
              svgcontent.appendChild(def);
            }
          }

          def.appendChild(copy);
        } else {
          this.updateClipPaths([copy]);

          pasted.push(copy);
          (currentGroup || getCurrentDrawing().getCurrentLayer()).appendChild(copy);

          if (permission && INKIVE.C.BOOK.book_type === INKIVE.C.BOOK_TYPE.TEMPLATE) {
            INKIVE.RestrictionsFactory?.setGlobalElementRestriction(copy.getAttribute('id'), permission);
          }
        }
      }

      selectOnly(pasted.filter(element => !element.classList.contains('table')));
      if (type === 'icon-context-menu') {
        const coords = svgedit.utilities.getStrokedBBox(pasted);
        const zoom = INKIVE.EditorFacade.handle('zoom', 'get');

        canvas.moveSelectedElements(-coords.x * zoom, -coords.y * zoom, false);
        canvas.moveSelectedElements(x, y, false);
      } else if (type !== 'in_place') {
        let ctrX,
          ctrY;
        const bbox = svgedit.utilities.getStrokedBBox(pasted);

        if (!type) {
          ctrX = canvas.lastClickPoint.x;
          ctrY = canvas.lastClickPoint.y;
        } else if (type === 'point') {
          ctrX = x;
          ctrY = y;
        }

        let cx = ctrX - (bbox.x + bbox.width / 2);
        let cy = ctrY - (bbox.y + bbox.height / 2);

        if (type === 'move') {
          cx = x;
          cy = y;
        }

        const dx = [];
        const dy = [];

        $.each(pasted, function () {
          dx.push(cx);
          dy.push(cy);
        });

        canvas.moveSelectedElements(dx, dy, false);
      }

      canvas.clipBoard = [];
      canvas.runExtensions('selectedChanged', { pastedElements: pasted });
      call('editor.changed', pasted);

      return pasted;
    };

    // Function: groupSelectedElements
    // Wraps all the selected elements in a group (g) element

    // Parameters:
    // type - type of element to group into, defaults to <g>
    this.groupSelectedElements = function (type, urlArg) {
      if (!type) { type = 'g'; }
      let url = '';

      switch (type) {
        case 'a':
          if (arguments.length > 1) {
            url = urlArg;
          }
          break;

        default:
          type = 'g';
          break;
      }

      // create and insert the group element
      const g = addSvgElementFromJson({
        element: type,
        attr: { id: getNextId() }
      });

      type === 'a' && setHref(g, url);

      // now move all children into the group
      let i = selectedElements.length;
      while (i--) {
        let elem = selectedElements[i];
        $(elem).removeClass('element');
        if (elem === null) { continue; }

        if (elem.parentNode.tagName === 'a' && elem.parentNode.childNodes.length === 1) {
          elem = elem.parentNode;
        }

        g.appendChild(elem);
      }

      type === 'a' && setBoxCoord(g);
      $(g).addClass('element group');
      // update selection
      selectOnly([g], true);

      function setBoxCoord (group) {
        const bbox = svgedit.utilities.getBBox(group);
        const box = canvas.addSvgElementFromJson({
          element: 'title',
          curStyles: false,
          attr: {}
        });

        box.textContent = Math.floor4(bbox.x) +
          ' ' + Math.floor4(bbox.y) +
          ' ' + Math.floor4(bbox.width) +
          ' ' + Math.floor4(bbox.height);

        $(box).addClass('coords');
        group.appendChild(box);
      }
    };

    // Function: pushGroupProperties
    // Pushes all appropriate parent group properties down to its children, then
    // removes them from the group
    const pushGroupProperties = this.pushGroupProperties = function (g, skipClipPath) {
      const children = g.childNodes;
      const len = children.length;
      const xform = g.getAttribute('transform');

      const glist = svgedit.transformlist.getTransformList(g);
      const m = svgedit.math.transformListToTransform(glist).matrix;

      // TODO: get all fill/stroke properties from the group that we are about to destroy
      // "fill", "fill-opacity", "fill-rule", "stroke", "stroke-dasharray", "stroke-dashoffset",
      // "stroke-linecap", "stroke-linejoin", "stroke-miterlimit", "stroke-opacity",
      // "stroke-width"
      // and then for each child, if they do not have the attribute (or the value is 'inherit')
      // then set the child's attribute

      let i;
      const gangle = svgedit.utilities.getRotationAngle(g);
      const attributeList = ['filter', 'opacity'];
      if (!skipClipPath) attributeList.push('clip-path');
      const gattrs = $(g).attr(attributeList);
      let gfilter,
        gblur,
        changes;

      for (i = 0; i < len; i++) {
        const elem = children[i];

        if (elem.nodeType !== 1) { continue; }

        if (gattrs.opacity !== null && gattrs.opacity !== 1) {
          const newOpac = Math.round((elem.getAttribute('opacity') || 1) * gattrs.opacity * 100) / 100;
          changeSelectedAttribute('opacity', newOpac, [elem]);
        }

        if (gattrs['clip-path'] && !elem.hasAttribute('transform')) {
          changeSelectedAttribute('clip-path', g.getAttribute('clip-path'), [elem]);
        }

        if (gattrs.filter) {
          let cblur = this.getBlur(elem);
          const origCblur = cblur;
          if (!gblur) { gblur = this.getBlur(g); }
          if (cblur) {
            // Is this formula correct?
            cblur = Number(gblur) + Number(cblur);
          } else if (cblur === 0) {
            cblur = gblur;
          }

          // If child has no current filter, get group's filter or clone it.
          if (!origCblur) {
            // Set group's filter to use first child's ID
            if (!gfilter) {
              gfilter = svgedit.utilities.getRefElem(gattrs.filter);
            } else {
              // Clone the group's filter
              gfilter = copyElem(gfilter);
              svgedit.utilities.findDefs().appendChild(gfilter);
            }
          } else {
            gfilter = svgedit.utilities.getRefElem(elem.getAttribute('filter'));
          }

          // Change this in future for different filters
          const suffix = (gfilter.firstChild.tagName === 'feGaussianBlur')
            ? 'blur'
            : 'filter';
          gfilter.id = elem.id + '_' + suffix;
          changeSelectedAttribute('filter', 'url(#' + gfilter.id + ')', [elem]);

          // Update blur value
          if (cblur) {
            changeSelectedAttribute('stdDeviation', cblur, [gfilter.firstChild]);
            canvas.setBlurOffsets(gfilter, cblur);
          }
        }

        let chtlist = svgedit.transformlist.getTransformList(elem);

        // Don't process gradient transforms
        if (~elem.tagName.indexOf('Gradient')) { chtlist = null; }

        // Hopefully not a problem to add this. Necessary for elements like <desc/>
        if (!chtlist) { continue; }

        // Apparently <defs> can get get a transformlist, but we don't want it to have one!
        if (elem.tagName === 'defs') { continue; }

        if (glist.numberOfItems) {
          // TODO: if the group's transform is just a rotate, we can always transfer the
          // rotate() down to the children (collapsing consecutive rotates and factoring
          // out any translates)
          if (gangle && glist.numberOfItems === 1) {
            // [Rg] [Rc] [Mc]
            // we want [Tr] [Rc2] [Mc] where:
            //  - [Rc2] is at the child's current center but has the
            // sum of the group and child's rotation angles
            //  - [Tr] is the equivalent translation that this child
            // undergoes if the group wasn't there

            // [Tr] = [Rg] [Rc] [Rc2_inv]

            // get group's rotation matrix (Rg)
            const rgm = glist.getItem(0).matrix;

            // get child's rotation matrix (Rc)
            let rcm = svgroot.createSVGMatrix();
            const cangle = svgedit.utilities.getRotationAngle(elem);
            if (cangle) {
              rcm = chtlist.getItem(0).matrix;
            }

            // get child's old center of rotation
            const cbox = svgedit.utilities.getBBox(elem);
            const ceqm = svgedit.math.transformListToTransform(chtlist).matrix;
            const coldc = svgedit.math.transformPoint(cbox.x + cbox.width / 2, cbox.y + cbox.height / 2, ceqm);

            // sum group and child's angles
            const sangle = gangle + cangle;

            // get child's rotation at the old center (Rc2_inv)
            const r2 = svgroot.createSVGTransform();
            r2.setRotate(sangle, coldc.x, coldc.y);

            // calculate equivalent translate
            const trm = svgedit.math.matrixMultiply(rgm, rcm, r2.matrix.inverse());

            // set up tlist
            if (cangle) {
              chtlist.removeItem(0);
            }

            if (sangle) {
              if (chtlist.numberOfItems) {
                chtlist.insertItemBefore(r2, 0);
              } else {
                chtlist.appendItem(r2);
              }
            }

            if (trm.e || trm.f) {
              const tr = svgroot.createSVGTransform();
              tr.setTranslate(trm.e, trm.f);
              if (chtlist.numberOfItems) {
                chtlist.insertItemBefore(tr, 0);
              } else {
                chtlist.appendItem(tr);
              }
            }
          } else {
            // more complicated than just a rotate

            // transfer the group's transform down to each child and then
            // call svgedit.recalculate.recalculateDimensions()
            const oldxform = elem.getAttribute('transform');
            changes = {};
            changes.transform = oldxform || '';

            const newxform = svgroot.createSVGTransform();

            // [ gm ] [ chm ] = [ chm ] [ gm' ]
            // [ gm' ] = [ chm_inv ] [ gm ] [ chm ]
            const chm = svgedit.math.transformListToTransform(chtlist).matrix;
            const chmInv = chm.inverse();
            const gm = svgedit.math.matrixMultiply(chmInv, m, chm);
            newxform.setMatrix(gm);
            chtlist.appendItem(newxform);
          }

          svgedit.recalculate.recalculateDimensions(elem);
        }
      }

      // remove transform and make it undo-able
      if (xform) {
        changes = {};
        changes.transform = xform;
        g.setAttribute('transform', '');
        g.removeAttribute('transform');
      }
    };

    // Function: ungroupSelectedElement
    // Unwraps all the elements in a selected group (g) element. This requires
    // significant recalculations to apply group's transforms, etc to its children
    this.ungroupSelectedElement = function () {
      let g = selectedElements[0];
      if ($(g).data('gsvg') || $(g).data('symbol')) {
        // Is svg, so actually convert to group
        convertToGroup(g);
        return;
      }
      if (g.tagName === 'use') {
        // Somehow doesn't have data set, so retrieve
        const symbol = svgedit.utilities.getElem(getHref(g).substr(1));
        $(g).data('symbol', symbol).data('ref', symbol);
        convertToGroup(g);
        return;
      }
      const parentsA = $(g).parents('a');
      if (parentsA.length) {
        g = parentsA[0];
      }

      // Look for parent "a"
      if (g.tagName === 'g' || g.tagName === 'a') {
        pushGroupProperties(g, true);

        const parent = g.parentNode;
        const anchor = g.nextSibling;
        const children = new Array(g.childNodes.length);

        let i = 0;

        while (g.firstChild) {
          let elem = g.firstChild;
          const oldParent = elem.parentNode;

          // Remove child title elements
          if (elem.tagName === 'title') {
            oldParent.removeChild(elem);
            continue;
          }

          children[i++] = elem = parent.insertBefore(elem, anchor);
        }

        // remove the group from the selection
        clearSelection();

        // delete the group element (but make undo-able)
        parent.removeChild(g);

        // update selection
        $(children).addClass('element');
        addToSelection(children);
      }
    };

    // Function: moveToTopSelectedElement
    // Repositions the selected element to the bottom in the DOM to appear on top of
    // other elements
    this.moveToTopSelectedElement = function () {
      if (!selectedElements[0]) return;
      const move = (selected) => {
        if (selected !== null) {
          let t = selected;
          const oldParent = t.parentNode;
          const oldNextSibling = t.nextSibling;
          t = t.parentNode.appendChild(t);
          // If the element actually moved position, add the command and fire the changed
          // event handler.
          if (oldNextSibling !== t.nextSibling) {
            addCommandToHistory(new svgedit.history.MoveElementCommand(t, oldNextSibling, oldParent, 'top'));
            call('editor.changed', [t]);
          }
        }
      };
      selectedElements.filter(Boolean).forEach(move);
    };

    // Function: moveToBottomSelectedElement
    // Repositions the selected element to the top in the DOM to appear under
    // other elements
    this.moveToBottomSelectedElement = function () {
      if (!selectedElements[0]) return;
      const move = (selected) => {
        const oldParent = selected.parentNode;
        const oldNextSibling = selected.nextSibling;
        let firstChild = selected.parentNode.firstChild;
        if (firstChild.tagName === 'title') {
          firstChild = firstChild.nextSibling;
        }
        if (firstChild.tagName === 'defs') {
          firstChild = firstChild.nextSibling;
        }
        selected = selected.parentNode.insertBefore(selected, firstChild);
        // If the element actually moved position, add the command and fire the changed
        // event handler.
        if (oldNextSibling !== selected.nextSibling) {
          addCommandToHistory(new svgedit.history.MoveElementCommand(selected, oldNextSibling, oldParent, 'bottom'));
          call('editor.changed', [selected]);
        }
      };
      selectedElements.filter(Boolean).forEach(move);
    };

    // Function: moveUpDownSelected
    // Moves the select element up or down the stack, based on the visibly
    // intersecting elements
    //
    // Parameters:
    // dir - String that's either 'Up' or 'Down'
    this.moveUpDownSelected = function (dir) {
      if (!selectedElements[0]) return;
      const move = (selected) => {
        canvas.curBBoxes = [];
        let closest,
          foundCur;
        // jQuery sorts this list
        const strokedBBoxe = svgedit.utilities.getStrokedBBox([selected]);
        const list = $(getIntersectionList(strokedBBoxe)).toArray();
        if (dir === 'Down') { list.reverse(); }

        $.each(list, function () {
          if (!foundCur) {
            if (this === selected) {
              foundCur = true;
            }
            return;
          }
          closest = this;
          return false;
        });

        const t = selected;
        if (!closest) {
          closest = dir === 'Down'
            ? t.previousSibling
            : t.nextSibling;
          if (!closest) return;
        }
        const oldParent = t.parentNode;
        const oldNextSibling = t.nextSibling;
        $(closest)[dir === 'Down'
          ? 'before'
          : 'after'](t);
        // If the element actually moved position, add the command and fire the changed
        // event handler.
        if (oldNextSibling !== t.nextSibling) {
          addCommandToHistory(new svgedit.history.MoveElementCommand(t, oldNextSibling, oldParent, 'Move ' + dir));
          call('editor.changed', [t]);
        }
      };
      selectedElements.filter(Boolean).forEach(move);
    };

    // Function: moveSelectedElements
    // Moves selected elements on the X/Y axis
    //
    // Parameters:
    // dx - Float with the distance to move on the x-axis
    // dy - Float with the distance to move on the y-axis
    // undoable - Boolean indicating whether or not the action should be undoable
    //
    // Returns:
    // Batch command for the move
    this.moveSelectedElements = function (dx, dy, undoable) {
      // if undoable is not sent, default to true
      // if single values, scale them to the zoom
      if (dx.constructor !== Array) {
        dx /= currentZoom;
        dy /= currentZoom;
      }

      undoable = typeof undoable === 'undefined'
        ? true
        : undoable;
      const batchCmd = new svgedit.history.BatchCommand('position');
      let i = selectedElements.length;

      while (i--) {
        const selected = selectedElements[i];
        if (selected !== null) {
          const xform = svgroot.createSVGTransform();
          const tlist = svgedit.transformlist.getTransformList(selected);

          const isArrayPosition = dx.constructor === Array;

          isArrayPosition
            ? xform.setTranslate(dx[i], dy[i])
            : xform.setTranslate(dx, dy);

          tlist.numberOfItems
            ? tlist.insertItemBefore(xform, 0)
            : tlist.appendItem(xform);

          const cmd = svgedit.recalculate.recalculateDimensions(selected);
          cmd && batchCmd.addSubCommand(cmd);

          selectorManager.requestSelector(selected).resize();
        }
      }

      if (!batchCmd.isEmpty()) {
        undoable && addCommandToHistory(batchCmd);

        call('editor.changed', selectedElements);
        return batchCmd;
      }
    };

    this.softMoveSelectedElements = function (dx, dy) {
      if (dx.constructor !== Array) {
        dx /= currentZoom;
        dy /= currentZoom;
      }

      let i = selectedElements.length;

      while (i--) {
        const selected = selectedElements[i];

        if (selected) {
          const xform = svgroot.createSVGTransform();
          const tlist = svgedit.transformlist.getTransformList(selected);
          let translateTransform;

          for (let i = 0; i < tlist.numberOfItems; i++) {
            const item = tlist.getItem(i);

            if (item.type === 2) {
              translateTransform = item;
              tlist.removeItem(i);
              break;
            }
          }

          if (translateTransform) {
            dx = translateTransform.matrix.e + dx;
            dy = translateTransform.matrix.f + dy;
          }

          xform.setTranslate(dx, dy);

          tlist.numberOfItems
            ? tlist.insertItemBefore(xform, 0)
            : tlist.appendItem(xform);
        }
      }
    };

    // Function: cloneSelectedElements
    // Create deep DOM copies (clones) of all selected elements and move them slightly
    // from their originals
    this.cloneSelectedElements = function (x, y) {
      canvas.copySelectedElements();
      const clonedElements = canvas.pasteElements('move', x, y);
      canvas.clipBoard = [];

      return clonedElements;
    };

    // Function: alignSelectedElements
    // Aligns selected elements
    //
    // Parameters:
    // type - String with single character indicating the alignment type
    // relative_to - String that must be one of the following:
    // "selected", "largest", "smallest", "page"
    this.alignSelectedElements = function (type, relativeTo) {
      let i,
        elem;
      const bboxes = [];
      let minx = Number.MAX_VALUE;
      let maxx = Number.MIN_VALUE;
      let miny = Number.MAX_VALUEv;
      let maxy = Number.MIN_VALUE;
      let curwidth = Number.MIN_VALUE;
      let curheight = Number.MIN_VALUE;
      const len = selectedElements.length;
      if (!len) { return; }
      for (i = 0; i < len; ++i) {
        if (selectedElements[i] === null) { break; }
        elem = selectedElements[i];
        bboxes[i] = svgedit.utilities.getStrokedBBox([elem]);

        // now bbox is axis-aligned and handles rotation
        switch (relativeTo) {
          case 'smallest':
            if (((type === 'l' || type === 'c' || type === 'r') &&
              (curwidth === Number.MIN_VALUE || curwidth > bboxes[i].width)) ||
              ((type === 't' || type === 'm' || type === 'b') &&
                (curheight === Number.MIN_VALUE || curheight > bboxes[i].height))) {
              minx = bboxes[i].x;
              miny = bboxes[i].y;
              maxx = bboxes[i].x + bboxes[i].width;
              maxy = bboxes[i].y + bboxes[i].height;
              curwidth = bboxes[i].width;
              curheight = bboxes[i].height;
            }
            break;
          case 'largest':
            if (((type === 'l' || type === 'c' || type === 'r') &&
              (curwidth === Number.MIN_VALUE || curwidth < bboxes[i].width)) ||
              ((type === 't' || type === 'm' || type === 'b') &&
                (curheight === Number.MIN_VALUE || curheight < bboxes[i].height))) {
              minx = bboxes[i].x;
              miny = bboxes[i].y;
              maxx = bboxes[i].x + bboxes[i].width;
              maxy = bboxes[i].y + bboxes[i].height;
              curwidth = bboxes[i].width;
              curheight = bboxes[i].height;
            }
            break;
          default: // 'selected'
            if (bboxes[i].x < minx) { minx = bboxes[i].x; }
            if (bboxes[i].y < miny) { miny = bboxes[i].y; }
            if (bboxes[i].x + bboxes[i].width > maxx) { maxx = bboxes[i].x + bboxes[i].width; }
            if (bboxes[i].y + bboxes[i].height > maxy) { maxy = bboxes[i].y + bboxes[i].height; }
            break;
        }
      } // loop for each element to find the bbox and adjust min/max

      if (relativeTo === 'page') {
        minx = 0;
        miny = 0;
        maxx = canvas.contentW;
        maxy = canvas.contentH;
      }

      const dx = new Array(len);
      const dy = new Array(len);
      for (i = 0; i < len; ++i) {
        if (selectedElements[i] === null) { break; }
        elem = selectedElements[i];
        const bbox = bboxes[i];
        dx[i] = 0;
        dy[i] = 0;
        switch (type) {
          case 'l': // left (horizontal)
            dx[i] = minx - bbox.x;
            break;
          case 'c': // center (horizontal)
            dx[i] = (minx + maxx) / 2 - (bbox.x + bbox.width / 2);
            break;
          case 'r': // right (horizontal)
            dx[i] = maxx - (bbox.x + bbox.width);
            break;
          case 't': // top (vertical)
            dy[i] = miny - bbox.y;
            break;
          case 'm': // middle (vertical)
            dy[i] = (miny + maxy) / 2 - (bbox.y + bbox.height / 2);
            break;
          case 'b': // bottom (vertical)
            dy[i] = maxy - (bbox.y + bbox.height);
            break;
        }
      }
      this.moveSelectedElements(dx, dy);
    };

    // Group: Additional editor tools

    this.contentW = getResolution().w;
    this.contentH = getResolution().h;

    // Function: updateCanvas
    // Updates the editor canvas width/height/position after a zoom has occurred
    //
    // Parameters:
    // w - Float with the new width
    // h - Float with the new height
    //
    // Returns:
    // Object with the following values:
    // * x - The canvas' new x coordinate
    // * y - The canvas' new y coordinate
    // * old_x - The canvas' old x coordinate
    // * old_y - The canvas' old y coordinate
    // * d_x - The x position difference
    // * d_y - The y position difference
    this.updateCanvas = function (w, h, options) {
      const bg = document.querySelector('#canvasBackground');
      const bgImg = svgedit.utilities.getElem('background_image');
      const cnBackgroundRect = document.querySelector('#svgcontent .background rect');
      const canvasCliper = document.querySelector('.canvasCliper');

      options = $.extend(options, {
        margin: true
      });

      w = parseFloat(w || curConfig.dimensions[0]);
      h = parseFloat(h || curConfig.dimensions[1]);

      const z = currentZoom;
      const wrapperBBox = wrapper.getBoundingClientRect();
      const vW = wrapperBBox.width;
      const vH = wrapperBBox.height;

      if (vW < w * z) {
        if (options.margin) {
          w += curConfig.margin[0];
        }
      } else {
        w = vW / z;
      }

      if (vH < h * z) {
        if (options.margin) {
          h += curConfig.margin[1];
        }
      } else {
        h = vH / z;
      }

      const oldX = svgcontent.getAttribute('x');
      const oldY = svgcontent.getAttribute('y');
      const x = (w / 2 - this.contentW / 2);
      const y = (h / 2 - this.contentH / 2);

      const canvasDimensions = {
        width: this.contentW,
        height: this.contentH,
        x,
        y
      };

      editor.style.width = `${w * z}px`;
      editor.style.height = `${h * z}px`;
      svgcanvas.style.width = `${w}px`;
      svgcanvas.style.height = `${h}px`;

      svgroot.setAttribute('width', w);
      svgroot.setAttribute('height', h);

      svgedit.utilities.assignAttributes(svgcontent, canvasDimensions);

      canvasCliper.setAttribute('x', 0);
      canvasCliper.setAttribute('y', 0);
      canvasCliper.setAttribute('width', this.contentW);
      canvasCliper.setAttribute('height', this.contentH);

      canvaspanel.style.width = `${w * z}px`;
      canvaspanel.style.height = `${h * z}px`;

      svgedit.utilities.assignAttributes(bg, canvasDimensions);

      if (bgImg) {
        svgedit.utilities.assignAttributes(bgImg, {
          width: '100%',
          height: '100%'
        });
      }

      // Canvas background
      if (cnBackgroundRect) {
        svgedit.utilities.assignAttributes(cnBackgroundRect, {
          width: '100%',
          height: '100%'
        });
      }

      selectorManager.selectorParentGroup.setAttribute('transform',
        'translate(' + x * currentZoom + ',' + y * currentZoom + ')');

      INKIVE.EditorFacade.trigger('canvas.relativeSize', canvasDimensions);
      runExtensions('canvasUpdated', {
        new_x: x,
        new_y: y,
        old_x: oldX,
        old_y: oldY,
        d_x: x - oldX,
        d_y: y - oldY
      });

      return {
        x,
        y,
        old_x: oldX,
        old_y: oldY,
        d_x: x - oldX,
        d_y: y - oldY
      };
    };

    // Function: cycleElement
    // Select the next/previous element within the current layer
    //
    // Parameters:
    // next - Boolean where true = next and false = previous element
    this.cycleElement = function (next) {
      let num;
      const curElem = selectedElements[0];
      let elem = false;
      const allElems = getVisibleElements(currentGroup || getCurrentDrawing().getCurrentLayer());
      if (!allElems.length) { return; }
      if (curElem === null) {
        num = next
          ? allElems.length - 1
          : 0;
        elem = allElems[num];
      } else {
        let i = allElems.length;
        while (i--) {
          if (allElems[i] === curElem) {
            num = next
              ? i - 1
              : i + 1;
            if (num >= allElems.length) {
              num = 0;
            } else if (num < 0) {
              num = allElems.length - 1;
            }
            elem = allElems[num];
            break;
          }
        }
      }
      selectOnly([elem], true);
      call('editor.selected', selectedElements);
    };

    this.clear();

    // DEPRECATED: getPrivateMethods
    // Since all methods are/should be public somehow, this function should be removed

    // Being able to access private methods publicly seems wrong somehow,
    // but currently appears to be the best way to allow testing and provide
    // access to them to plugins.
    this.getPrivateMethods = function () {
      return {
        addCommandToHistory,
        setGradient,
        addSvgElementFromJson,
        assignAttributes,
        BatchCommand,
        call,
        ChangeElementCommand,
        copyElem,
        ffClone,
        findDefs,
        findDuplicateGradient,
        getElem,
        getId,
        getIntersectionList,
        getMouseTarget,
        getNextId,
        getPathBBox,
        getUrlFromAttr,
        hasMatrixTransform,
        identifyLayers,
        InsertElementCommand,
        isIdentity: svgedit.math.isIdentity,
        logMatrix,
        matrixMultiply,
        MoveElementCommand,
        preventClickDefault,
        recalculateAllSelectedDimensions,
        recalculateDimensions,
        remapElement,
        RemoveElementCommand,
        removeUnusedDefElems,
        round,
        runExtensions,
        sanitizeSvg,
        SVGEditTransformList: svgedit.transformlist.SVGTransformList,
        // toString: toString,
        transformBox: svgedit.math.transformBox,
        transformListToTransform,
        transformPoint,
        walkTree: svgedit.utilities.walkTree
      };
    };
  }
}
